<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="ap-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="right-container">
      <div class="top-section">
        <div class="filter-desc">
          {{ filterText }}
        </div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
          :buttonIsDisabled="mainFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              :title="`${pageTitle} Date`"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterAPListingByDate"
            >
              <div>
                <DateTimePicker
                  placeholder="From"
                  :isRequired="filterAPListingByDate"
                  :showTime="false"
                  :isDisabled="!filterAPListingByDate"
                  :value.sync="apListingFromDate"
                  :errorMessage.sync="fromDateErrMsg"
                />
                <DateTimePicker
                  placeholder="To"
                  :isRequired="filterAPListingByDate"
                  :showTime="false"
                  :isDisabled="!filterAPListingByDate"
                  :value.sync="apListingToDate"
                  :errorMessage.sync="toDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              :title="`${pageLabel} Date`"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterAPListingByInvDate"
            >
              <div>
                <DateTimePicker
                  placeholder="From"
                  :isRequired="filterAPListingByInvDate"
                  :showTime="false"
                  :isDisabled="!filterAPListingByInvDate"
                  :value.sync="apListingFromInvDate"
                  :errorMessage.sync="fromInvDateErrMsg"
                />
                <DateTimePicker
                  placeholder="To"
                  :isRequired="filterAPListingByInvDate"
                  :showTime="false"
                  :isDisabled="!filterAPListingByInvDate"
                  :value.sync="apListingToInvDate"
                  :errorMessage.sync="toInvDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterAPListingBySearch"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterAPListingBySearch"
                  :isRequired="filterAPListingBySearch"
                  :selectedObject.sync="selectedSearchBy"
                  :errorMessage.sync="searchByErrMsg"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterAPListingBySearch"
                  :isRequired="filterAPListingBySearch"
                  :selectedObject.sync="selectedSearchCriteria"
                  :errorMessage.sync="searchCriteriaErr"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchValue"
                  :isDisabled="!filterAPListingBySearch"
                  :isRequired="filterAPListingBySearch"
                  :errorMessage.sync="searchValueErr"
                />
              </div>
            </BoxContainer>
            <button
              @click="refreshClicked('APListing')"
              :disabled="!filterAPListingByDate && !filterAPListingByInvDate && !filterAPListingBySearch"
            >
              Refresh
            </button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          :title="pageTitle"
          :tableData.sync="apListing"
          :selectedRow.sync="selectedAPListing"
          :loading="apListingTableLoading"
          :dataType="apListingTableDataType"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
    >
      <div class="ap-entries">
        <BoxContainer
          v-if="action != 'N'"
          title="Summary of Events"
          :isCollapsible="true"
          :isCollapsed="summaryIsCollapsed"
        >
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in summaryItems" :key="i">
              <Tooltip
                v-if="m['visible'] && m['Title'] != 'PaymentNo'"
                class="milestone-tooltip"
                :minWidth="210"
                :tooltipText="m['Label'].join(',')"
              >
                <div></div>
              </Tooltip>
              <div
                :id="m['Title']"
                v-if="m['Title'] == 'Indicator'"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'] }"
                style="min-width: fit-content; padding-right: 20px"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div
                :id="m['Title']"
                v-if="m['Title'] == 'Authorized'"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'] }"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div
                :id="m['Title']"
                v-if="m['Title'] == 'PaymentNo' && m['visible']"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'], minWidth: 'fit-content' }"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div
                :id="m['Title']"
                v-if="m['Title'] == 'Posted' && m['visible']"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'] }"
              >
                {{ m['indicatorToShow'] }}
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(l, i) in Object.entries(newLegend)" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: `${l[1]}` }"></div>
              {{ l[0] }}
            </div>
          </div>
        </BoxContainer>

        <!-- AP Header -->
        <BoxContainer :title="`${pageTitle} Header`" :isCollapsible="true" style="padding-bottom: 15px">
          <div class="ap-header">
            <div class="header-containers">
              <Dropdown
                ref="aptype"
                :placeholder="`${pageTitle} Type`"
                :items="apTypeList"
                itemKey="Description"
                :selectedObject.sync="selectedAPType"
                :errorMessage.sync="apTypeErr"
                :isRequired="true"
                :isDisabled="(viewMode && action != 'N') || selectedAPType != null"
                @selected="apTypeSelected"
                validator="text,50"
              />
              <div style="display: flex">
                <Input
                  ref="invoiceno"
                  :placeholder="`${pageLabel} No.`"
                  :inputValue.sync="invoiceNo"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  :errorMessage.sync="invoiceNoErr"
                  validator="text,25"
                  style="width: 50%"
                />
                <DateTimePicker
                  ref="invoicedate"
                  :placeholder="`${pageLabel} Date`"
                  :value.sync="invoiceDate"
                  :showTime="false"
                  :isDisabled="viewMode"
                  :errorMessage.sync="invoiceDateErr"
                  :isRequired="true"
                  :maxDate="new Date('2079-06-06')"
                  :minDate="new Date('1900-01-01')"
                  style="width: 50%; margin-left: 10px"
                />
              </div>
              <InputQuery
                ref="payeetype"
                placeholder="Payee Type"
                :items="payeeTypeList"
                itemKey="Party"
                :showDesc="false"
                :selectedObject.sync="selectedPayeeType"
                :errorMessage.sync="payeeTypeErr"
                :isRequired="true"
                :isDisabled="viewMode || (selectedPayeeCode != null && chargesDetailsList.length > 0)"
                @selected="payeeTypeSelected"
              />
              <InputQuery
                ref="payeecode"
                placeholder="Payee Code"
                :items="payeeCodeList"
                itemKey="Code - Name"
                :showDesc="false"
                :selectedObject.sync="selectedPayeeCode"
                :errorMessage.sync="payeeCodeErr"
                :isRequired="true"
                :isDisabled="
                  viewMode || !selectedPayeeType || (selectedPayeeCode != null && chargesDetailsList.length > 0)
                "
                @selected="payeeCodeSelected"
                :tableBoxHideColumns="['Code - Name']"
              />
              <div style="display: flex">
                <Dropdown
                  ref="term"
                  placeholder="Term"
                  :items="termList"
                  itemKey="term"
                  :selectedObject.sync="selectedTerm"
                  :search.sync="termInput"
                  :allowFreeText="true"
                  :errorMessage.sync="termErr"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  validator="positiveInteger,9"
                  @focusChanged="termFocusChanged($event, termInput)"
                  @selected="termSelected"
                  style="width: 50%"
                />
                <DateTimePicker
                  ref="duedate"
                  placeholder="Due Date"
                  :value.sync="dueDate"
                  :showTime="false"
                  :isDisabled="viewMode"
                  :errorMessage.sync="dueDateErr"
                  :isRequired="true"
                  :maxDate="new Date('2079-06-06')"
                  :minDate="new Date('1900-01-01')"
                  style="width: 50%; margin-left: 10px"
                />
              </div>
            </div>
            <div class="header-containers" style="margin-left: 50px">
              <div style="display: flex">
                <DateTimePicker
                  ref="apinvdate"
                  :placeholder="`AP ${pageLabel} Date`"
                  :value.sync="apInvDate"
                  :showTime="false"
                  :isDisabled="viewMode"
                  :errorMessage.sync="apInvDateErr"
                  :isRequired="true"
                  :maxDate="new Date('2079-06-06')"
                  :minDate="new Date('1900-01-01')"
                  style="width: 50%; margin-right: 5px"
                />
                <Input
                  ref="apinvno"
                  :placeholder="`${pageTitle} No.`"
                  :inputValue.sync="apInvNo"
                  :isDisabled="true"
                  :errorMessage.sync="apInvNoErr"
                  validator="text,25"
                  style="width: 50%; margin-left: 5px"
                />
              </div>
              <div style="display: flex">
                <InputQuery
                  placeholder="Currency"
                  :isRequired="true"
                  :items="currencyList"
                  itemKey="Currency Code"
                  descKey="Description"
                  :isDisabled="viewMode || chargesDetailsList.length > 0 || directPostingDetailsList.length > 0"
                  :selectedObject.sync="selectedHeaderCurr"
                  ref="headercurr"
                  :errorMessage.sync="headerCurrErr"
                  :showDesc="false"
                  style="margin-right: 5px"
                  @selected="headerCurrencySelected"
                />
                <Input
                  placeholder="Exch. Rate"
                  :isRequired="true"
                  :isDisabled="viewMode || (selectedHeaderCurr && selectedHeaderCurr['Currency Code'] == 'MYR')"
                  textAlign="right"
                  :inputValue.sync="headerExchRate"
                  :errorMessage.sync="headerExchRateErr"
                  validator="positiveDecimal,15,4"
                  ref="headerrate"
                  style="margin-right: 5px"
                  @focusChanged="headerRateLossFocus($event, headerExchRate)"
                />
                <Input
                  placeholder="Exch. Factor"
                  :isDisabled="true"
                  textAlign="right"
                  :inputValue.sync="headerExchFactor"
                  validator="positiveDecimal,15,4"
                />
              </div>
              <RemarksBox
                :text.sync="remarks"
                :isDisabled="viewMode"
                title="Remarks"
                :maxLength="200"
                style="height: 60%"
              />
            </div>
          </div>
        </BoxContainer>

        <div style="height: 100%; margin-top: 10px">
          <Tab
            :preventTabSwitch="true"
            :tabs="tabsItems"
            :tabIndex.sync="currentTabIndex"
            @tabClicked="tabClicked"
            style="height: 100%"
          >
            <div slot="Charges Details" class="tab-slot">
              <div
                class="tab-details-styling"
                :style="{
                  width: showJobCharges || showInputCharges ? '50%' : '100%',
                }"
              >
                <div class="tab-button-total">
                  <div class="tab-buttons" style="width: 100%">
                    <button
                      v-if="txnType == '0' && (!viewMode || action != '')"
                      @click="openJobCharges"
                      style="min-width: 100px"
                      :disabled="showJobCharges || payeeTypeCodeNotSelected"
                    >
                      Select From Job Charges
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openChargesInput"
                      style="min-width: 100px"
                      :disabled="showInputCharges"
                    >
                      Add Charge Code
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="deleteJobCharges"
                      style="min-width: 100px"
                      :disabled="showInputCharges"
                    >
                      Delete Charges
                    </button>
                  </div>
                  <div
                    :style="{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                      margin: showJobCharges || showInputCharges ? '5px' : '',
                    }"
                  >
                    <div style="margin-right: 15px; align-self: center">
                      <b style="font-size: 1em">Total Tax</b>
                      <div class="total-block" style="font-size: 1em; padding-top: 5px">
                        MYR {{ totalTaxJCDisplay }}
                      </div>
                    </div>
                    <div style="margin-right: 10px; align-self: center">
                      <b style="font-size: 1em">Total Amount</b>
                      <div style="display: flex">
                        <div class="total-block" style="font-size: 1em; padding-top: 5px">
                          {{
                            jcMultiCurr ? 'N/A' : (
                              `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalFAmountJCDisplay}`
                            )
                          }}
                        </div>
                        <div
                          class="total-block"
                          style="
                            background-color: #467cdc;
                            color: white;
                            font-weight: bold;
                            font-size: 1em;
                            padding-top: 5px;
                          "
                        >
                          MYR {{ totalAmountJCDisplay }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <TableBox
                  title="Charges Details"
                  v-if="showJobChargesTable"
                  @buttonClicked="jobChargesTableButtonClicked"
                  :tableData="chargesDetailsList"
                  :dataType="jobChargesDataType"
                  style="height: auto; z-index: 0"
                  :additionalButtons="action == '' ? [] : tabTableButtons"
                  :selectedRow.sync="selectedChargesDetails"
                  :hideColumns="chargesDetailsHideColumns"
                />
              </div>

              <div v-if="showJobCharges" class="side-entry-open" :style="{ width: showJobCharges ? '50%' : '0%' }">
                <div class="title-button" style="margin-top: 5px">
                  <b>Select From Job Charges</b>
                  <div class="buttons-arr">
                    <button @click="selectDeselectJCClicked"> {{ selectDeselectJC }} All </button>
                    <button @click="addSelectedJobCharges"> Add Job Charges </button>
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="right"
                      buttonText="Change Filter"
                      :filterWidth="600"
                      :isOpen.sync="jobChargesFilterOpen"
                      style="margin-right: 5px"
                    >
                      <div class="filter-popup" style="display: flex; flex-direction: row">
                        <div style="width: 100%">
                          <!-- <CheckBox
                            title="Show All Payee Type"
                            :isChecked.sync="showAllPayeeType"
                            :isDisabled="viewMode"
                          /> -->
                          <RadioButtonSet
                            title="Payee"
                            :buttons="payeeFilterSelection"
                            labelKey="label"
                            :selected.sync="selectedPayeeFilterJC"
                            :isHorizontal="true"
                            :isDisabled="viewMode"
                            style="margin-right: 5px"
                          />
                          <BoxContainer
                            title="As At"
                            :isCheckBox="true"
                            style="margin-bottom: 5px"
                            :isChecked.sync="filterJobByAsAt"
                          >
                            <div>
                              <DateTimePicker
                                :isRequired="filterJobByAsAt"
                                :showTime="false"
                                placeholder="Date"
                                :isDisabled="!filterJobByAsAt"
                                :value.sync="asAtDate"
                                :errorMessage.sync="asAtDateErr"
                              />
                            </div>
                          </BoxContainer>
                          <InputQuery
                            placeholder="Job No"
                            :items="jobNoList"
                            itemKey="Job No"
                            :showDesc="false"
                            :selectedObject.sync="selectedJobNoSearch"
                            :isDisabled="viewMode"
                            style="width: 60%"
                            :showRowLimit="true"
                            :rowLimitInputValue.sync="jobnoRowLimit"
                            @rowLimitFocusChanged="jobnoRowLimitChanged"
                          />
                          <InputQuery
                            placeholder="Charges Code"
                            :items="expenseCodeList"
                            itemKey="Expenses Code"
                            descKey="Description"
                            :selectedObject.sync="selectedChargeCodeSearch"
                            :isDisabled="viewMode"
                          />
                        </div>
                        <div style="margin-left: 10px; width: 100%">
                          <div style="width: 100%"> Container No ({{ contSearchCount }}) </div>
                          <textarea
                            title="1 Container No per line"
                            v-model="searchContNo"
                            style="width: 100%; height: 90%"
                          ></textarea>
                        </div>
                      </div>
                      <button
                        @click="refreshClicked('Job')"
                        :disabled="searchJobChargesFilterDisable"
                        style="margin-top: 10px"
                      >
                        Refresh
                      </button>
                    </ButtonFilter>
                    <button @click="cancelSelectJobCharges">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll">
                  <div class="payee-info">
                    <BoxContainer class="info-container">
                      <div> <b>Payee Type:</b> {{ selectedPayeeType['Party'] }} </div>
                      <div> <b>Payee Code:</b> {{ selectedPayeeCode[payeeCodeKey] }} </div>
                      <div><b>Payee:</b> {{ selectedPayeeCode[payeeCodeDescKey] }}</div>
                    </BoxContainer>
                    <!-- <Input
                      placeholder="No of Rows Shown"
                      :inputValue.sync="jobchargesRowLimit"
                      :isDisabled="viewMode"
                      validator="integer,6"
                      @focusChanged="jobChargesListingRowChange"
                      style="width: 30%; margin: 0px 5px; align-self: center"
                    /> -->
                  </div>
                  <TableBox
                    title="Job Charges"
                    :tableData="jobChargesListing"
                    :selectedRow.sync="selectedJobCharges"
                    :maxSelection="-1"
                    :hideColumns="jobChargesListingHideColumns"
                    :loading="jobChargesTableBoxLoading"
                    :dataType="jobChargesListDataType"
                    style="z-index: 0"
                  />
                </div>
              </div>

              <div v-if="showInputCharges" class="side-entry-open" :style="{ width: showInputCharges ? '50%' : '0%' }">
                <div class="title-button">
                  <b>{{ viewMode ? 'Job Charges' : subtitleJC }}</b>
                  <div class="buttons-arr">
                    <button v-if="!viewMode" @click="saveChargesClicked">
                      {{ buttonTitleJC }}
                    </button>
                    <button @click="cancelChargesClicked">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll; min-height: calc(100% - 40px)">
                  <div style="display: flex; width: 100%">
                    <div style="width: 50%; margin-right: 5px">
                      <InputQuery
                        placeholder="Charges Code"
                        :items="expenseCodeList"
                        itemKey="Code - Description"
                        :tableBoxHideColumns="['Code - Description']"
                        :selectedObject.sync="selectedCharges"
                        :isRequired="true"
                        :isDisabled="viewMode || billedJob"
                        :errorMessage.sync="chargesErr"
                        ref="jccharges"
                      />
                      <InputQuery
                        ref="jcjobno"
                        placeholder="Job No"
                        :items="jobNoList"
                        itemKey="Job No"
                        :selectedObject.sync="selectedJobNo"
                        :search.sync="selectedJobNoFreeText"
                        :errorMessage.sync="jobNoErr"
                        :isDisabled="viewMode || billedJob"
                        :isRequired="jobNoRequired"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="jobnoRowLimit"
                        :allowFreeText="true"
                        @selected="selectedJobNoChange"
                        @rowLimitFocusChanged="jobnoRowLimitChanged"
                      />
                      <InputQuery
                        placeholder="Cont No"
                        :items="containerList"
                        itemKey="Cont No"
                        :selectedObject.sync="selectedContainer"
                        :search.sync="selectedContainerFreeText"
                        :errorMessage.sync="containerErr"
                        :isDisabled="viewMode"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="contnoRowLimit"
                        :allowFreeText="true"
                        @selected="contNoSelected"
                        @rowLimitFocusChanged="contnoRowLimitChanged"
                      />
                      <InputQuery
                        placeholder="Consignment No"
                        :items="consignmentList"
                        itemKey="Consignment No"
                        :selectedObject.sync="selectedConsignment"
                        :search.sync="selectedConsignmentFreeText"
                        :errorMessage.sync="consignmentErr"
                        :isDisabled="viewMode"
                        ref="jccsgn"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="consignmentRowLimit"
                        :allowFreeText="true"
                        @selected="cnnoSelected"
                        @rowLimitFocusChanged="consignmentRowLimitChanged"
                      />
                    </div>
                    <div style="width: 50%; align-content: space-around; margin-left: 5px">
                      <InputQuery
                        v-if="txnType != '0'"
                        placeholder="AP Invoice No"
                        :items="apinList"
                        itemKey="AP Invoice No"
                        :selectedObject.sync="selectedAPIN"
                        :tableBoxDataType="{ 'Total Amt': 'currency', 'FTotal Amt': 'currency' }"
                        :tableBoxHideColumns="['Total Amt', 'FTotal Amt']"
                      />
                      <InputQuery
                        placeholder="Cost Code"
                        :items="costCodeList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedJCCostCode"
                        :isDisabled="viewMode"
                      />
                      <Input
                        placeholder="SMK No"
                        :inputValue.sync="smkNoJC"
                        :isDisabled="viewMode"
                        :errorMessage.sync="smkNoJCErr"
                        validator="text,35"
                        ref="jcsmkno"
                      />
                      <Input
                        placeholder="PO No"
                        :inputValue.sync="poNoJC"
                        :isDisabled="viewMode"
                        :errorMessage.sync="poNoJCErr"
                        validator="text,25"
                        ref="jcpono"
                      />
                    </div>
                  </div>
                  <RemarksBox
                    :text.sync="jobChargeRemarks"
                    :isDisabled="viewMode"
                    title="Remarks"
                    :maxLength="200"
                    style="height: 100%"
                  />
                  <div style="margin: 5px; margin-top: 15px">
                    <div class="foreign-currency-main">
                      <div class="foreign-currency-controls">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                textAlign="right"
                                placeholder="Quantity"
                                style="max-width: 120px"
                                :isRequired="true"
                                :inputValue.sync="chargesQty"
                                ref="jcqty"
                                :errorMessage.sync="chargesQtyErr"
                                :isDisabled="viewMode"
                                validator="positiveDecimal,6,,none"
                                @focusChanged="chargesPriceLossFocus($event, 'qty', chargesQty)"
                              />
                              <InputQuery
                                placeholder="UOM"
                                :items="uomList"
                                itemKey="UOM Code"
                                :showDesc="false"
                                :selectedObject.sync="selectedChargesUOM"
                                :isDisabled="viewMode"
                                :allowFreeText="true"
                                :search.sync="uomText"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column" style="text-align: center">Buy (MYR) -> Sell (MYR)</div>
                          <div class="foreign-currency-column" style="text-align: center"> MYR Equivalent </div>
                        </div>
                        <BoxContainer background="rgba(225, 255, 255)" title="Buying">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Currency"
                                  :isRequired="true"
                                  :items="currencyList"
                                  itemKey="Currency Code"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  :selectedObject.sync="selectedChargesCurrency"
                                  ref="chargesCurrency"
                                  :errorMessage.sync="chargesCurrencyErr"
                                  @selected="chargesCurrSelected"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesBuyFUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'buyfunitprice', chargesBuyFUPrice)"
                                  :errorMessage.sync="chargesBuyFUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcbuyfuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesBuyFTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'buyftotalprice', chargesBuyFTotalPrice)"
                                  :errorMessage.sync="chargesBuyFTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcbuyftotprice"
                                />
                                <Input
                                  placeholder="Exch. Rate"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable || chargeExchgRateDisabled"
                                  textAlign="right"
                                  :inputValue.sync="chargesExchangeRate"
                                  :errorMessage.sync="chargesExchangeRateErr"
                                  @focusChanged="chargesPriceLossFocus($event, 'exchgRate', chargesExchangeRate)"
                                  validator="positiveDecimal,15,4"
                                  ref="jcexchgrate"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesBuyUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'buyunitprice', chargesBuyUPrice)"
                                  :errorMessage.sync="chargesBuyUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcbuyuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesBuyTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'buytotalprice', chargesBuyTotalPrice)"
                                  :errorMessage.sync="chargesBuyTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcbuytotprice"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)" title="Mark Up">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row"> </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesMUpFUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'mUpfunitprice', chargesMUpFUPrice)"
                                  :errorMessage.sync="chargesMUpFUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmupfuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesMUpFTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'mupftotalprice', chargesMUpFTotalPrice)"
                                  :errorMessage.sync="chargesMUpFTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmupftotprice"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesMUpUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'mupunitprice', chargesMUpUPrice)"
                                  :errorMessage.sync="chargesMUpUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmupuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesMUpTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'muptotalprice', chargesMUpTotalPrice)"
                                  :errorMessage.sync="chargesMUpTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmuptotprice"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)" title="Selling">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Currency"
                                  :isRequired="true"
                                  :items="currencyList"
                                  itemKey="Currency Code"
                                  :isDisabled="true"
                                  :selectedObject.sync="selectedChargesCurrency"
                                  ref="chargesCurrency"
                                  :errorMessage.sync="chargesCurrencyErr"
                                  @selected="chargesCurrSelected"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesSellFUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'sellfunitprice', chargesSellFUPrice)"
                                  :errorMessage.sync="chargesSellFUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcsellfuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesSellFTotalPrice"
                                  @focusChanged="
                                    chargesPriceLossFocus($event, 'sellftotalprice', chargesSellFTotalPrice)
                                  "
                                  :errorMessage.sync="chargesSellFTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcsellftotprice"
                                />
                                <Input
                                  placeholder="Exch. Rate"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesExchangeRate"
                                  :errorMessage.sync="chargesExchangeRateErr"
                                  @focusChanged="chargesPriceLossFocus($event, 'exchgRate', chargesExchangeRate)"
                                  validator="positiveDecimal,15,4"
                                  ref="jcexchgrate"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesSellUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'sellunitprice', chargesSellUPrice)"
                                  :errorMessage.sync="chargesSellUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcselluprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesSellTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'selltotalprice', chargesSellTotalPrice)"
                                  :errorMessage.sync="chargesSellTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcselltotprice"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Tax Code"
                                  itemKey="Tax Code"
                                  :allowFreeText="false"
                                  descKey="Tax Code Desc"
                                  :showDesc="false"
                                  :isDisabled="
                                    viewMode ||
                                    chargesAmtInputDisable ||
                                    !sasFieldsControl['cmdTaxCode'] ||
                                    !sasFieldsControl['TxtTaxCode']
                                  "
                                  :items="taxCodeListToUse"
                                  :selectedObject.sync="selectedChargesTaxCode"
                                  @selected="taxCodeChrgSelected"
                                />
                                <Input
                                  placeholder="Tax Rate %"
                                  style="width: 50%; margin-left: 4px"
                                  :isDisabled="viewMode || chargesAmtInputDisable || !sasFieldsControl['TxtTaxRate']"
                                  textAlign="right"
                                  :inputValue.sync="chargesTaxRate"
                                  @focusChanged="chargesPriceLossFocus($event, 'taxrate', chargesTaxRate)"
                                  :isRequired="selectedChargesTaxCode != null"
                                  validator="positiveInteger,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div>Net Inclusive Tax</div>
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesFTaxAmt"
                                  :errorMessage.sync="chargesFTaxAmtErr"
                                  validator="decimal,14,4"
                                  ref="jcftaxamt"
                                />
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesFUPriceInclTax"
                                  :errorMessage.sync="chargesFUPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesFTotalPriceInclTax"
                                  :errorMessage.sync="chargesFTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div>Net Inclusive Tax</div>
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesTaxAmt"
                                  :errorMessage.sync="chargesTaxAmtErr"
                                  validator="decimal,14,4"
                                  ref="jctaxamt"
                                />
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesUPriceInclTax"
                                  :errorMessage.sync="chargesUPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesTotalPriceInclTax"
                                  :errorMessage.sync="chargesTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <div v-if="enableVendorRebate" style="margin-top: 10px; width: calc(100% / 3)">
                          <Input
                            textAlign="right"
                            placeholder="Rebate Amt"
                            style="max-width: 100%"
                            :inputValue.sync="chargesRebate"
                            :errorMessage.sync="chargesRebateErr"
                            :isDisabled="viewMode"
                            validator="decimal,14,4"
                            @focusChanged="chargesPriceLossFocus($event, 'rebate', chargesRebate)"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-background">
                        <div class="background-left"></div>
                        <div class="background-middle"></div>
                        <div class="background-right"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div slot="Direct Posting" class="tab-slot">
              <div
                class="tab-details-styling"
                :style="{
                  width: showExpenses || showPO || showInputDirectPosting ? '50%' : '100%',
                }"
              >
                <div class="tab-button-total">
                  <div class="tab-buttons" style="width: 100%">
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openExpenses"
                      :disabled="showExpenses || payeeTypeCodeNotSelected"
                    >
                      Select Expenses
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openPO"
                      :disabled="showPO || payeeTypeCodeNotSelected"
                    >
                      Select PO
                    </button>
                    <button v-if="!viewMode || action != ''" @click="openDPInput" :disabled="showInputDirectPosting">
                      Add Direct Posting
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="deleteDirectPosting"
                      :disabled="showInputDirectPosting"
                    >
                      Delete Direct Posting
                    </button>
                  </div>
                  <div
                    :style="{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                      margin: showExpenses || showPO || showInputDirectPosting ? '5px' : '',
                    }"
                  >
                    <div style="margin-right: 15px; align-self: center">
                      <b style="font-size: 1em">Total Tax</b>
                      <div class="total-block" style="font-size: 1em; padding-top: 5px">
                        MYR {{ totalTaxDPDisplay }}
                      </div>
                    </div>
                    <div style="margin-right: 10px; align-self: center">
                      <b style="font-size: 1em">Total Amount</b>
                      <div style="display: flex">
                        <div class="total-block" style="font-size: 1em; padding-top: 5px">
                          {{
                            dpMultiCurr ? 'N/A' : (
                              `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalFAmountDPDisplay}`
                            )
                          }}
                        </div>
                        <div
                          class="total-block"
                          style="
                            background-color: #467cdc;
                            color: white;
                            font-weight: bold;
                            font-size: 1em;
                            padding-top: 5px;
                          "
                        >
                          MYR {{ totalAmountDPDisplay }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <TableBox
                  title="Direct Posting"
                  v-if="showDirectPostingTable"
                  @buttonClicked="directPostingTableButtonClicked"
                  :tableData="directPostingDetailsList"
                  :dataType="directPostingDataType"
                  style="height: auto; z-index: 0"
                  :additionalButtons="action == '' ? [] : tabTableButtons"
                  :selectedRow.sync="selectedDPEntry"
                  :hideColumns="directPostingHideColumns"
                />
              </div>

              <div v-if="showExpenses" class="side-entry-open" :style="{ width: showExpenses ? '50%' : '0%' }">
                <div class="title-button">
                  <b>Select Expenses</b>
                  <div class="buttons-arr">
                    <button @click="selectDeselectExpensesClicked"> {{ selectDeselectExpenses }} All </button>
                    <button @click="addSelectedExpenses">Add Expenses</button>
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="right"
                      buttonText="Change Filter"
                      :filterWidth="500"
                      :isOpen.sync="expensesFilterOpen"
                      style="margin-right: 5px"
                    >
                      <div style="display: flex; flex-direction: row" class="filter-popup">
                        <div style="width: 100%">
                          <BoxContainer
                            title="Expense Date"
                            :isCheckBox="true"
                            style="margin-bottom: 5px"
                            :isChecked.sync="filterExpensesByDate"
                          >
                            <div>
                              <DateTimePicker
                                :isRequired="filterExpensesByDate"
                                :showTime="false"
                                placeholder="From"
                                :isDisabled="!filterExpensesByDate"
                                :value.sync="filterExpenseFromDate"
                              />
                              <DateTimePicker
                                :isRequired="filterExpensesByDate"
                                :showTime="false"
                                placeholder="To"
                                :isDisabled="!filterExpensesByDate"
                                :value.sync="filterExpenseToDate"
                              />
                            </div>
                          </BoxContainer>
                          <InputQuery
                            placeholder="Expense Code"
                            :items="expenseCodeList"
                            itemKey="Expenses Code"
                            descKey="Description"
                            :selectedObject.sync="selectedExpenseCode"
                            :isDisabled="viewMode"
                          />
                          <CheckBox title="GST Inclusive" :isChecked.sync="gstInclusive" :isDisabled="viewMode" />
                        </div>
                        <div style="width: 100%; margin: 10px">
                          <InputQuery
                            placeholder="Vehicle #"
                            :items="vehicleNoList"
                            itemKey="Lorry No"
                            :showDesc="false"
                            :selectedObject.sync="selectedExpenseVehicle"
                            :isDisabled="viewMode"
                          />
                          <Input
                            placeholder="Job #"
                            :inputValue.sync="expenseJobNo"
                            :isDisabled="viewMode"
                            validator="text,25"
                            style="width: 92%"
                          />
                          <Input
                            placeholder="Cash Voucher #"
                            :inputValue.sync="expenseCashVoucher"
                            :isDisabled="viewMode"
                            validator="text,25"
                            style="width: 92%"
                          />
                          <InputQuery
                            placeholder="Expenses Doc Ref #"
                            :items="expensesDocRefNoList"
                            itemKey="Expenses  Ref #"
                            :showDesc="false"
                            :selectedObject.sync="selectedExpensesRefNo"
                            :isDisabled="viewMode"
                          />
                        </div>
                      </div>
                      <button
                        @click="refreshClicked('Expenses')"
                        :disabled="searchExpensesFilterDisable"
                        style="margin-top: 10px"
                      >
                        Refresh
                      </button>
                    </ButtonFilter>
                    <button @click="cancelSelectExpenses">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll">
                  <TableBox
                    title="Expenses"
                    :tableData="expensesListing"
                    :selectedRow.sync="selectedExpenses"
                    :maxSelection="-1"
                    :hideColumns="[]"
                    style="z-index: 0"
                    :loading="expensesTableLoading"
                    :dataType="expensesListingDataType"
                  />
                </div>
              </div>

              <div v-if="showPO" class="side-entry-open" :style="{ width: showPO ? '50%' : '0%' }">
                <div class="title-button" style="margin-top: 5px">
                  <b>Select From PO</b>
                  <div class="buttons-arr">
                    <button @click="selectDeselectPOClicked">{{ selectDeselectPO }} All</button>
                    <button @click="addSelectedPO"> Add PO </button>
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="right"
                      buttonText="Change Filter"
                      :filterWidth="300"
                      :isOpen.sync="poFilterOpen"
                      :buttonIsDisabled="poFilterBtnDisabled"
                      style="margin-right: 5px"
                    >
                      <div class="filter-popup">
                        <!-- <CheckBox
                          title="Show All Payee Type"
                          :isChecked.sync="poFilterShowAllPayee"
                          :isDisabled="viewMode"
                        /> -->
                        <!-- <RadioButtonSet
                          title="Payee"
                          :buttons="payeeFilterSelection"
                          labelKey="label"
                          :selected.sync="selectedPayeeFilterPO"
                          :isHorizontal="true"
                          :isDisabled="viewMode"
                          style="margin-right: 5px"
                        /> -->
                        <BoxContainer
                          title="As At"
                          :isCheckBox="true"
                          style="margin-bottom: 5px"
                          :isChecked.sync="filterPOByAsAt"
                        >
                          <div>
                            <DateTimePicker
                              :isRequired="filterPOByAsAt"
                              :showTime="false"
                              placeholder="Date"
                              :isDisabled="!filterPOByAsAt"
                              :value.sync="poAsAtDate"
                              :errorMessage.sync="poAsAtDateErr"
                            />
                          </div>
                        </BoxContainer>
                        <Input
                          placeholder="PO #"
                          :inputValue.sync="filterPONo"
                          :isDisabled="viewMode"
                          :errorMessage.sync="filterPONoErr"
                          validator="text,35"
                        />
                      </div>
                      <button @click="refreshClicked('PO')" style="margin-top: 10px">Refresh</button>
                    </ButtonFilter>
                    <button @click="cancelSelectPO">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll">
                  <div class="payee-info">
                    <BoxContainer class="info-container">
                      <div> <b>Payee Type:</b> {{ selectedPayeeType['Party'] }} </div>
                      <div> <b>Payee Code:</b> {{ selectedPayeeCode[payeeCodeKey] }} </div>
                      <div><b>Payee:</b> {{ selectedPayeeCode[payeeCodeDescKey] }}</div>
                    </BoxContainer>
                  </div>
                  <TableBox
                    title="PO"
                    :tableData.sync="poListing"
                    :selectedRow.sync="selectedPO"
                    :maxSelection="-1"
                    :hideColumns="[]"
                    :dataType="poListingDataType"
                    :loading="poTableBoxLoading"
                    style="z-index: 0"
                  />
                </div>
              </div>
              <div
                v-if="showInputDirectPosting"
                class="side-entry-open"
                :style="{ width: showInputDirectPosting ? '50%' : '0%' }"
              >
                <div class="title-button">
                  <b>{{ viewMode ? 'Direct Posting' : subtitleDP }}</b>
                  <div class="buttons-arr">
                    <button @click="saveDirectPosting" v-if="!viewMode"> Save Direct Posting </button>
                    <button @click="cancelDirectPostingClicked">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll; min-height: calc(100% - 40px)">
                  <div style="display: flex; justify-content: space-between; margin-top: 5px">
                    <div style="width: 100%">
                      <InputQuery
                        :placeholder="`${txnType == '0' ? 'Debit' : 'Credit'} Account`"
                        :items="glAccList"
                        itemKey="Code - Description"
                        ::tableBoxHideColumns="['Code - Description']"
                        :selectedObject.sync="selectDrCrAcc"
                        :isRequired="true"
                        :isDisabled="viewMode"
                        :errorMessage.sync="selectDrCrAccErr"
                        ref="dpdrcracc"
                      />
                      <InputQuery
                        :placeholder="`Tax ${txnType == '0' ? 'Debit' : 'Credit'} Account`"
                        :items="glAccList"
                        itemKey="Code - Description"
                        :tableBoxHideColumns="['Code - Description']"
                        :selectedObject.sync="selectedTaxDrCrAcc"
                        :isDisabled="viewMode"
                        :errorMessage.sync="selectedTaxDrCrAccErr"
                      />
                      <InputQuery
                        placeholder="Cost Code"
                        :items="costCodeList"
                        itemKey="Cost Code"
                        descKey="Cost Desc"
                        :showDesc="false"
                        :selectedObject.sync="selectedDPCostCode"
                        :isDisabled="viewMode"
                      />
                    </div>
                    <div style="width: 100%">
                      <Input
                        placeholder="SMK No"
                        :inputValue.sync="smkNoDP"
                        :isDisabled="viewMode"
                        :errorMessage.sync="smkNoDPErr"
                        validator="text,35"
                        ref="dpsmkno"
                      />
                      <Input
                        placeholder="PO No"
                        :inputValue.sync="poNoDP"
                        :isDisabled="viewMode"
                        :errorMessage.sync="poNoDPErr"
                        validator="text,35"
                        ref="dppono"
                      />
                      <InputQuery
                        placeholder="Vehicle No."
                        :items="vehicleNoList"
                        itemKey="Lorry No"
                        descKey="Vehicle Code"
                        :showDesc="false"
                        :selectedObject.sync="selectedDPVehicleNo"
                        :isDisabled="viewMode"
                      />
                    </div>
                  </div>
                  <div style="width: 100%; margin: 0px 10px">
                    <RemarksBox
                      :text.sync="directPostingRemarks"
                      :isDisabled="viewMode"
                      title="Remarks"
                      :maxLength="200"
                      style="height: 100%"
                    />
                  </div>
                  <div style="margin: 5px; margin-top: 15px">
                    <div class="foreign-currency-main">
                      <div class="foreign-currency-controls">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column"></div>
                          <div class="foreign-currency-column" style="text-align: center"></div>
                          <div class="foreign-currency-column" style="text-align: center"> MYR Equivalent </div>
                        </div>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Currency"
                                  :isRequired="true"
                                  :items="currencyList"
                                  itemKey="Currency Code"
                                  descKey="Description"
                                  :isDisabled="viewMode"
                                  :selectedObject.sync="selectedDPCurrency"
                                  ref="dpcurrency"
                                  :errorMessage.sync="dpCurrencyErr"
                                  :showDesc="false"
                                  @selected="dpCurrSelected"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode"
                                  textAlign="right"
                                  :inputValue.sync="dpFTotalPrice"
                                  @focusChanged="dpPriceLossFocus($event, 'ftotalprice', dpFTotalPrice)"
                                  :errorMessage.sync="dpFTotalPriceErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Exch. Rate"
                                  :isRequired="true"
                                  :isDisabled="viewMode || dpExchgRateDisabled"
                                  textAlign="right"
                                  :inputValue.sync="dpExchangeRate"
                                  :errorMessage.sync="dpExchangeRateErr"
                                  validator="positiveDecimal,15,4"
                                  @focusChanged="dpPriceLossFocus($event, 'exchgrate', dpExchangeRate)"
                                  ref="dpexchrate"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode"
                                  textAlign="right"
                                  :inputValue.sync="dpTotalPrice"
                                  @focusChanged="dpPriceLossFocus($event, 'totalprice', dpTotalPrice)"
                                  :errorMessage.sync="dpTotalPriceErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Tax Code"
                                  itemKey="Tax Code"
                                  :allowFreeText="false"
                                  descKey="Tax Code Desc"
                                  :showDesc="false"
                                  :isDisabled="viewMode"
                                  :items="taxCodeListToUse"
                                  :selectedObject.sync="selectedDPTaxCode"
                                  @selected="taxCodeDPSelected"
                                />
                                <Input
                                  placeholder="Tax Rate %"
                                  style="width: 50%; margin-left: 4px"
                                  :isDisabled="viewMode"
                                  textAlign="right"
                                  :inputValue.sync="dpTaxRate"
                                  @focusChanged="dpPriceLossFocus($event, 'taxrate', dpTaxRate)"
                                  validator="positiveInteger,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="dpFTaxAmt"
                                  :errorMessage.sync="dpFTaxAmtErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Net Inclusive Tax"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="dpFTotalPriceInclTax"
                                  :errorMessage.sync="dpFTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="dpTaxAmt"
                                  :errorMessage.sync="dpTaxAmtErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Net Inclusive Tax"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="dpTotalPriceInclTax"
                                  :errorMessage.sync="dpTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                      </div>
                      <div class="foreign-currency-background">
                        <div class="background-left"></div>
                        <div class="background-middle"></div>
                        <div class="background-right"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div slot="Attachment" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <div style="display: flex; justify-content: end; margin-top: 5px">
                  <button v-if="!viewMode" @click="openAttachmentPopup('new')" style="margin-right: 10px"
                    >Add Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="openAttachmentPopup('edit')"
                    style="margin-right: 10px"
                    >Edit Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="deleteAttachmentClicked"
                    style="margin-right: 10px"
                    >Del Attachment</button
                  >
                </div>
                <TableBox
                  v-if="showAttTable"
                  title="Attachment"
                  :tableData="attachmentsList"
                  :selectedRow.sync="selectedAtt"
                  :additionalButtons="[{ text: 'View', index: 0, width: 60 }]"
                  @buttonClicked="attAdditionalBtnsClicked"
                  :hideColumns="[
                    'Action',
                    'Doc Image',
                    'Sequence No',
                    'Document GUID',
                    'ID',
                    'Doc Ref No',
                    'Job No',
                    'Doc Code',
                    'Doc Type',
                    'Doc Rec Date',
                    'Doc Rec Time',
                    '_attachmentAction',
                  ]"
                  :dataType="{ 'Doc Date': 'date' }"
                />
              </div>
            </div>

            <div slot="Events" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <BoxContainer title="GL Entry">
                  <TableBox
                    :tableData="glEntryList"
                    title="GL Entry"
                    :maxSelection="0"
                    style="height: auto"
                    :dataType="glEntryTableDataType"
                  />
                  <div style="display: flex; justify-content: space-around; margin-top: 10px">
                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL DEBIT</div>
                      <div class="total-block expand" style="margin-left: 5px">{{ totalDebit }}</div>
                    </div>

                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL CREDIT</div>
                      <div class="total-block expand" style="margin-left: 5px">{{ totalCredit }}</div>
                    </div>
                  </div>
                </BoxContainer>
              </div>
            </div>
          </Tab>
        </div>
      </div>

      <BoxContainer>
        <div class="header-total-container">
          <div style="display: flex; margin-right: 20px">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em"> Total {{ pageTitle }} </div>
            <div style="display: flex">
              <div
                class="total-block expand"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px; min-width: 210px"
              >
                {{
                  multiCurr ? 'N/A' : (
                    `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalAPInvFAmountDisplay}`
                  )
                }}
              </div>
              <div
                class="total-block expand"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                  min-width: 210px;
                "
              >
                MYR {{ totalAPInvAmountDisplay }}
              </div>
            </div>
          </div>
          <div style="display: flex">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em"> Total Outstanding </div>
            <div style="display: flex">
              <div
                class="total-block expand"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px; min-width: 210px"
              >
                {{
                  multiCurr ? 'N/A' : (
                    `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalFOutstandingDisplay}`
                  )
                }}
              </div>
              <div
                class="total-block expand"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                  min-width: 210px;
                "
              >
                MYR {{ totalOutstandingDisplay }}
              </div>
            </div>
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>

    <PopupV2
      :show="showDeleteAP"
      :title="`Delete ${pageTitle}`"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div>
        Please state reason to delete
        {{ selectedAPListing.length > 0 ? selectedAPListing[0][`${pageTitle} No`] : '' }}?
      </div>
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deleteAPRemarks"
        :errorMessage.sync="deleteAPRemarksErr"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <PopupV2 :show="showEnterPswd" title="Enter Password" @buttonClicked="pswdPopupClicked" :buttons="pswdPopupButtons">
      <div>Please enter password to proceed:</div>
      <Input
        placeholder="Job Closed Password"
        :inputValue.sync="jobclosepswd"
        :errorMessage.sync="jobclosepswdErr"
        textType="password"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="attachmentsPopupBtnsClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            @fileChanged="handleFileUpload"
            :errorMessage.sync="attachmentsRequiredErrorMessage"
          />
          <div v-if="attachmentsAction == 'E'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment({
                  'File Name': selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '',
                  'MIME Type': null,
                  'File Data': selectedAtt.length > 0 ? selectedAtt[0]['Doc Image'] : '',
                })
              "
            >
              {{ selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '' }}
            </span>
          </div>
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>

    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedAtt[0]['File Type'].replaceAll('.', '').toUpperCase() }} files cannot be previewed. <br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    // For UI to display in selected currency format
    totalAPInvAmountDisplay: function () {
      return this.$currencyFormat(this.totalAPInvAmount);
    },
    totalAPInvFAmountDisplay: function () {
      return this.$currencyFormat(this.totalAPInvFAmount);
    },
    totalOutstandingDisplay: function () {
      return this.$currencyFormat(this.totalOutstanding);
    },
    totalFOutstandingDisplay: function () {
      return this.$currencyFormat(this.totalFOutstanding);
    },
    totalTaxJCDisplay: function () {
      return this.$currencyFormat(this.totalTaxJC);
    },
    totalFAmountJCDisplay: function () {
      return this.$currencyFormat(this.totalFAmountJC);
    },
    totalAmountJCDisplay: function () {
      return this.$currencyFormat(this.totalAmountJC);
    },
    totalTaxDPDisplay: function () {
      return this.$currencyFormat(this.totalTaxDP);
    },
    totalAmountDPDisplay: function () {
      return this.$currencyFormat(this.totalAmountDP);
    },
    totalFAmountDPDisplay: function () {
      return this.$currencyFormat(this.totalFAmountDP);
    },

    chargesDetailsHideColumns: function () {
      const cols = [
        'APInvNo',
        'Seq No',
        'Job Charges Code',
        'TxnID',
        'JobChargesTxnID',
        'JobSeq',
        'RecordSource',
        'Billed',
        'action',
      ];
      if (!this.enableVendorRebate) {
        cols.push('Rebate Amount');
      }
      return cols;
    },

    directPostingHideColumns: function () {
      const cols = ['APInvNo', 'Seq No', 'ExpenseGUID'];

      if (this.txnType == '0') {
        cols.push('Credit Ac');
        cols.push('Tax Credit Ac');
      } else {
        cols.push('Debit Ac');
        cols.push('Tax Debit Ac');
      }
      return cols;
    },
    // Calculate total debit in GL Entry
    totalDebit: function () {
      if (this.glEntryList.length > 0) {
        const debits = this.glEntryList.filter((entry) => entry['Debit Ac'] != '0');
        const total = debits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },

    // Calculate total credit in GL Entry
    totalCredit: function () {
      if (this.glEntryList.length > 0) {
        const credits = this.glEntryList.filter((entry) => entry['Credit Ac'] != '0');
        const total = credits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },

    selectDeselectJC: function () {
      return this.jobChargesListing.length > 0 && this.selectedJobCharges.length === this.jobChargesListing.length ?
          'Deselect'
        : 'Select';
    },

    contSearchCount: function () {
      let containers = [];
      if (this.searchContNo.trim().length > 0) {
        containers = this.searchContNo.trim().split('\n');
        containers = containers.filter((cont) => {
          cont.trim() != '' || cont.trim() != ',';
        });
      }
      return containers.length;
    },

    searchJobChargesFilterDisable: function () {
      return (
        !this.selectedPayeeFilterJC &&
        !this.filterJobByAsAt &&
        !this.selectedJobNoSearch &&
        !this.selectedChargeCodeSearch
      );
    },

    selectDeselectExpenses: function () {
      return this.expensesListing.length > 0 && this.selectedExpenses.length === this.expensesListing.length ?
          'Deselect'
        : 'Select';
    },

    searchExpensesFilterDisable: function () {
      return (
        !this.filterExpensesByDate &&
        !this.selectedExpenseCode &&
        !this.selectedExpenseVehicle &&
        !this.expenseJobNo &&
        !this.expenseCashVoucher &&
        !this.selectedExpensesRefNo
      );
    },

    selectDeselectPO: function () {
      return this.poListing.length > 0 && this.selectedPO.length === this.poListing.length ? 'Deselect' : 'Select';
    },

    payeeTypeCodeNotSelected: function () {
      return !this.selectedPayeeType == null || !this.selectedPayeeCode;
    },

    jobChargesListingHideColumns: function () {
      const cols = ['Pending Billing Charges', 'MTR Job Charges', 'Ref No'];
      if (!this.enableVendorRebate) {
        cols.push('Rebate');
      }
      return cols;
    },

    pageLabel: function () {
      return (
        this.txnType == '1' ? 'Credit Note'
        : this.txnType == '3' ? 'Debit Note'
        : 'Invoice'
      );
    },

    taxCodeListToUse: function () {
      if (this.txnType == '3') return this.ARTaxCodeList; // APDN akin to "billing to supplier", therefore uses vwTaxCodeSupply for tax codes
      return this.taxCodeList;
    },
  },
  data: function () {
    return {
      txnType: '0', // 0-APIN; 1-APCN; 3-APDN
      // pageType: '',
      pageTitle: '',
      menu: [
        {
          title: `New `,
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'mnuNew',
        },
        {
          title: `Edit `,
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'mnuEdit',
        },
        {
          title: `Delete `,
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'mnuDelete',
        },
        {
          title: `View `,
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: false,
        },
        {
          title: 'Import Data',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: true,
          key: 'mnuImportData',
        },
        {
          title: 'Authorize',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnAuthorized',
        },
      ],
      sasFieldsControl: {
        TxtTaxRate: true, // disable tax rate field
        cmdTaxCode: true, // disable query for tax code
        TxtTaxCode: true, // disable tax code field in direct posting
      },
      enableVendorRebate: false, // enable vendor rebate, settingssystem EnableVendorRebate
      settingsJobClosePswd: '', // password to enter if user has permission to modify closed jobs, settingssystem JobClosedPassword
      jobNoRequired: false,

      action: '',

      filterText: '',
      lastRefreshed: '',
      mainFilterIsOpen: false,
      mainFilterIsDisabled: false,
      filterAPListingByDate: true,
      apListingFromDate: new Date(),
      fromDateErrMsg: '',
      apListingToDate: new Date(),
      toDateErrMsg: '',

      filterAPListingByInvDate: false,
      apListingFromInvDate: new Date(),
      fromInvDateErrMsg: '',
      apListingToInvDate: new Date(),
      toInvDateErrMsg: '',

      filterAPListingBySearch: false,
      searchFilterItems: [
        { 'Search By': 'AP Invoice #', key: 'APINV' },
        // { 'Search By': 'AP Credit Note #', key: 'APCN' },
        { 'Search By': 'Invoice #', key: 'IN' },
      ],
      selectedSearchBy: null,
      searchByErrMsg: '',

      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedSearchCriteria: null,
      searchCriteriaErr: '',

      searchValue: '',
      searchValueErr: '',

      apListing: [],
      selectedAPListing: [],
      apListingTableLoading: false,
      apListingTableDataType: {
        Date: 'date',
        'Invoice Date': 'date',
        'Credit Note Date': 'date',
        'Debit Note Date': 'date',
        Rate: 'currency',
        Total: 'currency',
        Outstanding: 'currency',
        Term: 'number',
        'Due Date': 'date',
      },

      apDetails: [],
      apTypeList: [],
      payeeTypeList: [],
      payeeCodeList: [],
      currencyList: [],
      payeeCodeKey: '',
      termList: [{ term: '30' }, { term: '60' }, { term: '90' }, { term: '120' }],
      jobNoList: [],
      jobnoRowLimit: '3000',
      oriConsignmentList: [],
      consignmentList: [],
      consignmentRowLimit: '5000',
      oriContainerList: [],
      containerList: [],
      contnoRowLimit: '5000',
      expenseCodeList: [],
      vehicleNoList: [],
      costCodeList: [],
      uomList: [],
      taxCodeList: [],
      ARTaxCodeList: [],
      expensesDocRefNoList: [],
      payeeFilterSelection: [
        { label: 'Selected Payee', keyValue: 'selected' },
        { label: 'All Payee Type', keyValue: 'all' },
      ],
      glAccList: [],
      apinList: [],

      //#region Popup Entry & Tab
      // Popup Entry
      showPopup: false,
      popupTitle: '',
      createdBy: '',
      updatedBy: '',
      requiredError: [],
      successMessages: [],
      errorMessages: [],
      viewMode: false,

      // Tab
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Charges Details', disabled: false },
        { title: 'Direct Posting', disabled: false },
        { title: 'Attachment', disabled: false },
        { title: 'Events', disabled: false }, // hide IRBM section
      ],
      //#endregion

      //#region Summary of Events
      summaryIsCollapsed: false,
      summaryItems: [
        {
          Title: 'Indicator',
          Label: ['New', 'Cancelled', 'Paid'],
          Indicator: [
            { code: 'N', name: 'New AP Invoice', bg: '#ffff99' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'Paid', bg: '#79d279' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'Authorized',
          Label: ['Authorized', 'Unauthorize'],
          Indicator: [
            { code: 'A', name: 'Authorized', bg: '#79d279' },
            { code: 'U', name: 'Unauthorize', bg: '#ffff99' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'Posted',
          Label: ['Posted To GL', 'Opening Entry'],
          Indicator: [
            { code: 'L', name: 'Posted To GL', bg: '#79d279' },
            { code: 'O', name: 'Opening Entry', bg: '#79d279' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: false,
        },
        {
          Title: 'PaymentNo',
          Label: [],
          Indicator: [{ code: 'Payment No', name: '' }],
          indicatorToShow: '',
          bgcolorToShow: '#79d279',
          visible: false,
        },
      ],
      newLegend: {
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Completed: '#79d279',
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Pending: '#ffff99',
      },
      //#endregion

      //#region AP Header
      selectedAPType: null,
      apTypeErr: '',
      apInvNo: '',
      apInvNoErr: '',
      apInvDate: new Date(),
      apInvDateErr: '',
      invoiceNo: '',
      invoiceNoErr: '',
      invoiceDate: new Date(),
      invoiceDateErr: '',
      selectedPayeeType: null,
      payeeTypeErr: '',
      selectedPayeeCode: null,
      payeeCodeErr: '',
      selectedTerm: null,
      termInput: '',
      termErr: '',
      dueDate: new Date(),
      dueDateErr: '',
      remarks: '',
      selectedHeaderCurr: null,
      headerCurrErr: '',
      oldRate: '',
      headerExchRate: '1.0000',
      headerExchRateErr: '',
      headerExchFactor: '1',

      totalAPInvAmount: '0.00',
      totalAPInvFAmount: '0.00',
      totalOutstanding: '0.00',
      totalFOutstanding: '0.00',
      multiCurr: false,
      //#endregion

      tabTableButtons: [{ text: 'Edit', index: 0, width: 80 }],

      //#region Job Charges Details
      showJobChargesTable: true,
      chargesDetailsList: [],
      deletedChargesDetails: [],
      jobChargesDataType: {
        // Rate: 'currency',
        Qty: 'number',
        'Buying Unit Price': 'currency',
        'F.Buying Unit Price': 'currency',
        'Buying Amount': 'currency',
        'F.Buying Amount': 'currency',
        'Mark Up Unit Price': 'currency',
        'F.Mark Up Unit Price': 'currency',
        'Mark Up Amount': 'currency',
        'F.Mark Up Amount': 'currency',
        'Selling Unit Price': 'currency',
        'F.Selling Unit Price': 'currency',
        'Selling Amount': 'currency',
        'F.Selling Amount': 'currency',
        'Tax Amount': 'currency',
        'F.Tax Amount': 'currency',
        'Net Unit Price Incl Tax': 'currency',
        'Net Incl Tax': 'currency',
        'F.Net Unit Price Incl Tax': 'currency',
        'F.Net Incl Tax': 'currency',
        'Job Charges Amount': 'currency',
        'Rebate Amount': 'currency',
        'Date Updated': 'date',
      },
      selectedChargesDetails: [],
      showJobCharges: false,
      showInputCharges: false,

      // Job Charges Filter
      jobChargesFilterOpen: false,
      // showAllPayeeType: false,
      selectedPayeeFilterJC: null,
      filterJobByAsAt: false,
      asAtDate: null,
      asAtDateErr: '',
      selectedJobNoSearch: null,
      selectedChargeCodeSearch: null,
      searchContNo: '',
      jobChargesTableBoxLoading: false,
      oriJobChargesListing: [],
      jobChargesListing: [],
      jobChargesListDataType: {
        'Tax Rate': 'number',
        Amount: 'currency',
        'Adj Amt': 'currency',
        'Net Amt': 'currency',
        'F.Amount': 'currency',
        'F.Adj Amt': 'currency',
        'F.Net Amt': 'currency',
        Rate: 'currency',
        Rebate: 'currency',
        Qty: 'number',
        'Unit Price': 'currency',
        'F.Unit Price': 'currency',
        'Tax Amt': 'currency',
        'F.Tax Amount': 'currency',
      },
      selectedJobCharges: [],

      saveJCAction: '',
      jcMultiCurr: false,
      selectedJobNo: null,
      selectedJobNoFreeText: '',
      jobNoErr: '',
      billedJob: false,
      selectedContainer: null,
      selectedContainerFreeText: '',
      containerErr: '',
      selectedCharges: null,
      chargesErr: '',
      selectedConsignment: null,
      selectedConsignmentFreeText: '',
      consignmentErr: '',
      selectedAPIN: null,
      selectedJCCostCode: null,
      smkNoJC: '',
      smkNoJCErr: '',
      poNoJC: '',
      poNoJCErr: '',
      jobChargeRemarks: '',

      chargesAmtInputDisable: true,
      chargeExchgRateDisabled: true,
      chargesQty: '',
      chargesQtyErr: '',
      selectedChargesUOM: null,
      uomText: '',
      selectedChargesCurrency: null,
      chargesCurrencyErr: '',
      chargesExchangeRate: '',
      chargesExchangeRateErr: '',
      chargesFactor: '',

      // Buying
      chargesBuyFUPrice: '0.0000', // 4dp
      chargesBuyFUPriceErr: '',
      chargesBuyUPrice: '0.0000', // 4dp
      chargesBuyUPriceErr: '',
      chargesBuyFTotalPrice: '0.00', //2dp
      chargesBuyFTotalPriceErr: '',
      chargesBuyTotalPrice: '0.00', //2dp
      chargesBuyTotalPriceErr: '',
      // Mark Up
      chargesMUpFUPrice: '0.0000', // 4dp
      chargesMUpFUPriceErr: '',
      chargesMUpFTotalPrice: '0.00', // 2dp
      chargesMUpFTotalPriceErr: '',
      chargesMUpUPrice: '0.0000', // 4dp
      chargesMUpUPriceErr: '',
      chargesMUpTotalPrice: '0.00', // 2dp
      chargesMUpTotalPriceErr: '',
      // Selling
      chargesSellFUPrice: '0.0000', // 4dp
      chargesSellFUPriceErr: '',
      chargesSellFTotalPrice: '0.00', // 2dp
      chargesSellFTotalPriceErr: '',
      chargesSellUPrice: '0.0000', // 4dp
      chargesSellUPriceErr: '',
      chargesSellTotalPrice: '0.00', // 2dp
      chargesSellTotalPriceErr: '',
      // Tax
      selectedChargesTaxCode: null,
      chargesTaxRate: '',
      chargesFTaxAmt: '0.00', // 2dp
      chargesFTaxAmtErr: '',
      chargesFUPriceInclTax: '0.0000', // 4dp
      chargesFUPriceInclTaxErr: '',
      chargesFTotalPriceInclTax: '0.00', // 2dp
      chargesFTotalPriceInclTaxErr: '',
      chargesTaxAmt: '0.00', // 2dp
      chargesTaxAmtErr: '',
      chargesUPriceInclTax: '0.0000', // 4dp
      chargesUPriceInclTaxErr: '',
      chargesTotalPriceInclTax: '0.00', // 2dp
      chargesTotalPriceInclTaxErr: '',
      chargesRebate: '0.00', // 2dp
      chargesRebateErr: '',

      totalTaxJC: '0.00',
      totalAmountJC: '0.00',
      totalFAmountJC: '0.00',

      subtitleJC: '',
      buttonTitleJC: '',

      showEnterPswd: false,
      pswdPopupButtons: [
        { text: 'OK', color: 'green', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      jobclosepswd: '',
      jobclosepswdErr: '',
      //#endregion

      //#region Direct Posting Details
      showDirectPostingTable: true,
      directPostingDetailsList: [],
      deletedDirectPosting: [],
      directPostingDataType: {
        Amount: 'currency',
        'F.Amount': 'currency',
        'Tax Amount': 'currency',
        'F.Tax Amount': 'currency',
        'Net Incl Tax': 'currency',
        'F.Net Incl Tax': 'currency',
        Rate: 'currency',
        'Tax Rate': 'number',
        'Date Updated': 'date',
      },
      selectedDPEntry: [],
      showExpenses: false,
      showPO: false,
      showInputDirectPosting: false,

      //#region expenses filter
      expensesFilterOpen: false,
      filterExpensesByDate: true,
      filterExpenseFromDate: new Date(),
      filterExpenseToDate: new Date(),
      selectedExpenseCode: null,
      selectedExpenseVehicle: null,
      expenseJobNo: '',
      expenseCashVoucher: '',
      selectedExpensesRefNo: null,
      gstInclusive: false,
      //#endregion

      oriExpensesListing: [],
      expensesListing: [],
      expensesListingDataType: {
        Date: 'date',
        Amount: 'currency',
        'F.Amount': 'currency',
        Rate: 'currency',
        'Tax Amount': 'currency',
        'F.Tax Amount': 'currency',
        'Net Incl Tax': 'currency',
        'F.Net Incl Tax': 'currency',
      },
      selectedExpenses: [],
      expensesTableLoading: false,

      //#region PO Filter
      poFilterOpen: false,
      poFilterBtnDisabled: false,
      filterPOByAsAt: false,
      // poFilterShowAllPayee: false,
      selectedPayeeFilterPO: null,
      poAsAtDate: new Date(),
      poAsAtDateErr: '',
      oriPOListing: [],
      poListing: [],
      poListingDataType: {
        'PO Date': 'date',
        Quantity: 'number',
        'Unit Price': 'currency',
        Amount: 'currency',
        'F.Amount': 'currency',
        'Tax Rate': 'number',
        'Tax Amount': 'currency',
        'F.Tax Amount': 'currency',
        'Amt Incl Tax': 'currency',
        'F.Amt Incl Tax': 'currency',
      },
      poTableBoxLoading: false,
      selectedPO: [],
      filterPONo: '',
      filterPONoErr: '',
      //#endregion

      saveDPAction: '',
      dpMultiCurr: false,
      dpExchgRateDisabled: true,
      selectDrCrAcc: null,
      selectDrCrAccErr: '',
      selectedTaxDrCrAcc: null,
      selectedTaxDrCrAccErr: '',
      selectedDPCostCode: null,
      smkNoDP: '',
      smkNoDPErr: '',
      poNoDP: '',
      poNoDPErr: '',
      selectedDPVehicleNo: null,
      directPostingRemarks: '',

      selectedDPCurrency: null,
      dpCurrencyErr: '',
      dpExchangeRate: '1.0000',
      dpExchangeRateErr: '',
      dpFactor: '',
      dpFTotalPrice: '0.00',
      dpFTotalPriceErr: '',
      dpTotalPrice: '0.00',
      dpTotalPriceErr: '',
      selectedDPTaxCode: null,
      dpTaxRate: '0',
      dpFTaxAmt: '0.00', // 2dp
      dpFTaxAmtErr: '',
      dpFTotalPriceInclTax: '0.00', // 2dp
      dpFTotalPriceInclTaxErr: '',
      dpTaxAmt: '0.00', // 2dp
      dpTaxAmtErr: '',
      dpTotalPriceInclTax: '0.00', // 2dp
      dpTotalPriceInclTaxErr: '',

      totalTaxDP: '0.00',
      totalAmountDP: '0.00',
      totalFAmountDP: '0.00',
      //#endregion

      //#region GL Entry
      glEntryList: [],
      glEntryTableDataType: {
        'Ref No': 'string',
        'Debit No': 'string',
        'Credit No': 'string',
        Amount: 'currency',
        Vehicle: 'string',
        'Cost Code': 'string',
        Remarks: 'string',
      },
      //#endregion

      //#region Attachments data
      showAttTable: true,
      attachmentsList: [],
      deletedAttList: [],
      selectedAtt: [],
      showAttachmentsPopup: false,
      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],
      documents: {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      oldDoc: '',
      attachmentsAction: '', // 'N', 'E', 'V'
      attachmentsRequiredErrorMessage: '',
      showAttachmentProjector: false,
      isProjectable: false,
      attachmentProjectorFileName: '',
      attachmentProjectorData: '',
      attachmentProjectorType: '',
      //#endregion

      //#region Delete
      showDeleteAP: false,
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      deleteAPRemarks: '',
      deleteAPRemarksErr: '',
      //#endregion
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearAllData();
      this.clearAllMessages();
      this.mainFilterIsOpen = false;

      switch (e['title']) {
        case `New ${this.pageTitle}`:
          this.action = 'N';
          this.popupTitle = e['title'];

          if (this.apTypeList.length == 1) {
            this.selectedAPType = this.apTypeList[0];
            await this.apTypeSelected(this.selectedAPType);
            this.viewMode = false;
          } else {
            this.viewMode = true;
          }

          this.apInvNo = '**********';
          this.apInvDate = new Date();
          this.invoiceDate = new Date();
          this.selectedHeaderCurr = this.currencyList.find((c) => c['Currency Code'] == 'MYR');

          this.selectedPayeeFilterJC = this.payeeFilterSelection[0];
          this.selectedPayeeFilterPO = this.payeeFilterSelection[0];
          this.showPopup = true;
          break;

        case `Edit ${this.pageTitle}`: {
          if (this.selectedAPListing.length == 0) {
            this.$showAlert('error', `Edit ${this.pageTitle}`, `Please select a ${this.pageTitle} to edit`);
            return;
          }

          if (this.selectedAPListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              `Selected ${this.pageTitle} Already Cancelled`,
              `You are not allowed to edit cancelled ${this.pageTitle}!`,
            );
            return;
          } else if (
            this.selectedAPListing[0]['Indicator'] == 'P' ||
            Number(this.selectedAPListing[0]['Total']) != Number(this.selectedAPListing[0]['Outstanding'])
          ) {
            this.$showAlert(
              'error',
              `Selected ${this.pageTitle} Already Paid`,
              `You are not allowed to edit paid ${this.pageTitle}!`,
            );
            return;
          }

          const editable = await this.apEditable(
            this.selectedAPListing[0][`${this.pageTitle} No`],
            // this.pageType == 'Credit' ? '1' : '0',
            this.txnType,
          );
          if (!editable['editable']) {
            this.$showAlert('error', editable['header'], `You are not allowed to edit this ${this.pageTitle}!`);
            return;
          }

          this.action = 'E';
          this.selectedPayeeFilterJC = this.payeeFilterSelection[0];
          this.selectedPayeeFilterPO = this.payeeFilterSelection[0];
          await this.getAPInvoice(e['title']);
          this.updateSummaryItems();
          this.showPopup = true;
          break;
        }

        case `Delete ${this.pageTitle}`: {
          if (this.selectedAPListing.length == 0) {
            this.$showAlert('error', `Delete ${this.pageTitle}`, `Please select a ${this.pageTitle} to delete`);
            return;
          }

          if (this.selectedAPListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              `Selected ${this.pageTitle} Already Cancelled`,
              `You are not allowed to delete cancelled ${this.pageTitle}!`,
            );
            return;
          } else if (this.selectedAPListing[0]['Indicator'] == 'P') {
            this.$showAlert(
              'error',
              `Selected ${this.pageTitle} Already Paid`,
              `You are not allowed to delete paid ${this.pageTitle}!`,
            );
            return;
          }

          this.action = 'D';
          this.deleteAPRemarks = '';
          this.deleteAPRemarksErr = '';
          this.showDeleteAP = true;

          break;
        }

        case `View ${this.pageTitle}`: {
          if (this.selectedAPListing.length == 0) {
            this.$showAlert('error', `View ${this.pageTitle}`, `Please select a ${this.pageTitle} to view`);
            return;
          }

          this.action = '';
          this.viewMode = true;
          await this.getAPInvoice(e['title']);
          this.updateSummaryItems();
          this.showPopup = true;
          break;
        }

        case 'Import Data': {
          const route = this.$router.resolve(`/Home/${this.$route.params.user}/FINPayment/Import AP Invoice`);
          window.open(route.href, '_blank');
          break;
        }

        case 'Authorize':
        case 'Unauthorize': {
          if (this.selectedAPListing.length == 0) {
            this.$showAlert(
              'error',
              `${e['title']} ${this.pageTitle}`,
              `Please select a ${this.pageTitle} to ${e['title'].toLowerCase()}`,
            );
            return;
          } else if (this.selectedAPListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              `Selected ${this.pageTitle} Already Cancelled`,
              `You are not allowed to ${e['title'].toLowerCase()} cancelled ${this.pageTitle}!`,
            );
            return;
          }

          const editable = await this.apEditable(
            this.selectedAPListing[0][`${this.pageTitle} No`],
            // this.pageType == 'Credit' ? '1' : '0',
            this.txnType,
          );
          if (!editable['editable']) {
            this.$showAlert(
              'error',
              editable['header'],
              `You are not allowed to ${e['title'].toLowerCase()} this ${this.pageTitle}!`,
            );
            return;
          }

          this.$fullscreenLoading(`${e['title'] == 'Authorize' ? 'Authorizing' : 'Unauthorizing'} ${this.pageTitle}`);
          const dataToPost = {
            action: e['title'] == 'Authorize' ? 'A' : 'U', //A-auth, U-Unauth
            docrefname: this.txnType == '1' ? 'AP CREDIT NOTE' : 'AP INVOICE',
            docrefid: this.selectedAPListing[0][`${this.pageTitle} No`],
            date: this.selectedAPListing[0]['Date'],
          };
          const authorizeResult = await this.$apiCallV2('/APInvoice/Authorization', dataToPost);
          this.$fullscreenLoading();

          if (authorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              `${e['title']}d!`,
              `${this.pageTitle} ${this.selectedAPListing[0][`${this.pageTitle} No`]} was successfully ${e['title']}d.`,
            );
          } else {
            this.$showAlert('error', `${e['title']} Unsuccessful`, `${authorizeResult[0]['ErrMsg']}`);
          }
          this.apListing = [];
          this.selectedAPListing = [];
          this.$nextTick(async () => await this.refreshClicked('APListing'));
          break;
        }
      }
    },

    apEditable: async function (apno, txntype) {
      this.$fullscreenLoading(`Checking ${this.pageTitle} Editable`);
      const results = (
        await this.$apiCallV2('/APInvoice/APEditable', {
          apno: apno,
          txntype: txntype,
        })
      )[0];
      this.$fullscreenLoading();

      if (results['Type'] == 'L') {
        return { editable: false, header: `${this.pageTitle} Already Posted to GL` };
      }

      if (results['ContraEntry'] == '3') {
        return {
          editable: false,
          header: `Opening ${this.pageTitle}`,
        };
      }

      if (results['IsClose'] == '1') {
        return {
          editable: false,
          header: `${this.pageTitle} already Month End`,
        };
      }

      return { editable: true, header: '' };
    },

    clearAllData: function () {
      this.currentTabIndex = 0;

      this.selectedAPType = null;
      this.apInvNo = '';
      this.apInvDate = new Date();
      this.invoiceNo = '';
      this.invoiceDate = new Date();
      this.selectedPayeeType = null;
      this.selectedPayeeCode = null;
      this.selectedTerm = null;
      this.termInput = '';
      this.dueDate = new Date();
      this.remarks = '';
      this.selectedHeaderCurr = null;
      this.oldRate = '';
      this.headerExchRate = '1.0000';
      this.headerExchFactor = '1';
      this.totalAPInvAmount = '0.00';
      this.totalAPInvFAmount = '0.00';
      this.totalOutstanding = '0.00';
      this.totalFOutstanding = '0.00';
      this.multiCurr = false;
      this.createdBy = '';
      this.updatedBy = '';

      this.totalAmountJC = '0.00';
      this.totalFAmountJC = '0.00';
      this.totalTaxJC = '0.00';
      this.totalAmountDP = '0.00';
      this.totalFAmountDP = '0.00';
      this.totalTaxDP = '0.00';

      this.chargesDetailsList = [];
      this.selectedChargesDetails = [];
      this.deletedChargesDetails = [];
      this.directPostingDetailsList = [];
      this.selectedDPEntry = [];
      this.deletedDirectPosting = [];
      this.glEntryList = [];

      this.showInputCharges = false;
      this.showExpenses = false;
      this.showPO = false;
      this.showInputDirectPosting = false;

      this.expensesFilterOpen = false;
      this.filterExpensesByDate = true;
      this.filterExpenseFromDate = new Date();
      this.filterExpenseToDate = new Date();
      this.selectedExpenseCode = null;
      this.selectedExpenseVehicle = null;
      this.expenseJobNo = '';
      this.expenseCashVoucher = '';
      this.selectedExpensesRefNo = null;
      this.gstInclusive = false;

      this.consignmentList = this.oriConsignmentList;
      this.containerList = this.oriContainerList;

      this.oriExpensesListing = [];
      this.expensesListing = [];
      this.selectedExpenses = [];

      this.oriPOListing = [];
      this.poListing = [];
      this.selectedPO = [];
      this.oriJobChargesListing = [];
      this.jobChargesListing = [];
      this.selectedJobCharges = [];

      this.jobnoRowLimit = '3000';
      this.contnoRowLimit = '5000';
      this.consignmentRowLimit = '5000';

      this.showAttTable = true;
      this.attachmentsList = [];
      this.selectedAtt = [];

      this.clearAllAttData();
      this.clearAllChargesData();
      this.clearAllDPData();
    },

    clearAllMessages: function () {
      this.requiredError = [];
      this.successMessages = [];
      this.errorMessages = [];

      this.apTypeErr = '';
      this.apInvNoErr = '';
      this.apInvDateErr = '';
      this.invoiceNoErr = '';
      this.invoiceDateErr = '';
      this.payeeTypeErr = '';
      this.payeeCodeErr = '';
      this.termErr = '';
      this.dueDateErr = '';
      this.headerCurrErr = '';
      this.headerExchRateErr = '';
    },

    clearAllChargesData: function () {
      this.showInputCharges = false;
      this.showJobCharges = false;
      this.subtitleJC = '';
      this.buttonTitleJC = '';
      
      this.selectedJobNo = null;
      this.selectedJobNoFreeText = '';
      this.billedJob = false;
      this.selectedContainer = null;
      this.selectedContainerFreeText = '';
      this.selectedCharges = null;
      this.selectedConsignment = null;
      this.selectedConsignmentFreeText = '';
      this.selectedJCCostCode = null;
      this.smkNoJC = '';
      this.poNoJC = '';
      this.jobChargeRemarks = '';
      this.chargesAmtInputDisable = true;
      this.chargeExchgRateDisabled = true;
      this.chargesQty = '';
      this.selectedChargesUOM = null;
      this.uomText = '';
      this.selectedChargesCurrency = null;
      this.chargesExchangeRate = '';
      this.chargesFactor = '';
      this.chargesBuyFUPrice = '0.0000';
      this.chargesBuyUPrice = '0.0000';
      this.chargesBuyFTotalPrice = '0.00';
      this.chargesBuyTotalPrice = '0.00';
      this.chargesMUpFUPrice = '0.0000';
      this.chargesMUpUPrice = '0.0000';
      this.chargesMUpTotalPrice = '0.00';
      this.chargesSellFUPrice = '0.0000';
      this.chargesSellFTotalPrice = '0.00';
      this.chargesSellUPrice = '0.0000';
      this.chargesSellTotalPrice = '0.00';
      this.selectedChargesTaxCode = null;
      this.chargesTaxRate = '';
      this.chargesFTaxAmt = '0.00';
      this.chargesFUPriceInclTax = '0.0000';
      this.chargesFTotalPriceInclTax = '0.00';
      this.chargesTaxAmt = '0.00';
      this.chargesUPriceInclTax = '0.0000';
      this.chargesTotalPriceInclTax = '0.00';
      this.chargesRebate = '0.00';

      this.clearAllChargesMessages();
    },

    clearAllChargesMessages: function () {
      this.chargesErr = '';
      this.jobNoErr = '';
      this.containerErr = '';
      this.consignmentErr = '';
      this.smkNoJCErr = '';
      this.poNoJCErr = '';
      this.chargesQtyErr = '';
      this.chargesCurrencyErr = '';
      this.chargesExchangeRateErr = '';
      this.chargesBuyFUPriceErr = '';
      this.chargesBuyUPriceErr = '';
      this.chargesBuyFTotalPriceErr = '';
      this.chargesBuyTotalPriceErr = '';
      this.chargesMUpFUPriceErr = '';
      this.chargesMUpUPriceErr = '';
      this.chargesMUpTotalPriceErr = '';
      this.chargesSellFUPriceErr = '';
      this.chargesSellFTotalPriceErr = '';
      this.chargesSellUPriceErr = '';
      this.chargesSellTotalPriceErr = '';
      this.chargesFTaxAmtErr = '';
      this.chargesFUPriceInclTaxErr = '';
      this.chargesFTotalPriceInclTaxErr = '';
      this.chargesTaxAmtErr = '';
      this.chargesUPriceInclTaxErr = '';
      this.chargesTotalPriceInclTaxErr = '';
      this.chargesRebateErr = '';
    },

    clearAllDPData: function () {
      this.selectDrCrAcc = null;
      this.selectedTaxDrCrAcc = null;
      this.selectedDPCostCode = null;
      this.smkNoDP = '';
      this.poNoDP = '';
      this.selectedDPVehicleNo = null;
      this.directPostingRemarks = '';

      this.selectedDPCurrency = null;
      this.dpExchangeRate = '1.0000';
      this.dpFTotalPrice = '0.00';
      this.dpTotalPrice = '0.00';
      this.selectedDPTaxCode = null;
      this.dpTaxRate = '0';
      this.dpFTaxAmt = '0.00';
      this.dpFTotalPriceInclTax = '0.00';
      this.dpTaxAmt = '0.00';
      this.dpTotalPriceInclTax = '0.00';

      this.clearAllDPMessages();
    },

    clearAllDPMessages: function () {
      this.selectDrCrAccErr = '';
      this.selectedTaxDrCrAccErr = '';
      this.smkNoDPErr = '';
      this.poNoDPErr = '';
      this.dpCurrencyErr = '';
      this.dpExchangeRateErr = '';
      this.dpFTotalPriceErr = '';
      this.dpTotalPriceErr = '';
      this.dpFTaxAmtErr = '';
      this.dpFTotalPriceInclTaxErr = '';
      this.dpTaxAmtErr = '';
      this.dpTotalPriceInclTaxErr = '';
    },

    clearAllAttData: function () {
      this.documents = {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      };
      this.attachmentsRequiredErrorMessage = '';
    },

    refreshClicked: async function (content) {
      switch (content) {
        case 'APListing': {
          this.fromDateErrMsg = '';
          this.toDateErrMsg = '';
          this.fromInvDateErrMsg = '';
          this.toInvDateErrMsg = '';
          this.searchByErrMsg = '';
          this.searchCriteriaErr = '';
          this.searchValueErr = '';

          let error = false;
          if (this.filterAPListingByDate) {
            if (!this.apListingFromDate) {
              this.fromDateErrMsg = 'Enter from date!';
              error = true;
            }
            if (!this.apListingToDate) {
              this.toDateErrMsg = 'Enter to date!';
              error = true;
            } else if (this.apListingFromDate > this.apListingToDate) {
              this.toDateErrMsg = 'To date cannot be before From date!';
              error = true;
            }
          }

          if (this.filterAPListingByInvDate) {
            if (!this.apListingFromInvDate) {
              this.fromInvDateErrMsg = 'Enter from Invoice date!';
              error = true;
            }
            if (!this.apListingToInvDate) {
              this.toInvDateErrMsg = 'Enter to Invoice date!';
              error = true;
            } else if (this.apListingFromInvDate > this.apListingToInvDate) {
              this.toInvDateErrMsg = 'To Invoice date cannot be before From Invoice date!';
              error = true;
            }
          }

          if (this.filterAPListingBySearch) {
            if (!this.selectedSearchBy) {
              this.searchByErr = 'Select search by!';
              error = true;
            }

            if (!this.selectedSearchCriteria) {
              this.searchCriteriaErr = 'Select search criteria!';
              error = true;
            }

            if (!this.searchValue.trim()) {
              this.searchValueErr = 'Enter search value!';
              error = true;
            }
          }

          if (error) return;

          this.mainFilterIsOpen = false;
          const dataToFilter = {
            txntype: this.txnType, // this.pageType == 'Credit' ? '1' : '0',
            startdate: this.filterAPListingByDate ? this.apListingFromDate.formatString('yyyy-MM-dd') : '',
            enddate: this.filterAPListingByDate ? this.apListingToDate.formatString('yyyy-MM-dd') : '',
            startinvdate: this.filterAPListingByInvDate ? this.apListingFromInvDate.formatString('yyyy-MM-dd') : '',
            endinvdate: this.filterAPListingByInvDate ? this.apListingToInvDate.formatString('yyyy-MM-dd') : '',
            searchfield: this.filterAPListingBySearch ? this.selectedSearchBy['key'] : '',
            searchcriteria: this.filterAPListingBySearch ? this.selectedSearchCriteria['name'] : '',
            searchvalue: this.filterAPListingBySearch ? this.searchValue : '',
          };

          const filterItems = [];
          if (this.filterAPListingByDate) {
            filterItems.push(
              `date from ${this.apListingFromDate.formatString('yyyy-MM-dd')} to ${this.apListingToDate.formatString('yyyy-MM-dd')}`,
            );
          }

          if (this.filterAPListingByInvDate) {
            filterItems.push(
              `invoice date from ${this.apListingFromInvDate.formatString('yyyy-MM-dd')} to ${this.apListingToInvDate.formatString('yyyy-MM-dd')}`,
            );
          }

          if (this.filterAPListingBySearch) {
            filterItems.push(
              `${this.selectedSearchBy['Search By']} ${this.selectedSearchCriteria['name']} ${this.searchValue}`,
            );
          }

          this.filterText = `Filter by ${filterItems.join(', ')}`;

          this.$fullscreenLoading(`Getting ${this.pageTitle} Listing`);
          this.apListingTableLoading = true;
          this.apListing = [];
          this.apListing = await this.$apiCallV2('/APInvoice/GetAPInvoiceListing', dataToFilter);
          this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
          this.apListingTableLoading = false;
          this.$fullscreenLoading();
          break;
        }

        case 'Job': {
          if (this.filterJobByAsAt && !this.asAtDate) {
            this.asAtDateErr = 'Enter As At Date!';
            return;
          }
          this.jobChargesFilterOpen = false;
          let contNoArr = '';
          if (this.searchContNo.trim().length > 0) {
            let containers = this.searchContNo.trim().replaceAll(',', '').split('\n');
            containers = containers.filter((cont) => cont.trim() != '');
            contNoArr = containers.join(',');
          }
          const showAllPayeeType = this.selectedPayeeFilterJC && this.selectedPayeeFilterJC['keyValue'] === 'all';
          const dataToPost = {
            payeetype: showAllPayeeType ? '' : this.selectedPayeeType['Party Code'],
            payeecode: showAllPayeeType ? '' : this.selectedPayeeCode[this.payeeCodeKey],
            allpayeetype: showAllPayeeType ? '1' : '0',
            asat: this.filterJobByAsAt ? new Date(this.asAtDate).formatString('yyyy-MM-dd') : '',
            chargecode: this.selectedChargeCodeSearch ? this.selectedChargeCodeSearch['Expenses Code'] : '',
            containerno: contNoArr,
            jobno: this.selectedJobNoSearch ? this.selectedJobNoSearch['Job No'] : '',
            isapinv: '1',
          };

          this.$fullscreenLoading('Getting Job Charges');
          this.jobChargesTableBoxLoading = true;
          this.oriJobChargesListing = [];
          this.jobChargesListing = [];
          const joblist = await this.$apiCallV2('/APInvoice/GetPaymentJobChargesListing', dataToPost);
          this.oriJobChargesListing = joblist;
          this.jobChargesListing = this.updateJobChargesListing();
          this.jobChargesTableBoxLoading = false;
          this.$fullscreenLoading();
          break;
        }

        case 'Expenses': {
          this.expensesFilterOpen = false;
          const dataToPost = {
            expcode: this.selectedExpenseCode ? this.selectedExpenseCode['Expense Code'] : '',
            startdate: this.filterExpensesByDate ? new Date(this.filterExpenseFromDate).formatString('yyyy-MM-dd') : '',
            enddate: this.filterExpensesByDate ? new Date(this.filterExpenseToDate).formatString('yyyy-MM-dd') : '',
            lorryno: this.selectedExpenseVehicle ? this.selectedExpenseVehicle['Lorry No'] : '',
            expdocrefno: this.selectedExpensesRefNo ? this.selectedExpensesRefNo['Ref No'] : '',
            jobno: this.expenseJobNo,
            cvdocno: this.expenseCashVoucher,
            chkinclgst: this.gstInclusive ? '1' : '0',
          };

          this.$fullscreenLoading('Getting Expenses');
          this.expensesTableLoading = true;
          this.oriExpensesListing = [];
          const explist = await this.$apiCallV2('/APInvoice/GetExpensesListing', dataToPost);
          this.oriExpensesListing = explist;
          this.expensesTableLoading = false;
          this.$fullscreenLoading();
          this.expensesListing =
            this.oriExpensesListing.length > 0 ?
              this.filterExpensesListing(this.oriExpensesListing)
            : [...this.oriExpensesListing];
          break;
        }

        case 'PO': {
          this.poFilterOpen = false;
          const showAllPayee = this.selectedPayeeFilterPO && this.selectedPayeeFilterPO['keyValue'] === 'all';
          const dataToPost = {
            payeecode: this.selectedPayeeCode ? this.selectedPayeeCode[this.payeeCodeKey] : '',
            showallpayee: showAllPayee ? '1' : '0',
            asat: this.filterPOByAsAt ? new Date(this.poAsAtDate).formatString('yyyy-MM-dd') : '',
            pono: this.filterPONo.trim(),
          };

          this.$fullscreenLoading('Getting PO');
          this.poTableBoxLoading = true;
          this.oriPOListing = [];
          this.oriPOListing = await this.$apiCallV2('/APInvoice/GetApPOListing', dataToPost);
          this.poTableBoxLoading = false;
          this.$fullscreenLoading();
          this.poListing =
            this.oriPOListing.length > 0 ? this.filterPOListing(this.oriPOListing) : [...this.oriPOListing];
          break;
        }
      }
    },

    getPayeeCodeList: async function (payeeType) {
      const payeeCodes =
        payeeType ?
          await this.$apiCallV2('/Master/GetDetailsMaster', {
            masterfile: 'payeecode',
            recordid: payeeType['Table'],
          })
        : [];
      if (payeeCodes.length > 0) {
        const codeKeys = Object.keys(payeeCodes[0]);
        this.payeeCodeKey = codeKeys[0];
        this.payeeCodeDescKey = codeKeys[1];

        this.payeeCodeList = payeeCodes.map((payee) => {
          return {
            ...payee,
            'Code - Name': `${payee[codeKeys[0]]} - ${payee[codeKeys[1]]}`,
          };
        });
      }
    },

    getAPINForAPCN: async function (payeetype, payeecode) {
      this.$fullscreenLoading('Getting AP Invoice List');
      const apin = await this.$apiCallV2('/APInvoice/GetAPCnAPList', {
        payeetype: payeetype,
        payeecode: payeecode,
      });
      this.$nextTick(() => {
        this.apinList = apin;
      });
      this.$fullscreenLoading();
    },

    getAPInvoice: async function (title) {
      this.popupTitle = title;
      const apinvno = this.selectedAPListing[0][`${this.pageTitle} No`];
      await this.populateAPInvoiceDetails(apinvno);
    },

    populateAPInvoiceDetails: async function (apinvno) {
      this.$fullscreenLoading('Getting data');
      this.apDetails = [];
      const results = await this.$apiCallV2('/APInvoice/GetAPInvoiceDetails', {
        apinvno: apinvno,
        txntype: this.txnType, // this.pageType == 'Credit' ? '1' : '0',
      });
      this.apDetails = results['AP']['APHeader'];

      this.createdBy =
        this.apDetails['CreatedBy'] ?
          `${this.apDetails['CreatedBy']} ${new Date(this.apDetails['DateCreated']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.updatedBy =
        this.apDetails['UpdatedBy'] ?
          `${this.apDetails['UpdatedBy']} ${new Date(this.apDetails['DateUpdated']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.selectedAPType = this.apTypeList.find((a) => a['JobTypeID'] == (this.apDetails['JobConfig'] || '0'));
      await this.apTypeSelected(this.selectedAPType);
      this.apInvNo = this.apDetails['APInvNo'] || '';
      this.apInvDate = this.apDetails['APInvDate'] ? new Date(this.apDetails['APInvDate']) : new Date();
      this.invoiceNo = this.apDetails['InvNo'] || '';
      this.invoiceDate = this.apDetails['InvDate'] ? new Date(this.apDetails['InvDate']) : new Date();
      this.selectedPayeeType =
        this.apDetails['PayeeType'] ?
          this.payeeTypeList.find((payee) => payee['Party Code'] == this.apDetails['PayeeType'])
        : null;
      await this.getPayeeCodeList(this.selectedPayeeType);
      this.selectedPayeeCode =
        this.apDetails['PayeeCode'] ?
          this.payeeCodeList.find((payee) => payee[this.payeeCodeKey] == this.apDetails['PayeeCode'])
        : null;
      await this.payeeCodeSelected(this.selectedPayeeCode);
      this.selectedTerm = this.termList.find((t) => t['term'] == this.apDetails['Term']);
      if (!this.selectedTerm) {
        this.termInput = this.apDetails['Term'].toString() || '';
      }
      this.dueDate =
        this.apDetails['DueDate'] ?
          new Date(this.apDetails['DueDate'])
        : this.calculateDueDate(
            this.selectedTerm ? this.selectedTerm['term'] : this.termInput,
            new Date(this.invoiceDate),
          );
      this.selectedHeaderCurr = this.currencyList.find(
        (c) => c['Currency Code'] == (this.apDetails['currency'] || 'MYR'),
      );
      this.headerExchRate = this.apDetails['rate'] ?? this.selectedHeaderCurr['Currency Rate'];
      this.headerExchFactor = this.selectedHeaderCurr['Factor'].toString();
      this.remarks = this.apDetails['Remark'] || '';
      this.totalAPInvAmount = this.apDetails['Total'] ? parseFloat(this.apDetails['Total']).toFixed(2) : '0.00';
      this.totalAPInvFAmount = this.apDetails['FTotal'] ? parseFloat(this.apDetails['FTotal']).toFixed(2) : '0.00';

      const tempCharges = this.apDetails['JobCharges'] || [];
      this.chargesDetailsList = this.updateJCIndex(tempCharges);
      this.updateChargesTotal(this.chargesDetailsList);

      const tempDP = this.apDetails['DirectPosting'] || [];
      this.directPostingDetailsList = this.updateDPIndex(tempDP);
      this.updateDPTotal(this.directPostingDetailsList);
      this.glEntryList = this.apDetails['GL Entry'] || [];
      this.attachmentsList = this.apDetails['Attachments'] || [];

      this.totalOutstanding =
        this.apDetails['Outstanding'] ? parseFloat(this.apDetails['Outstanding']).toFixed(2) : '0.00';
      this.totalFOutstanding =
        this.apDetails['FOutstanding'] ? parseFloat(this.apDetails['FOutstanding']).toFixed(2) : '0.00';

      this.$fullscreenLoading();
    },

    populateChargesDetails: function (data) {
      this.selectedCharges =
        data['Charges Code'].trim() ?
          this.expenseCodeList.find((c) => c['Expenses Code'] == data['Charges Code'])
        : null;
      this.selectedJobNo = data['Job No'].trim() ? this.jobNoList.find((j) => j['Job No'] == data['Job No']) : null;
      this.selectedJobNoFreeText = data['Job No'] ? data['Job No'] : '';
      this.selectedContainer =
        data['Cont No'].trim() ? this.containerList.find((c) => c['Cont No'] == data['Cont No']) : null;
      this.selectedContainerFreeText = data['Cont No'] ? data['Cont No'] : '';
      this.selectedConsignment =
        data['Consignment No'].trim() ?
          this.consignmentList.find((c) => c['Consignment No'] == data['Consignment No'])
        : null;
      this.selectedConsignmentFreeText = data['Consignment No'] ? data['Consignment No'] : '';
      this.selectedJCCostCode =
        data['Cost Code'].trim() ? this.costCodeList.find((c) => c['Cost Code'] == data['Cost Code']) : null;
      this.smkNoJC = data['SMK No'] || '';
      this.poNoJC = data['PO No'] || '';
      this.jobChargeRemarks = data['Remarks'] || '';

      if (this.txnType != '0') {
        this.selectedAPIN = this.apinList.find((ap) => ap['AP Invoice No'] == data['AP Invoice No']);
      }

      this.chargesQty = data['Qty'] ? Number(data['Qty']).toString() : '';
      this.uomText = data['UOM'] ?? '';
      this.selectedChargesUOM = data['UOM'].trim() ? this.uomList.find((u) => u['UOM Code'] == data['UOM']) : null;
      this.selectedChargesCurrency =
        data['Currency'].trim() ? this.currencyList.find((c) => c['Currency Code'] == data['Currency']) : null;
      this.chargesExchangeRate = data['Rate'] ? Number(data['Rate']).toFixed(4) : '';

      this.chargesBuyFUPrice = parseFloat(data['F.Buying Unit Price'] || '0.0000').toFixed(4);
      this.chargesBuyFTotalPrice = parseFloat(data['F.Buying Amount'] || '0.00').toFixed(2);
      this.chargesBuyUPrice = parseFloat(data['Buying Unit Price'] || '0.0000').toFixed(4);
      this.chargesBuyTotalPrice = parseFloat(data['Buying Amount'] || '0.00').toFixed(2);

      this.chargesMUpFUPrice = parseFloat(data['F.Mark Up Unit Price'] || '0.0000').toFixed(4);
      this.chargesMUpFTotalPrice = parseFloat(data['F.Mark Up Amount'] || '0.00').toFixed(2);
      this.chargesMUpUPrice = parseFloat(data['Mark Up Unit Price'] || '0.0000').toFixed(4);
      this.chargesMUpTotalPrice = parseFloat(data['Mark Up Amount'] || '0.00').toFixed(2);

      this.chargesSellFUPrice = parseFloat(data['F.Selling Unit Price'] || '0.0000').toFixed(4);
      this.chargesSellFTotalPrice = parseFloat(data['F.Selling Amount'] || '0.00').toFixed(2);
      this.chargesSellUPrice = parseFloat(data['Selling Unit Price'] || '0.0000').toFixed(4);
      this.chargesSellTotalPrice = parseFloat(data['Selling Amount'] || '0.00').toFixed(2);

      this.selectedChargesTaxCode =
        data['Tax Code'].trim() ? this.taxCodeListToUse.find((t) => t['Tax Code'] == data['Tax Code']) : null;
      this.chargesTaxRate = data['Tax Rate'] ? Number(data['Tax Rate']).toString() : '';
      this.chargesFTaxAmt = parseFloat(data['F.Tax Amount'] || '0.00').toFixed(2);
      this.chargesFUPriceInclTax = parseFloat(data['F.Buying Unit Price Incl Tax'] || '0.0000').toFixed(4);
      this.chargesFTotalPriceInclTax = parseFloat(data['F.Net Unit Price Incl Tax'] || '0.00').toFixed(2);
      this.chargesTaxAmt = parseFloat(data['Tax Amount'] || '0.00').toFixed(2);
      this.chargesUPriceInclTax = parseFloat(data['Net Unit Price Incl Tax'] || '0.0000').toFixed(4);
      this.chargesTotalPriceInclTax = parseFloat(data['Net Incl Tax'] || '0.00').toFixed(2);
      this.chargesRebate = parseFloat(data['Rebate Amount'] || '0.00').toFixed(2);
    },

    populateDirectPostingDetails: function (data) {
      this.selectDrCrAcc =
        data[`${this.txnType == '0' ? 'Debit' : 'Credit'} Ac`].trim() ?
          this.glAccList.find((a) => a['Acc No'] == data[`${this.txnType == '0' ? 'Debit' : 'Credit'} Ac`])
        : null;
      this.selectedTaxDrCrAcc =
        data[`Tax ${this.txnType == '0' ? 'Debit' : 'Credit'} Ac`].trim() ?
          this.glAccList.find((a) => a['Acc No'] == data[`Tax ${this.txnType == '0' ? 'Debit' : 'Credit'} Ac`])
        : null;
      this.selectedDPCostCode = this.costCodeList.find((c) => c['Cost Code'] == data['Cost Code']) || null;
      this.smkNoDP = data['SMK No'] || '';
      this.poNoDP = data['PO No'] || '';
      this.selectedDPVehicleNo =
        data['Vehicle No'].trim() ? this.vehicleNoList.find((v) => v['Lorry No'] == data['Vehicle No']) : null;
      this.directPostingRemarks = data['Remarks'] || '';

      this.selectedDPCurrency =
        data['Currency'].trim() ? this.currencyList.find((c) => c['Currency Code'] == data['Currency']) : null;
      this.dpExchangeRate = data['Rate'] ? Number(data['Rate']).toFixed(4) : '1.0000';
      this.dpFTotalPrice = parseFloat(data['F.Amount'] || '0.00').toFixed(2);
      this.dpTotalPrice = parseFloat(data['Amount'] || '0.00').toFixed(2);
      this.selectedDPTaxCode =
        data['Tax Code'].trim() ? this.taxCodeListToUse.find((t) => t['Tax Code'] == data['Tax Code']) : null;
      this.dpTaxRate = data['Tax Rate'] ? Number(data['Tax Rate']).toString() : '0';
      this.dpFTaxAmt = parseFloat(data['F.Tax Amount'] || '0.00').toFixed(2);
      this.dpFTotalPriceInclTax = parseFloat(data['F.Net Incl Tax'] || '0.00').toFixed(2);
      this.dpTaxAmt = parseFloat(data['Tax Amount'] || '0.00').toFixed(2);
      this.dpTotalPriceInclTax = parseFloat(data['Net Incl Tax'] || '0.00').toFixed(2);
    },

    calculateDueDate: function (term, date) {
      const termNum = parseInt(term);
      const oriDate = new Date(date);
      const duedate = oriDate.setDate(oriDate.getDate() + termNum);
      return new Date(duedate);
    },

    requiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showJobCharges && tabIndex != 0) {
        if (this.selectedJobCharges.length > 0) {
          // const discard = await this.promptDiscardJobCharges();
          const discard = await this.promptDiscard('Job Charges not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showJobCharges = false;
          this.selectedJobCharges = [];
        });
      }

      if (this.showInputCharges && tabIndex != 0 && this.action != '') {
        const discard = await this.promptDiscard('Charges details not added yet');

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputCharges = false;
          this.clearAllChargesData();
        });
      }

      if (this.showExpenses && tabIndex != 1) {
        if (this.selectedExpenses.length > 0) {
          const discard = await this.promptDiscard('Expenses not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.showPO && tabIndex != 1) {
        if (this.selectedPO.length > 0) {
          const discard = await this.promptDiscard('PO not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.showInputDirectPosting && tabIndex != 1 && this.action != '') {
        const discard = await this.promptDiscard('Direct Posting details not added yet');

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputDirectPosting = false;
          this.clearAllDPData();
        });
      }
      this.currentTabIndex = tabIndex;
    },

    promptDiscard: async function (headerMsg = 'Changes not saved') {
      return await this.$showAlert('warning', headerMsg, 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    checkInputFields: function () {
      let error = false;

      if (!this.selectedAPType) {
        this.apTypeErr = `${this.pageTitle} Type is required!`;
        this.requiredError.push({ key: 'aptype', value: this.apTypeErr });
      }

      if (!this.apInvDate) {
        this.apInvDateErr = `${this.pageTitle} Date is required!`;
        this.requiredError.push({ key: 'apinvdate', value: this.apInvDateErr });
        error = true;
      } else if (this.apInvDate < new Date('1900-01-01')) {
        this.apInvDateErr = `Invalid ${this.pageTitle} Date!`;
        this.requiredError.push({ key: 'apinvdate', value: this.apInvDateErr });
        error = true;
      } else if (this.apInvDate > new Date('2079-06-06')) {
        this.apInvDateErr = `Invalid ${this.pageTitle} Date!`;
        this.requiredError.push({ key: 'apinvdate', value: this.apInvDateErr });
        error = true;
      }

      if (this.invoiceNo.trim().length == 0) {
        this.invoiceNoErr = `${this.pageTitle.replace('AP ', '')} No is required!`;
        this.requiredError.push({ key: 'invoiceno', value: this.invoiceNoErr });
        error = true;
      }

      if (!this.invoiceDate) {
        this.invoiceDateErr = `${this.pageTitle.replace('AP ', '')} Date is required!`;
        this.requiredError.push({ key: 'invoicedate', value: this.invoiceDateErr });
        error = true;
      } else if (this.invoiceDate < new Date('1900-01-01')) {
        this.invoiceDateErr = `Invalid ${this.pageTitle.replace('AP ', '')} Date!`;
        this.requiredError.push({ key: 'invoicedate', value: this.invoiceDateErr });
        error = true;
      } else if (this.invoiceDate > new Date('2079-06-06')) {
        this.invoiceDateErr = `Invalid ${this.pageTitle.replace('AP ', '')} Date!`;
        this.requiredError.push({ key: 'invoicedate', value: this.invoiceDateErr });
        error = true;
      }

      if (!this.selectedPayeeType) {
        this.payeeTypeErr = 'Payee Type is required!';
        this.requiredError.push({ key: 'payeetype', value: this.payeeTypeErr });
        error = true;
      }

      if (!this.selectedPayeeCode) {
        this.payeeCodeErr = 'Payee Code is required!';
        this.requiredError.push({ key: 'payeecode', value: this.payeeCodeErr });
        error = true;
      }

      if (!this.selectedTerm && this.termInput.trim().length == 0) {
        this.termErr = 'Term is required!';
        this.requiredError.push({ key: 'term', value: this.termErr });
        error = true;
      }

      if (!this.dueDate) {
        this.dueDateErr = 'Due Date is required!';
        this.requiredError.push({ key: 'duedate', value: this.dueDateErr });
        error = true;
      } else if (this.dueDate < new Date('1900-01-01')) {
        this.dueDateErr = 'Invalid Due Date!';
        this.requiredError.push({ key: 'duedate', value: this.dueDateErr });
        error = true;
      } else if (this.dueDate > new Date('2079-06-06')) {
        this.dueDateErr = 'Invalid Due Date!';
        this.requiredError.push({ key: 'duedate', value: this.dueDateErr });
        error = true;
      }

      if (!this.selectedHeaderCurr) {
        this.headerCurrErr = 'Header Currency is required!';
        this.requiredError.push({ key: 'headercurr', value: this.headerCurrErr });
        error = true;
      }

      if (!this.headerExchRate.trim()) {
        this.headerExchRateErr = 'Exchange Rate is required!';
        this.requiredError.push({ key: 'headerrate', value: this.headerExchRateErr });
        error = true;
      } else if (this.headerExchRate && Number(this.headerExchRate) <= 0) {
        this.headerExchRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({ key: 'headerrate', value: this.headerExchRateErr });
        error = true;
      }

      return error;
    },

    saveClicked: async function () {
      this.errorMessages = [];
      this.requiredError = [];

      if (this.showJobCharges) {
        if (this.selectedJobCharges.length > 0) {
          // const discard = await this.promptDiscardJobCharges();
          const discard = await this.promptDiscard('Job Charges not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showJobCharges = false;
          this.selectedJobCharges = [];
        });
      }

      if (this.showInputCharges) {
        const discard = await this.promptDiscard('Charges details not added yet');

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputCharges = false;
          this.clearAllChargesData();
        });
      }

      if (this.showExpenses) {
        if (this.selectedExpenses.length > 0) {
          const discard = await this.promptDiscard('Expenses not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.showPO) {
        if (this.selectedPO.length > 0) {
          const discard = await this.promptDiscard('PO not added yet');
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.showInputDirectPosting) {
        const discard = await this.promptDiscard('Direct Posting details not added yet');

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputDirectPosting = false;
          this.clearAllDPData();
        });
      }
      if (this.checkInputFields()) return;

      const checkTotal =
        this.selectedHeaderCurr['Currency Code'] == 'MYR' ?
          Number(this.totalAPInvAmount)
        : Number(this.totalAPInvFAmount);
      if (Number(checkTotal) < 0) {
        this.$showAlert('error', 'Total Amount Invalid', 'Total amount cannot be negative!');
        return;
      } else if (checkTotal == 0) {
        const confirmsave = await this.$showAlert(
          'warning',
          `Total ${this.pageTitle} Amount is Zero`,
          'Do you want to continue?',
          [
            { text: 'Yes', color: 'green', textColor: 'white' },
            { text: 'No', color: 'red', textColor: 'white' },
          ],
        );
        if (confirmsave == 'No') return;
      }

      const allCharges = this.chargesDetailsList.concat(this.deletedChargesDetails);
      let chargesToSave = allCharges.map((chrg) => {
        return {
          action: chrg['action'] || '',
          jobno: chrg['Job No'] || '',
          contno: chrg['Cont No'] || '',
          chargescode: chrg['Charges Code'] || '',
          consignmentno: chrg['Consignment No'] || '',
          currency: chrg['Currency'] || '',
          exchrate: chrg['Rate'] || '',
          remarks: chrg['Remarks'] || '',
          costcode: chrg['Cost Code'] || '',
          smkno: chrg['SMK No'] || '',
          pono: chrg['PO No'] || '',
          qty: chrg['Qty'] || '',
          uom: chrg['UOM'] || '',
          unitprice: chrg['Buying Unit Price'] || '0.0000',
          funitprice: chrg['F.Buying Unit Price'] || '0.0000',
          amount: chrg['Buying Amount'] || '0.00',
          famount: chrg['F.Buying Amount'] || '0.00',
          markupamount: chrg['Mark Up Amount'] || '0.00',
          fmarkupamount: chrg['F.Mark Up Amount'] || '0.00',
          sellingamount: chrg['Selling Amount'] || '0.00',
          fsellingamount: chrg['F.Selling Amount'] || '0.00',
          taxcode: chrg['Tax Code'] || '',
          taxrate: chrg['Tax Rate'] || '0',
          taxableamt: chrg['Tax Amount'] || '0.00',
          ftaxableamt: chrg['F.Tax Amount'] || '0.00',
          netincltax: chrg['Net Incl Tax'] || '0.00',
          fnetincltax: chrg['F.Net Incl Tax'] || '0.00',
          apinvno: this.txnType != '0' && this.selectedAPIN ? this.selectedAPIN['AP Invoice No'] : '', // only applicable for APCN & APDN
          rebateamt: chrg['Rebate Amount'] || '0.00',
          txnguid: chrg['action'] == 'N' ? '' : chrg['TxnID'],
          jobchargestxnid: chrg['JobChargesTxnID'] || '',
          canceloption: chrg['canceloption'] || '0',
        };
      });
      chargesToSave = chargesToSave.filter((chrg) => chrg['action'] != '');

      const allDP = this.directPostingDetailsList.concat(this.deletedDirectPosting);
      let dpToSave = allDP.map((dp) => {
        return {
          action: dp['action'] || '',
          seqno: dp['Seq No'].toString() || '',
          debitac: dp['Debit Ac'] || '',
          creditac: dp['Credit Ac'] || '',
          amount: dp['Amount'] || '0.00',
          famount: dp['F.Amount'] || '0.00',
          netincltax: dp['Net Incl Tax'] || '0.00',
          fnetincltax: dp['F.Net Incl Tax'] || '0.00',
          currency: dp['Currency'] || '',
          exchrate: dp['Rate'] || '',
          taxdebitac: dp['Tax Debit Ac'] || '',
          taxcreditac: dp['Tax Credit Ac'] || '',
          taxcode: dp['Tax Code'] || '',
          taxrate: dp['Tax Rate'] || '0',
          taxamount: dp['Tax Amount'] || '0.00',
          ftaxamount: dp['F.Tax Amount'] || '0.00',
          remarks: dp['Remarks'] || '',
          costcode: dp['Cost Code'] || '',
          vehicleno: dp['Vehicle No'] || '',
          smkno: dp['SMK No'] || '',
          pono: dp['PO No'] || '',
          expenseguid: dp['ExpenseGUID'] || '',
        };
      });
      dpToSave = dpToSave.filter((dp) => dp['action'] != '');

      const allDocs = this.deletedAttList.concat(this.attachmentsList);
      let docsToSave = allDocs.map((doc) => {
                return {
          action: doc['Action'] ?? '',
          doccode: 'ATT',
          docrefno: '',
          remarks: doc['Doc Remark'],
          status: '',
          docname: doc['File Name'],
          docimage: doc['Doc Image'],
          docimagetype: doc['File Type'],
          docguid: this.action == 'N' ? '' : doc['Document GUID'],
        };
      });
      docsToSave = docsToSave.filter((doc) => doc['action'] != '');

      const allDetails = {
        action: this.action,
        apinvtype: this.selectedAPType ? this.selectedAPType['JobTypeID'] : '',
        apinvno: this.action == 'N' ? '' : this.apInvNo,
        txntype: this.txnType, // this.pageType == 'Credit' ? '1' : '0', // 0-apinv; 1-apcn; 3-apdn
        apinvdate: this.apInvDate ? new Date(this.apInvDate).formatString('yyyy-MM-dd') : '',
        invno: this.invoiceNo.trim() || '',
        invdate: this.invoiceDate ? new Date(this.invoiceDate).formatString('yyyy-MM-dd') : '',
        payeetype: this.selectedPayeeType ? this.selectedPayeeType['Party Code'] : '',
        payeecode: this.selectedPayeeCode ? this.selectedPayeeCode[this.payeeCodeKey] : '',
        term: this.selectedTerm ? this.selectedTerm['term'] : this.termInput.trim() || '',
        duedate: this.dueDate ? new Date(this.dueDate).formatString('yyyy-MM-dd') : '',
        totalap: this.totalAPInvAmount || '0.00',
        ftotalap: this.totalAPInvFAmount || '0.00',
        currency: this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : '',
        exchrate: this.headerExchRate || '1.0000',
        remark: this.remarks || '',
        jobcharges: chargesToSave || [],
        directposting: dpToSave || [],
        attachments: docsToSave || [],
      };

      this.$fullscreenLoading(`Saving ${this.pageTitle}`);
      const result = await this.$apiCallV2('/APInvoice/SaveAPInvoice', allDetails);
      this.$fullscreenLoading();

      if (result[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating ${this.pageTitle}: ${result[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing ${this.pageTitle}: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      this.showJobChargesTable = false;
      this.showDirectPostingTable = false;
      this.showAttTable = false;
      this.clearAllMessages();
      const apno = result[0]['APInv'];
      this.$nextTick(async () => {
        await this.populateAPInvoiceDetails(apno);
        this.updateSummaryItems();
        this.showJobChargesTable = true;
        this.showDirectPostingTable = true;
        this.showAttTable = true;
        await this.refreshClicked('APListing');
      });

      if (this.action == 'N') {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      } else {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      }

      this.viewMode = true;
      this.popupTitle = `View ${this.pageTitle}`;
      this.action = '';
    },

    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete': {
          // Check if delete remarks is empty
          if (this.deleteAPRemarks.trim().length == 0) {
            this.deleteAPRemarksErr = 'Please enter delete remarks!';
            return;
          }

          const cancelOption = await this.$showAlert(
            'warning',
            'Cancel Job Charges',
            `Do you want to cancel Job Charges keyed with this ${this.pageTitle}?`,
            [{ text: 'Yes', color: 'Red', textColor: 'white' }, { text: 'No' }],
          );

          this.$fullscreenLoading(`Deleting Selected ${this.pageTitle}`);
          const deleteBody = {
            action: this.action,
            apinvno: this.selectedAPListing[0][`${this.pageTitle} No`],
            reason: this.deleteAPRemarks || '',
            canceloption: cancelOption == 'Yes' ? '1' : '0',
            txntype: this.txnType, // this.pageType == 'Credit' ? '1' : '0',
          };

          const deleteResult = await this.$apiCallV2('/APInvoice/SaveAPInvoice', deleteBody);
          this.$fullscreenLoading();
          this.showDeleteAP = false;

          if (deleteResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `${this.pageTitle} ${this.selectedAPListing[0][`${this.pageTitle} No`]} was successfully deleted.`,
            );
            this.apListing = [];
            this.selectedAPListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked('APListing');
            });
          } else {
            this.$showAlert('error', 'Delete Unsuccessful', `${deleteResult[0]['ErrMsg']}`);
            this.apListing = [];
            this.selectedAPListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked('APListing');
            });
          }
          break;
        }

        case 'Cancel': {
          this.deleteAPRemarks = '';
          this.showDeleteAP = false;
          break;
        }
      }
    },

    checkPayeeTypeCodeSelected: function () {
      const notSelectedVal = [];
      if (!this.selectedPayeeType) {
        notSelectedVal.push('Payee Type');
      }
      if (!this.selectedPayeeCode) {
        notSelectedVal.push('Payee Code');
      }
      const selects = notSelectedVal.join(' & ');
      if (notSelectedVal.length > 0) {
        const warningMsg = `Please Select ${selects} First!`;
        this.$showAlert('error', `${selects} Not Selected`, warningMsg);
        return false;
      }
      return true;
    },

    updateAPTotal: function () {
      const totalMYRAmt =
        Number(this.totalAmountJC.replaceAll(',', '')) + Number(this.totalAmountDP.replaceAll(',', ''));
      const totalFAmt =
        Number(this.totalFAmountJC.replaceAll(',', '')) + Number(this.totalFAmountDP.replaceAll(',', ''));

      // this.totalAPInvAmount = this.$currencyFormat(`${totalMYRAmt}`);
      // this.totalAPInvFAmount = this.$currencyFormat(`${totalFAmt}`);

      this.totalAPInvAmount = totalMYRAmt.toFixed(2);
      this.totalAPInvFAmount = totalFAmt.toFixed(2);

      this.totalOutstanding = this.totalAPInvAmount;
      this.totalFOutstanding = this.totalAPInvFAmount;

      if (this.chargesDetailsList.length > 0 || this.directPostingDetailsList.length > 0) {
        this.multiCurr = !(
          this.chargesDetailsList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code']) &&
          this.directPostingDetailsList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code'])
        );
      }
    },

    //#region Job Charges
    openJobCharges: function () {
      const discard = this.checkPayeeTypeCodeSelected();
      if (discard) {
        this.showJobCharges = true;
        this.showInputCharges = false;
      }
    },

    openChargesInput: function () {
      this.requiredError = [];
      this.headerExchRateErr = '';

      if (Number(this.headerExchRate) == 0) {
        this.headerExchRateErr = 'Exchange Rate is Invalid!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        return;
      }

      this.clearAllChargesData();
      this.selectedChargesDetails = [];
      this.selectedChargesCurrency = this.currencyList.find(
        (curr) =>
          curr['Currency Code'] === (this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : 'MYR'),
      );
      this.chargesCurrSelected(this.selectedChargesCurrency);
      this.chargesExchangeRate = Number(this.headerExchRate).toFixed(4);
      this.chargesFactor = Number(this.headerExchFactor);

      this.showInputCharges = true;
      this.showJobCharges = false;
      this.saveJCAction = 'N';
      this.subtitleJC = 'New Job Charges';
      this.buttonTitleJC = 'Add Charges';
    },

    deleteJobCharges: async function () {
      if (this.selectedChargesDetails.length == 0) {
        this.$showAlert('error', 'No Charges Selected', 'Please select at least one Charge to delete.');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Charges Record',
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let temp = this.chargesDetailsList;
      for (const entry of this.selectedChargesDetails) {
        if (entry['RecordSource'] && entry['RecordSource'] == '1') {
          if (entry['Billed'] == '1') {
            await this.$showAlert(
              'warning',
              'This is a Billed Charge',
              'You are not allowed to delete a billed charges.',
            );
            continue;
          }

          const cancelOp = await this.$showAlert(
            'warning',
            'Cancel Job Charges',
            `Do you want to cancel this Job Charges?`,
            [{ text: 'Yes', color: 'Red', textColor: 'white' }, { text: 'No' }],
          );
          entry['canceloption'] = cancelOp == 'Yes' ? '1' : '0';
        }

        if (entry['action'] && entry['action'] == 'N') {
          temp = temp.filter((c) => c['Seq No'] != entry['Seq No']);
        } else {
          this.chargesDetailsList.forEach((chrg) => {
            if (chrg['TxnID'] == entry['TxnID'] && chrg['Seq No'] == entry['Seq No']) {
              chrg['action'] = 'D';
              chrg['canceloption'] = entry['canceloption'] || '';
              this.deletedChargesDetails.push(chrg);
            }
          });
          temp = temp.filter((j) => j['action'] != 'D');
        }
      }

      this.chargesDetailsList = [];
      this.showJobChargesTable = false;
      this.$nextTick(() => {
        this.chargesDetailsList = this.updateJCIndex(temp);
        this.selectedChargesDetails = [];
        this.updateChargesTotal(temp);
        this.clearAllChargesData();
        this.jobChargesListing = this.updateJobChargesListing();
        this.showJobChargesTable = true;
      });
    },

    jobChargesTableButtonClicked: function (_btn, data) {
      this.showInputCharges = true;
      this.billedJob = data['Billed'] == '1';
      this.selectedChargesDetails = [data];
      this.saveJCAction = data['action'] && data['action'] == 'N' ? 'N' : 'E';
      this.subtitleJC = 'Edit Job Charges';
      this.buttonTitleJC = 'Edit Charges';
      this.populateChargesDetails(data);
    },

    selectDeselectJCClicked: function () {
      if (this.selectedJobCharges.length != this.jobChargesListing.length) {
        this.selectedJobCharges = [];
        this.selectedJobCharges = [...this.jobChargesListing];
      } else {
        this.selectedJobCharges = [];
      }
    },

    addSelectedJobCharges: async function () {
      if (this.selectedJobCharges.length == 0) {
        this.$showAlert('error', 'No Job Charges Selected', 'Please select at least one Job Charge to add. ');
        return;
      }

      const temp = this.chargesDetailsList;
      for (const jc of this.selectedJobCharges) {
        const proceed = await this.checkJobStatus(jc['Job No']);
        if (!proceed) return;

        const allCharges = temp.concat(this.deletedChargesDetails);
        const highestSeq =
          allCharges
            .map((d) => Number(d['Seq No']))
            .sort(function(a, b) { return a - b; })
            .reverse()[0] ?? 0;

        const dataToAdd = {
          '#': '',
          action: 'N',
          APInvNo: '',
          'Seq No': highestSeq + 1,
          'Charges Code': jc['Charges Code'] || '',
          'Job No': jc['Job No'] || '',
          'Job Charges Code': jc['Charges Code'] || '',
          'Cont No': jc['Cont No'] || '',
          'Consignment No': jc['CN No'] || '',
          'PO No': '',
          'Cost Code': jc['Cost Code'] || '',
          'SMK No': '',
          UOM: jc['UOM'] || '',
          Currency: jc['Currency'] || '',
          Rate: jc['Rate'] || '1.0000',
          Qty: jc['Qty'] || '0',
          'Buying Unit Price': Number(jc['Unit Price'] || 0).toFixed(4),
          'Buying Amount': Number(jc['Amount'] || 0).toFixed(2),
          'Mark Up Unit Price': (Number(jc['Adj Amt']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'Mark Up Amount': Number(jc['Adj Amt'] || 0).toFixed(2),
          'Selling Unit Price': (Number(jc['Net Amt']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'Selling Amount': Number(jc['Net Amt'] || 0).toFixed(2),
          'F.Buying Unit Price': Number(jc['F.Unit Price'] || 0).toFixed(4),
          'F.Buying Amount': Number(jc['F.Amount'] || 0).toFixed(2),
          'F.Mark Up Unit Price': (Number(jc['F.Adj Amt']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'F.Mark Up Amount': Number(jc['F.Adj Amt'] || 0).toFixed(2),
          'F.Selling Unit Price': (Number(jc['F.Net Amt']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'F.Selling Amount': Number(jc['F.Net Amt'] || 0).toFixed(2),
          'Tax Code': jc['Tax Code'] || '',
          'Tax Rate': Number(jc['Tax Rate'] || 0).toString(),
          'Tax Amount': Number(jc['Tax Amt'] || 0).toFixed(2),
          'Net Unit Price Incl Tax':
            (Number(jc['Unit Price']) + Number(jc['Tax Amt']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'Net Incl Tax': (Number(jc['Amount']) + Number(jc['Tax Amt'])).toFixed(2) || '0.00',
          'F.Tax Amount': Number(jc['F.Tax Amount'] || 0).toFixed(2),
          'F.Net Unit Price Incl Tax':
            (Number(jc['F.Unit Price']) + Number(jc['F.Tax Amount']) / Number(jc['Qty'])).toFixed(4) || '0.0000',
          'F.Net Incl Tax': (Number(jc['F.Amount']) + Number(jc['F.Tax Amount'])).toFixed(2) || '0.00',
          'Job Charges Amount': Number(jc['Amount']).toFixed(2),
          'Rebate Amount': Number(jc['Rebate'] || 0).toFixed(2),
          Remarks: jc['Remarks'] || '',
          UserID: '',
          'Date Updated': '',
          TxnID: `temp_${jc['Charges Code']}_${new Date().formatString('yyyyMMddHHmmss')}`, // temporary ID
          JobChargesTxnID: jc['TxnID'] || '',
          JobSeq: jc['Ref No'] || '',
          RecordSource: '0', // 0: Job charges/operation 1:APInvoice 2:Payment 3:ARInvoice
          Billed: jc['Pending Billing Charges'] == '0' ? '1' : '0',
        };
        temp.push(dataToAdd);
      }

      this.showJobCharges = false;
      this.showJobChargesTable = false;
      this.chargesDetailsList = [];
      this.$nextTick(() => {
        this.chargesDetailsList = this.updateJCIndex(temp);
        this.showJobChargesTable = true;
        this.jobChargesListing = this.updateJobChargesListing();
        this.updateChargesTotal(temp);
      });
    },

    cancelSelectJobCharges: function () {
      this.showJobCharges = false;
      this.selectedJobCharges = [];
    },

    checkChargesFields: function () {
      this.clearAllChargesMessages();
      let error = false;
      if (!this.selectedCharges) {
        this.chargesErr = 'Charges Code is required!';
        this.requiredError.push({ key: 'jccharges', value: this.chargesErr });
        error = true;
      }

      if (this.jobNoRequired && (!this.selectedJobNoFreeText || this.selectedJobNoFreeText.trim() == '')) {
        this.jobNoErr = 'Job No is required!';
        this.requiredError.push({ key: 'jcjobno', value: this.jobNoErr });
      }

      if (this.smkNoJC.trim().length > 35) {
        this.smkNoJCErr = 'SMK No cannot exceed 35 characters!';
        this.requiredError.push({ key: 'jcsmkno', value: this.smkNoJCErr });
        error = true;
      }

      if (this.poNoJC.trim().length > 25) {
        this.poNoJCErr = 'PO No cannot exceed 25 characters!';
        this.requiredError.push({
          key: 'jcpono',
          value: this.poNoJCErr,
        });
        error = true;
      }

      if (this.chargesQty.trim() == '') {
        this.chargesQtyErr = 'Quantity is required!';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQtyErr });
        error = true;
      } else if (Number(this.chargesQty) <= 0) {
        this.chargesQtyErr = 'Quantity is invalid!';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQtyErr });
        error = true;
      } else if (this.chargesQty.trim().length > 4) {
        this.chargesQtyErr = 'Quantity cannot exceed 4 characters';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQtyErr });
        error = true;
      }

      if (!this.selectedChargesCurrency) {
        this.chargesCurrencyErr = 'Currency is required!';
        this.requiredError.push({
          key: 'chargesCurrency',
          value: this.chargesCurrencyErr,
        });
        error = true;
      } else if (
        this.selectedChargesCurrency &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        this.selectedChargesCurrency['Currency Code'] != this.selectedHeaderCurr['Currency Code']
      ) {
        this.chargesCurrencyErr = 'Selected currency is different from header currency!';
        this.requiredError.push({
          key: 'chargesCurrency',
          value: this.chargesCurrencyErr,
        });
        error = true;
      }

      if (this.chargesExchangeRate.trim() == '') {
        this.chargesExchangeRateErr = 'Exchange Rate is required!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      } else if (Number(this.chargesExchangeRate) == 0) {
        this.chargesExchangeRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        Number(this.chargesExchangeRate) != Number(this.headerExchRate)
      ) {
        this.chargesExchangeRateErr = 'Selected exchange rate is different from header exchange rate!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      }

      if (this.chargesBuyFUPrice.trim() == '') {
        this.chargesBuyFUPriceErr = 'F.Buying Unit Price is required!';
        this.requiredError.push({
          key: 'jcbuyfuprice',
          value: this.chargesBuyFUPriceErr,
        });
        error = true;
      }

      if (this.chargesBuyFTotalPrice.trim() == '') {
        this.chargesBuyFTotalPriceErr = 'F.Buying Total Price is required!';
        this.requiredError.push({
          key: 'jcbuyftotprice',
          value: this.chargesBuyFTotalPriceErr,
        });
        error = true;
      }

      if (this.chargesBuyUPrice.trim() == '') {
        this.chargesBuyUPriceErr = 'Buying Unit Price is required!';
        this.requiredError.push({
          key: 'jcbuyuprice',
          value: this.chargesBuyUPriceErr,
        });
        error = true;
      }

      if (this.chargesBuyTotalPrice.trim() == '') {
        this.chargesBuyTotalPriceErr = 'Buying Total Price is required!';
        this.requiredError.push({
          key: 'jcbuytotprice',
          value: this.chargesBuyTotalPriceErr,
        });
        error = true;
      }

      if (this.chargesFTaxAmt.trim() == '') {
        this.chargesFTaxAmtErr = 'Tax Amt is required!';
        this.requiredError.push({
          key: 'jcftaxamt',
          value: this.chargesFTaxAmtErr,
        });
      }

      if (this.chargesTaxAmt.trim() == '') {
        this.chargesTaxAmtErr = 'Tax Amt is required!';
        this.requiredError.push({
          key: 'jctaxamt',
          value: this.chargesTaxAmtErr,
        });
      }

      if (this.chargesSellFUPrice.trim() == '') {
        this.chargesSellFUPriceErr = 'F.Selling Unit Price is required!';
        this.requiredError.push({
          key: 'jcsellfuprice',
          value: this.chargesSellFUPriceErr,
        });
        error = true;
      }

      if (this.chargesSellFTotalPrice.trim() == '') {
        this.chargesSellFTotalPriceErr = 'F.Selling Total Price is required!';
        this.requiredError.push({
          key: 'jcsellftotprice',
          value: this.chargesSellFTotalPriceErr,
        });
        error = true;
      }

      if (this.chargesSellUPrice.trim() == '') {
        this.chargesSellUPriceErr = 'Selling Unit Price is required!';
        this.requiredError.push({
          key: 'jcselluprice',
          value: this.chargesSellUPriceErr,
        });
        error = true;
      }

      if (this.chargesSellTotalPrice.trim() == '') {
        this.chargesSellTotalPriceErr = 'Selling Total Price is required!';
        this.requiredError.push({
          key: 'jcselltotprice',
          value: this.chargesSellTotalPriceErr,
        });
        error = true;
      }
      return error;
    },

    saveChargesClicked: async function () {
      if (this.checkChargesFields()) return;
      //checking on Job No, Cont No and Consignment No as they becomes free text
      let jobNoCheck = null;
      let contNoCheck = null;
      let cnNoCheck = null;
      let proceed = true;
      this.$fullscreenLoading('Validating...');
      // If job no selected, check if job charge issued
      if (this.selectedJobNoFreeText && this.selectedJobNoFreeText.trim() != '') {
        jobNoCheck = await this.$apiCallV2('/APInvoice/ValidateJobContCNNo', { type: 'JOB', jobno: this.selectedJobNoFreeText });
        if (!jobNoCheck || jobNoCheck.length == 0) {
          this.jobNoErr = 'Invalid Job No';
          proceed = false;
        }

        const existingAPIssued = await this.checkJobChargeIssued(
          this.selectedCharges['Expenses Code'],
          this.selectedJobNoFreeText,
          this.selectedContainerFreeText,
          'apin',
        );
        if (existingAPIssued[0]['Results'] == '1') {
          const confirmAdd = await this.$showAlert(
            'warning',
            existingAPIssued[0]['ErrMsg'],
            'Do you still want to add this item?',
            [
              { text: 'Yes', color: 'indianred', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmAdd == 'No') return;
        }

        const existingPaymentIssued = await this.checkJobChargeIssued(
          this.selectedCharges['Expenses Code'],
          this.selectedJobNoFreeText,
          this.selectedContainerFreeText,
          'payment',
        );
        if (existingPaymentIssued[0]['Results'] == '1') {
          const confirmAdd = await this.$showAlert(
            'warning',
            existingPaymentIssued[0]['ErrMsg'],
            'Do you still want to add this item?',
            [
              { text: 'Yes', color: 'indianred', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmAdd == 'No') return;
        }

        if (this.saveJCAction == 'N') {
          const existingJobChrg = await this.checkJobChargeIssued(
            this.selectedCharges['Expenses Code'],
            this.selectedJobNoFreeText,
            this.selectedContainerFreeText,
            'jobcharge',
          );
          if (existingJobChrg[0]['Results'] == '1') {
            const confirmAdd = await this.$showAlert(
              'warning',
              existingJobChrg[0]['ErrMsg'],
              'You can use "Select From Job Charges" to add this charge item. Do you still want to add this item?',
              [
                { text: 'Yes', color: 'indianred', textColor: 'white' },
                { text: 'No', color: 'lightgray', textColor: 'black' },
              ],
            );
            if (confirmAdd == 'No') return;
          }
        }
      }
      this.$fullscreenLoading('Validating...');
      if (this.selectedContainerFreeText && this.selectedContainerFreeText.trim() != '') {
        contNoCheck = await this.$apiCallV2('/APInvoice/ValidateJobContCNNo', { type: 'CONT', contno: this.selectedContainerFreeText, cnno: this.selectedConsignmentFreeText ?? '' });
        if (!contNoCheck || contNoCheck.length == 0) {
          this.containerErr = 'Invalid Container';
          proceed = false;
        }
      }

      if (this.selectedConsignmentFreeText && this.selectedConsignmentFreeText.trim() != '') {
        cnNoCheck = await this.$apiCallV2('/APInvoice/ValidateJobContCNNo', { type: 'CN', cnno: this.selectedConsignmentFreeText, jobno: this.selectedJobNoFreeText ?? '' });
        if (!cnNoCheck || cnNoCheck.length == 0) {
          this.consignmentErr = 'Invalid Consignment';
          proceed = false;
        }
      }
      
      this.$fullscreenLoading('');
      if (!proceed) { //if one of job, cont, cn failed then return
        return;
      }

      const temp = this.chargesDetailsList;
      const allCharges = temp.concat(this.deletedChargesDetails);
      const highestSeq =
        allCharges
          .map((d) => Number(d['Seq No']))
          .sort(function(a, b) { return a - b; })
          .reverse()[0] ?? 0;

      const dataToAdd = {
        '#': '',
        action:
          this.action == 'N' ? 'N'
          : this.selectedChargesDetails.length == 0 ? 'N'
          : this.saveJCAction,
        APInvNo: '',
        'Seq No': '',
        'Charges Code': this.selectedCharges ? this.selectedCharges['Expenses Code'] : '',
        'Job No': this.selectedJobNoFreeText ?? '',
        'Job Charges Code': '',
        'Cont No': this.selectedContainerFreeText ?? '',
        'Consignment No': this.selectedConsignmentFreeText ?? '',
        'PO No': this.poNoJC.trim() || '',
        'Cost Code': this.selectedJCCostCode ? this.selectedJCCostCode['Cost Code'] : '',
        'SMK No': this.smkNoJC.trim() || '',
        UOM: this.uomText,
        Currency: this.selectedChargesCurrency ? this.selectedChargesCurrency['Currency Code'] : 'MYR',
        Rate: this.chargesExchangeRate || '1.0000',
        Qty: this.chargesQty || '1',
        'Buying Unit Price': this.chargesBuyUPrice || '0.0000',
        'Buying Amount': this.chargesBuyTotalPrice || '0.00',
        'Mark Up Unit Price': this.chargesMUpUPrice || '0.0000',
        'Mark Up Amount': this.chargesMUpTotalPrice || '0.00',
        'Selling Unit Price': this.chargesSellUPrice || '0.0000',
        'Selling Amount': this.chargesSellTotalPrice || '0.00',
        'F.Buying Unit Price': this.chargesBuyFUPrice || '0.0000',
        'F.Buying Amount': this.chargesBuyFTotalPrice || '0.00',
        'F.Mark Up Unit Price': this.chargesMUpFUPrice || '0.0000',
        'F.Mark Up Amount': this.chargesMUpFTotalPrice || '0.00',
        'F.Selling Unit Price': this.chargesSellFUPrice || '0.0000',
        'F.Selling Amount': this.chargesSellFTotalPrice || '0.00',
        'Tax Code': this.selectedChargesTaxCode ? this.selectedChargesTaxCode['Tax Code'] : '',
        'Tax Rate': this.chargesTaxRate || '0',
        'Tax Amount': this.chargesTaxAmt || '0.00',
        'Net Unit Price Incl Tax': this.chargesUPriceInclTax || '0.0000',
        'Net Incl Tax': this.chargesTotalPriceInclTax || '0.00',
        'F.Tax Amount': this.chargesFTaxAmt || '0.00',
        'F.Net Unit Price Incl Tax': this.chargesFUPriceInclTax || '0.0000',
        'F.Net Incl Tax': this.chargesFTotalPriceInclTax || '0.00',
        'Job Charges Amount': '',
        'Rebate Amount': this.chargesRebate || '0.00',
        Remarks: this.jobChargeRemarks || '',
        UserID: '',
        'Date Updated': '',
        TxnID: `temp_${this.selectedCharges['Expenses Code']}_${new Date().formatString('yyyyMMddHHmmss')}`, // temporary ID
        JobChargesTxnID: '',
        JobSeq: '',
        RecordSource: '', // 0: Job charges/operation 1:APInvoice 2:Payment 3:ARInvoice
        Billed: this.billedJob ? '1' : '0',
      };

      if (this.selectedChargesDetails.length > 0) {
        dataToAdd['Seq No'] = this.selectedChargesDetails[0]['Seq No'];
        dataToAdd['JobChargesTxnID'] = this.selectedChargesDetails[0]['JobChargesTxnID'];
        dataToAdd['TxnID'] = this.selectedChargesDetails[0]['TxnID'];
        dataToAdd['RecordSource'] = this.selectedChargesDetails[0]['RecordSource'];
        temp.splice(Number(this.selectedChargesDetails[0]['#']) - 1, 1, dataToAdd);
      } else {
        dataToAdd['Seq No'] = highestSeq + 1;
        // dataToAdd['RecordSource'] = '';
        temp.push(dataToAdd);
      }

      this.showInputCharges = false;
      this.showJobChargesTable = false;
      this.$nextTick(() => {
        this.chargesDetailsList = this.updateJCIndex(temp);
        this.clearAllChargesData();
        this.requiredError = [];
        this.selectedChargesDetails = [];
        this.saveJCAction = '';
        this.jobChargesListing = this.updateJobChargesListing();
        this.updateChargesTotal(temp);
        this.showJobChargesTable = true;
      });
    },

    cancelChargesClicked: function () {
      this.jobChargesFilterOpen = false;
      this.showInputCharges = false;
      this.requiredError = [];
      this.clearAllChargesData();
    },

    updateJCIndex: function (detailList) {
      const orderedList = detailList.map((n, index) => ({
        ...n,
        '#': index + 1,
      }));
      return orderedList;
    },

    updateJobChargesListing: function () {
      const saved = [];
      this.chargesDetailsList.forEach((j) => {
        if (j['JobChargesTxnID'] && j['JobChargesTxnID'].trim() != '') saved.push(j['JobChargesTxnID']);
      });
      const filtered = this.oriJobChargesListing.filter((inv) => !saved.includes(inv['TxnID']));

      // Add status indicators
      const withStatus = filtered.map((j) => ({
        Billed:
          j['Pending Billing Charges'] == 0 ?
            `<div height="15" width="15" style="background-color: green; padding: 5px; border-radius:5px; color: white; pointer-events: none">Billed</div>`
          : `<div height="15" width="15" style="background-color: orange; padding: 5px; border-radius:5px; color: white ; pointer-events: none">Pending</div>`,
        MTR:
          j['MTR Job Charges'] == 1 ?
            `<div height="15" width="15" style="background-color: red; padding: 5px; border-radius:5px; color: white; pointer-events: none">MTR</div>`
          : '',
        ...j,
      }));

      return withStatus;
    },

    checkJobStatus: async function (jobno) {
      const jobstatus = await this.$apiCallV2('/APInvoice/CheckJobStatus', {
        jobno: jobno,
      });
      if (jobstatus[0]['Indicator'] == 'A') {
        const confirmProceed = await this.$showAlert('warning', `Job ${jobno} is complete`, `Proceed?`, [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ]);
        if (confirmProceed == 'No') {
          return false;
        }
        return true;
      } else if (jobstatus[0]['Indicator'] == 'O') {
        if (this.settingsJobClosePswd == '') {
          await this.$showAlert('error', `Job ${jobno} is complete`, 'You do not have the permission to use this Job!');
          return false;
        }
        const pswd = await this.showPasswordInput();
        if (pswd !== this.settingsJobClosePswd) {
          await this.$showAlert('error', `Incorrect Password`, '');
          return false;
        }
        return true;
      }
      return true;
    },

    checkJobChargeIssued: async function (charge, job, cont, checktype) {
      this.$fullscreenLoading('Checking Job Charge');
      const dataToPost = {
        chargecode: charge ?? '',
        jobno: job ?? '',
        contno: cont ?? '',
        checktype: checktype ?? '',
        apinvno: this.action == 'N' ? '' : this.apInvNo,
      };

      const jobChrgIssued = await this.$apiCallV2('/APInvoice/CheckAPJobChargesIssued', dataToPost);
      this.$fullscreenLoading();
      return jobChrgIssued;
    },

    updateChargesTotal: function (chargesList) {
      let totalTax = 0;
      let totalMYRAmt = 0;
      let totalFAmt = 0;
      chargesList.forEach((chrg) => {
        totalTax += Number(chrg['Tax Amount']);
        totalMYRAmt += Number(chrg['Net Incl Tax']);
        totalFAmt += Number(chrg['F.Net Incl Tax']);
      });

      this.totalTaxJC = totalTax.toFixed(2);
      this.totalAmountJC = totalMYRAmt.toFixed(2);
      this.totalFAmountJC = totalFAmt.toFixed(2);

      this.jcMultiCurr = !chargesList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code']);
      this.updateAPTotal();
    },
    //#endregion

    //#region Password
    // Button clicks in password popup
    pswdPopupClicked: function (e) {
      this.jobclosepswdErr = '';
      switch (e) {
        case 'OK':
          if (this.jobclosepswd.trim() == '') {
            this.jobclosepswdErr = 'Job Close Password is required!';
            return;
          }
          this.$emit('passwordEntered', this.jobclosepswd);
          break;

        case 'Cancel':
          this.jobclosepswd = '';
          this.$emit('passwordCancelled');
          break;
      }
      this.showEnterPswd = false;
    },

    // Show password input popup
    showPasswordInput: function () {
      this.jobclosepswd = '';
      return new Promise((resolve, reject) => {
        this.showEnterPswd = true;
        this.$once('passwordEntered', (password) => {
          this.$off('passwordCancelled');
          resolve(password);
        });

        this.$once('passwordCancelled', () => {
          this.$off('passwordEntered');
          reject('Cancel');
        });
      });
    },
    //#endregion

    //#region Direct Posting
    updateDPTotal: function (detailList) {
      let totalTax = 0;
      let totalMYRAmt = 0;
      let totalFAmt = 0;

      detailList.forEach((dp) => {
        totalTax += Number(dp['Tax Amount']);
        totalMYRAmt += Number(dp['Net Incl Tax']);
        totalFAmt += Number(dp['F.Net Incl Tax']);
      });

      this.totalTaxDP = totalTax.toFixed(2);
      this.totalAmountDP = totalMYRAmt.toFixed(2);
      this.totalFAmountDP = totalFAmt.toFixed(2);

      this.dpMultiCurr = !detailList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code']);
      this.updateAPTotal();
    },

    openExpenses: async function () {
      const discard = this.checkPayeeTypeCodeSelected();
      if (discard) {
        this.showPO = false;
        this.showInputDirectPosting = false;
        if (this.oriExpensesListing.length == 0) {
          await this.refreshClicked('Expenses');
        }
        this.showExpenses = true;
      }
    },

    openPO: async function () {
      const discard = this.checkPayeeTypeCodeSelected();
      if (discard) {
        this.showExpenses = false;
        this.showInputDirectPosting = false;
        if (this.oriPOListing.length == 0) {
          await this.refreshClicked('PO');
        }
        this.showPO = true;
      }
    },

    deleteDirectPosting: async function () {
      if (this.selectedDPEntry.length == 0) {
        this.$showAlert('error', 'No Direct Posting Selected', 'Please select at least 1 Direct Posting to delete.');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Direct Posting Record',
        `Are you sure you want to delete this record?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let temp = this.directPostingDetailsList;
      for (const entry of this.selectedDPEntry) {
        if (entry['action'] && entry['action'] == 'N') {
          temp = temp.filter((d) => d['Seq No'] != entry['Seq No']);
        } else {
          this.directPostingDetailsList.forEach((dp) => {
            if (dp['Seq No'] == entry['Seq No']) {
              dp['action'] = 'D';
              this.deletedDirectPosting.push(dp);
            }
          });
          temp = temp.filter((d) => d['action'] != 'D');
        }
      }

      this.directPostingDetailsList = [];
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = this.updateDPIndex(temp);
        this.selectedDPEntry = [];
        this.updateDPTotal(temp);
        this.clearAllDPData();
        this.expensesListing = this.filterExpensesListing(this.oriExpensesListing);
        this.poListing = this.filterPOListing(this.oriPOListing);
        this.showDirectPostingTable = true;
      });
    },

    openDPInput: function () {
      this.requiredError = [];
      this.headerExchRateErr = '';

      if (Number(this.headerExchRate) == 0) {
        this.headerExchRateErr = 'Exchange Rate is Invalid!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        return;
      }

      this.clearAllDPData();
      this.selectedDPEntry = [];
      this.selectedDPCurrency = this.currencyList.find(
        (curr) =>
          curr['Currency Code'] === (this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : 'MYR'),
      );
      this.dpCurrSelected(this.selectedDPCurrency);
      this.dpExchangeRate = Number(this.headerExchRate).toFixed(4);
      this.dpFactor = Number(this.headerExchFactor);

      this.showInputDirectPosting = true;
      this.showExpenses = false;
      this.showPO = false;
      this.saveDPAction = 'N';
      this.subtitleDP = 'New Direct Posting';
    },

    directPostingTableButtonClicked: function (_btn, data) {
      this.subtitleDP = 'Edit Direct Posting';
      this.showInputDirectPosting = true;
      this.selectedDPEntry = [data];
      this.saveDPAction = data['action'] && data['action'] == 'N' ? 'N' : 'E';
      this.populateDirectPostingDetails(data);
    },

    selectDeselectExpensesClicked: function () {
      if (this.selectedExpenses.length != this.expensesListing.length) {
        this.selectedExpenses = [];
        this.selectedExpenses = [...this.expensesListing];
      } else {
        this.selectedExpenses = [];
      }
    },

    addSelectedExpenses: function () {
      if (this.selectedExpenses.length == 0) {
        this.$showAlert('error', 'No Expenses Selected', 'Please select at least one Expense to add.');
        return;
      }

      const temp = this.directPostingDetailsList;

      for (const exp of this.selectedExpenses) {
        const allDP = temp.concat(this.deletedDirectPosting);
        const highestSeq =
          allDP
            .map((d) => Number(d['Seq No']))
            .sort(function(a, b) { return a - b; })
            .reverse()[0] ?? 0;

        const dataToAdd = {
          '#': '',
          action: 'N',
          APInvNo: '',
          'Seq No': highestSeq + 1,
          'Debit Ac': exp['Debit Acc'] || '',
          'Credit Ac': '', // Not used for now
          Amount: Number(exp['Amount'] || 0).toFixed(2),
          'F.Amount': Number(exp['F.Amount'] || 0).toFixed(2),
          'Tax Debit Ac': exp['Tax Dr Acc'] || '',
          'Tax Credit Ac': '', // Not used for now
          'Tax Code': exp['Tax Code'] || '',
          'Tax Rate': Number(exp['Tax Rate']).toString(),
          'Tax Amount': Number(exp['Tax Amount'] || 0).toFixed(2),
          'F.Tax Amount': Number(exp['F.Tax Amount'] || 0).toFixed(2),
          'Net Incl Tax': Number(exp['Net Incl Tax'] || 0).toFixed(2),
          'F.Net Incl Tax': Number(exp['F.Net Incl Tax'] || 0).toFixed(2),
          Remarks: exp['Remark'] || '',
          Currency: exp['Currency'] || '',
          Rate: Number(exp['Rate'] || '1.0000').toFixed(4),
          ExpenseGUID: exp['ExpenseGUID'] || '',
          'PO No': '',
          'Cost Code': '',
          'Vehicle No': exp['Vehicle No'] || '',
          'SMK No': '',
          UserID: '',
          'Date Updated': '',
        };
        temp.push(dataToAdd);
      }

      this.showExpenses = false;
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = this.updateDPIndex(temp);
        this.showDirectPostingTable = true;
        this.expensesListing = this.filterExpensesListing(this.oriExpensesListing);
        this.updateDPTotal(temp);
      });
    },

    cancelSelectExpenses: function () {
      this.showExpenses = false;
      this.selectedExpenses = [];
    },

    selectDeselectPOClicked: function () {
      if (this.selectedPO.length != this.poListing.length) {
        this.selectedPO = [];
        this.selectedPO = [...this.poListing];
      } else {
        this.selectedPO = [];
      }
    },

    addSelectedPO: function () {
      if (this.selectedPO.length == 0) {
        this.$showAlert('error', 'No PO Selected', 'Please select at least one PO to add.');
        return;
      }

      const temp = this.directPostingDetailsList;
      const allDP = temp.concat(this.deletedDirectPosting);
      for (const po of this.selectedPO) {
        const highestSeq =
          allDP
            .map((d) => Number(d['Seq No']))
            .sort(function(a, b) { return a - b; })
            .reverse()[0] ?? 0;

        const dataToAdd = {
          '#': '',
          action: 'N',
          APInvNo: '',
          'Seq No': highestSeq + 1,
          'Debit Ac': po['Debit Acc'] || '',
          'Credit Ac': 'PRPO', // Hardcode
          Amount: Number(po['Amount'] || '0.00').toFixed(2),
          'F.Amount': Number(po['Amount'] || '0.00').toFixed(2),
          'Tax Debit Ac': '',
          'Tax Credit Ac': '', // Not used for now
          'Tax Code': po['Tax Code'] || '',
          'Tax Rate': Number(po['Tax Rate'] || '0').toString(),
          'Tax Amount': Number(po['Tax Amount'] || '0.00').toFixed(2),
          'F.Tax Amount': Number(po['Tax Amount'] || '0.00').toFixed(2),
          'Net Incl Tax': Number(po['Net Incl Tax'] || '0.00').toFixed(2),
          'F.Net Incl Tax': Number(po['Net Incl Tax'] || '0.00').toFixed(2),
          Remarks: po['Remarks'] || '',
          Currency: po['Currency'] || '',
          Rate: Number(po['Rate'] || '1.0000').toFixed(4),
          ExpenseGUID: po['TxnID'] || '',
          'PO No': po['PO No'] || '',
          'Cost Code': '',
          'Vehicle No': '',
          'SMK No': '',
          UserID: '',
          'Date Updated': '',
        };
        temp.push(dataToAdd);
      }

      this.showPO = false;
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = this.updateDPIndex(temp);
        this.showDirectPostingTable = true;
        this.poListing = this.filterPOListing(this.oriPOListing);
        this.updateDPTotal(temp);
      });
    },

    cancelSelectPO: function () {
      this.showPO = false;
      this.selectedPO = [];
    },

    checkDPFields: function () {
      this.clearAllDPMessages();
      let error = false;
      if (!this.selectDrCrAcc) {
        this.selectDrCrAccErr = 'Debit Acc is required!';
        this.requiredError.push({
          key: 'dpdrcracc',
          value: this.selectDrCrAccErr,
        });
        error = true;
      }

      if (!this.selectedDPCurrency) {
        this.dpCurrencyErr = 'Currency is required!';
        this.requiredError.push({
          key: 'dpcurrency',
          value: this.dpCurrencyErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        this.selectedDPCurrency['Currency Code'] != this.selectedHeaderCurr['Currency Code']
      ) {
        this.dpCurrencyErr = 'Selected currency is different from header currency!';
        this.requiredError.push({
          key: 'dpCurrency',
          value: this.dpCurrencyErr,
        });
        error = true;
      }

      if (!this.dpExchangeRate.trim()) {
        this.dpExchangeRateErr = 'Exchange Rate is required!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
      } else if (Number(this.dpExchangeRate) == 0) {
        this.dpExchangeRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        Number(this.dpExchangeRate) != Number(this.headerExchRate)
      ) {
        this.dpExchangeRateErr = 'Selected exchange rate is different from header exchange rate!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
        error = true;
      }

      if (this.smkNoDP.trim().length > 35) {
        this.smkNoDPErr = 'SMK No cannot exceed 35 characters!';
        this.requiredError.push({ key: 'dpsmkno', value: this.smkNoDPErr });
        error = true;
      }

      if (this.poNoDP.trim().length > 25) {
        this.poNoDPErr = 'PO No cannot exceed 25 characters!';
        this.requiredError.push({ key: 'dppo', value: this.poNoDPErr });
        error = true;
      }
      return error;
    },

    saveDirectPosting: function () {
      this.clearAllDPMessages();
      if (this.checkDPFields()) return;

      const temp = this.directPostingDetailsList;
      const allDP = temp.concat(this.deletedDirectPosting);
      const highestSeq =
        allDP
          .map((d) => Number(d['Seq No']))
          .sort(function(a, b) { return a - b; })
          .reverse()[0] ?? 0;

      const dataToAdd = {
        '#': '',
        action: this.action == 'N' ? 'N' : this.saveDPAction,
        APInvNo: '',
        'Seq No': highestSeq + 1,
        'Debit Ac': this.txnType == '0' && this.selectDrCrAcc ? this.selectDrCrAcc['Acc No'] : '',
        'Credit Ac': this.txnType != '0' && this.selectDrCrAcc ? this.selectDrCrAcc['Acc No'] : '',
        Amount: this.dpTotalPrice || '0.00',
        'F.Amount': this.dpFTotalPrice || '0.00',
        'Tax Debit Ac': this.txnType == '0' && this.selectedTaxDrCrAcc ? this.selectedTaxDrCrAcc['Acc No'] : '',
        'Tax Credit Ac': this.txnType != '0' && this.selectedTaxDrCrAcc ? this.selectedTaxDrCrAcc['Acc No'] : '', // Not used for now
        'Tax Code': this.selectedDPTaxCode ? this.selectedDPTaxCode['Tax Code'] : '',
        'Tax Rate': this.dpTaxRate || '0',
        'Tax Amount': this.dpTaxAmt || '0.00',
        'F.Tax Amount': this.dpFTaxAmt || '0.00',
        'Net Incl Tax': this.dpTotalPriceInclTax || '0.00',
        'F.Net Incl Tax': this.dpFTotalPriceInclTax || '0.00',
        Remarks: this.directPostingRemarks || '',
        Currency: this.selectedDPCurrency ? this.selectedDPCurrency['Currency Code'] : 'MYR',
        Rate: this.dpExchangeRate || '1.0000',
        ExpenseGUID: '',
        'PO No': this.poNoDP || '',
        'Cost Code': this.selectedDPCostCode ? this.selectedDPCostCode['Cost Code'] : '',
        'Vehicle No': this.selectedDPVehicleNo ? this.selectedDPVehicleNo['Lorry No'] : '',
        'SMK No': this.smkNoDP || '',
        UserID: '',
        'Date Updated': '',
      };

      if (this.selectedDPEntry.length > 0) {
        dataToAdd['Seq No'] = this.selectedDPEntry[0]['Seq No'];
        dataToAdd['ExpenseGUID'] = this.selectedDPEntry[0]['ExpenseGUID'] || '';
        temp.splice(Number(this.selectedDPEntry[0]['#']) - 1, 1, dataToAdd);
      } else {
        temp.push(dataToAdd);
      }

      this.directPostingDetailsList = [];
      this.showInputDirectPosting = false;
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = this.updateDPIndex(temp);
        this.clearAllDPData();
        this.selectedDirectPostingEntry = [];
        this.saveDPAction = '';
        this.expensesListing = this.filterExpensesListing(this.oriExpensesListing);
        this.poListing = this.filterPOListing(this.oriPOListing);
        this.showDirectPostingTable = true;
        this.updateDPTotal(temp);
      });
    },

    cancelDirectPostingClicked: function () {
      this.requiredError = [];
      this.clearAllDPData();
      this.showInputDirectPosting = false;
    },

    filterExpensesListing: function (expenses) {
      const saved = [];
      this.directPostingDetailsList.forEach((d) => {
        if (d['ExpenseGUID'] != '') saved.push(d['ExpenseGUID']);
      });
      let filter1 = expenses;
      if (this.txnType != '0') {
        filter1 = expenses.filter((exp) => exp['Debit Acc'] != '');
      }
      filter1.splice(0, 1);
      return filter1.filter((exp) => !saved.includes(exp['ExpenseGUID']));
    },

    filterPOListing: function (po) {
      const saved = [];
      this.directPostingDetailsList.forEach((d) => {
        if (d['ExpenseGUID'] != '') saved.push(d['ExpenseGUID']);
      });
      return po.filter((p) => !saved.includes(p['ExpenseGUID']));
    },

    updateDPIndex: function (detailList) {
      const orderedList = detailList.map((n, index) => ({
        ...n,
        '#': index + 1,
      }));
      return orderedList;
    },
    //#endregion

    //#region Attachments
    openAttachmentPopup: function (action) {
      this.clearAllAttData();
      switch (action) {
        case 'new':
          this.attachmentsAction = 'N';
          this.selectedAtt = [];
          this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Add Attachment';
          break;

        case 'edit':
          if (this.selectedAtt.length == 0) {
            this.$showAlert('error', 'No Attachment Selected', 'Please select an Attachment to edit');
            return;
          }
          this.attachmentsAction = 'E';
          this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Edit Attachment';

          this.documents = {
            docName: this.selectedAtt[0]['File Name'],
            attachementFile: this.selectedAtt[0]['Doc Image'],
            filetype: this.selectedAtt[0]['File Type'],
            docGuid: this.selectedAtt[0]['Document GUID'],
            remarks: this.selectedAtt[0]['Doc Remark'],
            docID: this.selectedAtt[0]['ID'],
          };
          break;
      }
      this.showAttachmentsPopup = true;
    },

    deleteAttachmentClicked: async function () {
      if (this.selectedAtt.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Attachment',
        `Are you sure you want to delete this attachment ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let existingData = false;
      const tempDeleted = this.deletedAttList;
      this.deletedAttList = [];

      for (const d of this.selectedAtt) {
        const doc = {
          Action: 'D',
          'Document GUID': d['Document GUID'],
        };

        if (d['Document GUID']) {
          tempDeleted.push(doc);
          existingData = true;
        }
      }

      const temp = this.attachmentsList;
      const index = temp.findIndex((item) => {
        return existingData ?
            item['Document GUID'] == this.selectedAtt[0]['Document GUID']
          : item['ID'] == this.selectedAtt[0]['ID'];
      });
      temp.splice(index, 1);
      this.attachmentsList = [];

      this.$nextTick(() => {
        this.deletedAttList = tempDeleted;
        this.attachmentsList = temp;
        this.selectedAtt = [];
      });
    },

    attAdditionalBtnsClicked: async function (btn, data) {
      switch (btn) {
        case 'View':
          this.attachmentsAction = 'V';
          this.selectedAtt = [data];
          await this.projectAttachment({
            'File Name': data['File Name'],
            'MIME Type': null,
            'File Data': data['Doc Image'],
          });
          break;
      }
    },

    attachmentsPopupBtnsClicked: async function (btn) {
      let temp;
      let highestID = 1;
      let doc = null;

      switch (btn) {
        case 'Add':
          if (!this.documents['docName']) {
            this.attachmentsRequiredErrorMessage = 'Please attach at least one file';
            return;
          }

          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (d['File Name'] === this.documents['docName']) duplicate = true;
            });

            if (duplicate) {
              await this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }

          temp = [...this.attachmentsList];
          this.attachmentsList = [];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          doc = {
            Action: 'N',
            'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
            'Doc Date': new Date().formatString('yyyy-MM-dd'),
            'File Name': this.documents['docName'] ? this.documents['docName'] : '',
            'File Type': this.documents['filetype'] ? this.documents['filetype'] : '',
            'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
            'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
            ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            _attachmentAction: 'New',
          };

          this.$nextTick(() => {
            temp.push(doc);
            this.attachmentsList = temp;
            this.oldDoc = this.documents['docName'];
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;

        case 'Edit': {
          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (
                d['File Name'] === this.documents['docName'] &&
                this.documents['docName'] != this.selectedAtt[0]['File Name']
              )
                duplicate = true;
            });

            if (duplicate) {
              this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }
          temp = [...this.attachmentsList];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          let existingData = false;
          for (const d of this.selectedAtt) {
            doc = {
              Action: d['Action'] ? d['Action'] : 'E',
              'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
              'Doc Date': new Date().formatString('yyyy-MM-dd'),
              'File Name': this.documents['docName'] ? this.documents['docName'] : '',
              'File Type': this.documents['filetype'] ? this.documents['filetype'] : '',
              'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
              'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
              ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            };

            if (d['Document GUID']) existingData = true;
          }

          const temp2 = this.attachmentsList;
          const index = temp2.findIndex((item) => {
            return existingData ?
                item['Document GUID'] == this.selectedAtt[0]['Document GUID']
              : item['ID'] == this.selectedAtt[0]['ID'];
          });

          temp2.splice(index, 1, doc);
          this.attachmentsList = [];
          this.$nextTick(() => {
            this.oldDoc = this.documents['docName'];
            this.attachmentsList = temp2;
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;
        }

        case 'Cancel':
          this.showAttachmentsPopup = false;
          this.clearAllAttData();
          break;
      }
    },

    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['filetype'] = '';
        this.documents['attachmentFile'] = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.documents['docName'] = file[0].name;
        this.documents['filetype'] = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.attachmentsRequiredErrorMessage = '';
        this.$fullscreenLoading();
      }
    },

    projectAttachment: async function (file) {
      if (!file && process.env['NODE_ENV'] == 'development') {
        throw new Error('No file was passed to projectAttachment. Check for logic errors');
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/APInvoice/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );

        const untypedByteArray = [];
        for (let i = 0; i < byteCharacters.length; i++) {
          untypedByteArray.push(byteCharacters.charCodeAt(i));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: this.attachmentProjectorType });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },

    attachmentProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        return false;
      }

      const fileNameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = fileNameSplit[fileNameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },

    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedAtt[0]['File Name'];
      anchor.click();
    },

    closeProjector: function () {
      this.attachmentProjectorType = '';
      this.attachmentProjectorData = '';
      this.showAttachmentProjector = false;
    },
    //#endregion

    //#region Summary of Events
    // getIndicatorName: function (key, value, type) {
    //   const item = this.summaryItems.find((item) => item['Title'] === key);
    //   if (item && item['Indicator']) {
    //     const indicator = item['Indicator'].find((i) => i['code'] === value);

    //     if (indicator) {
    //       return type == 'title' ? indicator['name'] : indicator['bg'];
    //     }
    //   }
    //   return item?.Indicator?.[0]?.name || null;
    // },

    // loadSummaryOfEvents: function () {
    //   for (const key in this.apDetails) {
    //     if (Object.prototype.hasOwnProperty.call(this.apDetails, key)) {
    //       const indicatorName = this.getIndicatorName(key, this.apDetails[key], 'title');
    //       const bgColour = this.getIndicatorName(key, this.apDetails[key], 'background');
    //       const element = document.getElementById(key);
    //       if (element) {
    //         element.textContent = indicatorName;
    //         element.style.backgroundColor = bgColour;
    //       }
    //     }
    //   }
    // },

    updateSummaryItems: function () {
      for (const item of this.summaryItems) {
        const title = item['Title'];
        const value = this.apDetails[title];

        if (title == 'PaymentNo' && value) {
          item['indicatorToShow'] =
            `${value} - ${this.apDetails['PaymentCurr']} ${Number(this.apDetails['FOffsetAmt']).toFixed(2)}`;
          item['visible'] = true;
        } else if (value && item['Indicator']) {
          const indicator = item['Indicator'].find((i) => i['code'] === value);
          if (indicator) {
            if (title == 'Indicator' && value == 'N') {
              item['indicatorToShow'] = `New AP ${this.pageLabel}`;
            } else {
              item['indicatorToShow'] = indicator['name'];
            }
            item['bgcolorToShow'] = indicator['bg'];
            item['visible'] = true;
          }
        }
      }
    },
    //#endregion

    //#region Loss focus
    termFocusChanged: function (e, termInput) {
      if (!e && termInput && termInput.trim() !== '') {
        this.dueDate = this.calculateDueDate(termInput, this.invoiceDate);
      }
    },

    headerRateLossFocus: async function (focus, rate) {
      if (!focus) {
        if (Number(rate) <= 0) {
          await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
          this.headerExchRate = Number(this.oldRate).toFixed(4);
          return;
        }

        const allDetails = this.chargesDetailsList.concat(this.directPostingDetailsList);
        const currDiff = allDetails.length > 0 ? allDetails.some((d) => Number(d['Rate']) != Number(rate)) : false;

        if (this.selectedHeaderCurr['Currency Code'] != 'MYR' && currDiff) {
          const confirm = await this.$showAlert(
            'warning',
            'Header Exch. Rate Changed',
            `Are you sure you want to change all Exch. Rate?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );

          if (confirm == 'Yes') {
            this.showJobChargesTable = false;
            this.showDirectPostingTable = false;

            // Recalculate job charges and direct posting after exchange rate change
            this.headerExchRate = Number(rate || '1').toFixed(4);
            if (this.chargesDetailsList.length > 0) {
              this.chargesDetailsList.forEach((j) => {
                const qty = Number(j['Qty']);
                const buyUnitPrice = (Number(j['F.Unit Price']) * Number(rate)) / Number(this.headerExchFactor);
                const buyAmount = buyUnitPrice * qty;
                const markUnitPrice =
                  (Number(j['F.Mark Up Unit Price']) * Number(rate)) / Number(this.headerExchFactor);
                const markAmount = markUnitPrice * qty;
                const sellUnitPrice =
                  (Number(j['F.Selling Unit Price']) * Number(rate)) / Number(this.headerExchFactor);
                const sellAmount = sellUnitPrice * qty;
                const taxAmt = buyAmount * (Number(j['Tax Rate']) / 100);
                const netInclTax = buyAmount + taxAmt;
                const netUnitInclTax = netInclTax / qty;

                j['Rate'] = Number(rate).toFixed(4);
                j['Buying Unit Price'] = buyUnitPrice.toFixed(4);
                j['Buying Amount'] = buyAmount.toFixed(2);
                j['Mark Up Unit Price'] = markUnitPrice.toFixed(4);
                j['Mark Up Amount'] = markAmount.toFixed(2);
                j['Selling Unit Price'] = sellUnitPrice.toFixed(4);
                j['Selling Amount'] = sellAmount.toFixed(2);
                j['Tax Amount'] = taxAmt.toFixed(2);
                j['Net Unit Price Incl Tax'] = netUnitInclTax.toFixed(4);
                j['Net Incl Tax'] = netInclTax.toFixed(2);
              });
            }
            if (this.directPostingDetailsList.length > 0) {
              this.directPostingDetailsList.forEach((d) => {
                const amount = (Number(d['F.Amount']) * Number(rate)) / Number(this.headerExchFactor);
                const taxAmt = amount * (Number(d['Tax Rate']) / 100);
                const netInclTax = amount + taxAmt;

                d['Rate'] = Number(rate).toFixed(4);
                d['Amount'] = amount.toFixed(2);
                d['Taxable Amt'] = taxAmt.toFixed(2);
                d['Net Inclusive Tax'] = netInclTax.toFixed(2);
              });
            }

            this.$nextTick(() => {
              this.showJobChargesTable = true;
              this.showDirectPostingTable = true;
            });
          } else {
            this.headerExchRate = Number(this.oldRate).toFixed(4);
          }
        } else {
          this.headerExchRate = Number(rate || '1').toFixed(4);
        }
      }
    },

    jobnoRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Job No');
        this.jobNoList = [];
        this.jobNoList = await this.$apiCallV2(`/APInvoice/GetJobListing`, {
          top: this.jobnoRowLimit,
          jobtypeid: this.selectedAPType['JobTypeID'],
        });
        this.$fullscreenLoading();
      }
    },

    contnoRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Container No');
        this.containerList = [];
        this.oriContainerList = await this.$apiCallV2(`/APInvoice/GetQueryByJobConfig`, {
          table: 'Container',
          jobno: this.selectedJobNoFreeText ?? '',
          top: this.contnoRowLimit,
        });
        this.containerList = [...this.oriContainerList];
        this.$fullscreenLoading();
      }
    },

    consignmentRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Consignment No');
        this.consignmentList = [];
        this.oriConsignmentList = await this.$apiCallV2('/APInvoice/GetQueryByJobConfig', {
          table: 'Consignment', 
          top: this.consignmentRowLimit,
        });
        this.consignmentList = [...this.oriConsignmentList];
        this.$fullscreenLoading();
      }
    },

    //#region Charges Price Loss Focus and calculations
    chargesPriceLossFocus: function (state, field, value) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (field) {
          case 'qty':
            this.chargesBuyFTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesBuyFUPrice);
            this.chargesBuyTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesBuyUPrice);
            this.chargesFTaxAmt = this.cUpdateTaxAmt(this.chargesBuyFTotalPrice, this.chargesTaxRate);
            this.chargesFTotalPriceInclTax = this.cUpdateNetInclTax(this.chargesBuyFTotalPrice, this.chargesFTaxAmt);
            this.chargesTaxAmt = this.cUpdateTaxAmt(this.chargesBuyTotalPrice, this.chargesTaxRate);
            this.chargesTotalPriceInclTax = this.cUpdateNetInclTax(this.chargesBuyTotalPrice, this.chargesTaxAmt);
            this.chargesMUpFTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesMUpFUnitPrice);
            this.chargesMUpTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesMUpUnitPrice);
            this.chargesSellFTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesSellFUnitPrice);
            this.chargesSellTotalPrice = this.cUnitQtyUpdateTotal(value, this.chargesSellUnitPrice);
            break;

          case 'buyfunitprice':
            // Update Buy
            this.chargesBuyFUPrice = Number(value || 0).toFixed(4);
            this.chargesBuyFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesBuyUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesBuyTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesBuyUPrice);

            // Update Sell
            this.chargesSellFTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyFTotalPrice,
              this.chargesMUpFTotalPrice,
              this.chargesSellFTotalPrice,
              'sell',
            );
            this.chargesSellFUPrice = this.cTotalUpdateUnit(this.chargesSellFTotalPrice, this.chargesQty);
            this.chargesSellTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyTotalPrice,
              this.chargesMUpTotalPrice,
              this.chargesSellTotalPrice,
              'sell',
            );
            this.chargesSellUPrice = this.cTotalUpdateUnit(this.chargesSellTotalPrice, this.chargesQty);

            this.cUpdateTaxFields(
              value,
              this.chargesBuyFTotalPrice,
              this.chargesBuyUPrice,
              this.chargesBuyTotalPrice,
              this.chargesTaxRate,
            );
            break;

          case 'buyftotalprice':
            // Update Buy
            this.chargesBuyFTotalPrice = Number(value || 0).toFixed(2);
            this.chargesBuyFUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate foreign unit price based on total
            this.chargesBuyUPrice = this.cRateUpdateUnitPrice(
              this.chargesBuyFUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            ); // calculate local unit price based on foreign unit price (currency conversion)
            this.chargesBuyTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesBuyUPrice); // calculate local total price based on local unit price

            // Update Sell
            this.chargesSellFTotalPrice = this.cUpdateBuyMUSellTotal(
              value,
              this.chargesMUpFTotalPrice,
              this.chargesSellFTotalPrice,
              'sell',
            );
            this.chargesSellFUPrice = this.cTotalUpdateUnit(this.chargesSellFTotalPrice, this.chargesQty);
            this.chargesSellTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyTotalPrice,
              this.chargesMUpTotalPrice,
              this.chargesSellTotalPrice,
              'sell',
            );
            this.chargesSellUPrice = this.cTotalUpdateUnit(this.chargesSellTotalPrice, this.chargesQty);

            this.cUpdateTaxFields(
              this.chargesBuyFUPrice,
              value,
              this.chargesBuyUPrice,
              this.chargesBuyTotalPrice,
              this.chargesTaxRate,
            );
            break;

          case 'buyunitprice':
            // Update Buy
            this.chargesBuyUPrice = Number(value || 0).toFixed(4);
            this.chargesBuyTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesBuyFUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            );
            this.chargesBuyFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesBuyFUPrice);

            // Update Sell
            this.chargesSellTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyTotalPrice,
              this.chargesMUpTotalPrice,
              this.chargesSellTotalPrice,
              'sell',
            );
            this.chargesSellUPrice = this.cTotalUpdateUnit(this.chargesSellTotalPrice, this.chargesQty);
            this.chargesSellFTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyFTotalPrice,
              this.chargesMUpFTotalPrice,
              this.chargesSellFTotalPrice,
              'sell',
            );
            this.chargesSellFUPrice = this.cTotalUpdateUnit(this.chargesSellFTotalPrice, this.chargesQty);

            this.cUpdateTaxFields(
              this.chargesBuyFUPrice,
              this.chargesBuyFTotalPrice,
              value,
              this.chargesBuyTotalPrice,
              this.chargesTaxRate,
            );
            break;

          case 'buytotalprice':
            // Update Buy
            this.chargesBuyTotalPrice = Number(value || 0).toFixed(2);
            this.chargesBuyUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate local unit price based on total
            this.chargesBuyFUPrice = this.cRateUpdateUnitPrice(
              this.chargesBuyUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            ); // calculate foreign unit price based on local unit price (currency conversion)
            this.chargesBuyFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesBuyFUPrice); // calculate foreign total price based on foreign unit price

            // Update Sell
            this.chargesSellTotalPrice = this.cUpdateBuyMUSellTotal(
              value,
              this.chargesMUpTotalPrice,
              this.chargesSellTotalPrice,
              'sell',
            );
            this.chargesSellUPrice = this.cTotalUpdateUnit(this.chargesSellTotalPrice, this.chargesQty);
            this.chargesSellFTotalPrice = this.cUpdateBuyMUSellTotal(
              this.chargesBuyFTotalPrice,
              this.chargesMUpFTotalPrice,
              this.chargesSellFTotalPrice,
              'sell',
            );
            this.chargesSellFUPrice = this.cTotalUpdateUnit(this.chargesSellFTotalPrice, this.chargesQty);

            this.cUpdateTaxFields(
              this.chargesBuyFUPrice,
              this.chargesBuyFTotalPrice,
              this.chargesBuyUPrice,
              value,
              this.chargesTaxRate,
            );
            break;

          case 'mUpfunitprice':
            // Update markup
            this.chargesMUpFUPrice = Number(value || 0).toFixed(4);
            this.chargesMUpFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesMUpUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesMUpTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesMUpUPrice);

            // Update sell
            this.cUpdateSellFields();
            break;

          case 'mupunitprice':
            // Update markup
            this.chargesMUpUPrice = Number(value || 0).toFixed(4);
            this.chargesMUpTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesMUpFUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            );
            this.chargesMUpFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesMUpFUPrice);

            // Update sell
            this.cUpdateSellFields();
            break;

          case 'mupftotalprice':
            // Update markup
            this.chargesMUpFTotalPrice = Number(value || 0).toFixed(2);
            this.chargesMUpFUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate foreign unit price based on total
            this.chargesMUpUPrice = this.cRateUpdateUnitPrice(
              this.chargesMUpFUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            ); // calculate local unit price based on foreign unit price (currency conversion)
            this.chargesMUpTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesMUpUPrice); // calculate local total price based on local unit price

            // Update sell
            this.cUpdateSellFields();
            break;

          case 'muptotalprice':
            // Update markup
            this.chargesMUpTotalPrice = Number(value || 0).toFixed(2);
            this.chargesMUpUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate local unit price based on total
            this.chargesMUpFUPrice = this.cRateUpdateUnitPrice(
              this.chargesMUpUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            ); // calculate foreign unit price based on local unit price (currency conversion)
            this.chargesMUpFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesMUpFUPrice); // calculate foreign total price based on foreign unit price

            // Update sell
            this.cUpdateSellFields();
            break;

          case 'sellfunitprice':
            // Update sell
            this.chargesSellFUPrice = Number(value || 0).toFixed(4);
            this.chargesSellFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesSellUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesSellTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesSellUPrice);

            // Update markup
            this.cUpdateMarkupFields();
            break;

          case 'sellunitprice':
            // Update sell
            this.chargesSellUPrice = Number(value || 0).toFixed(4);
            this.chargesSellTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, value);
            this.chargesSellFUPrice = this.cRateUpdateUnitPrice(
              value,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            );
            this.chargesSellFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesSellFUPrice);

            // Update markup
            this.cUpdateMarkupFields();
            break;

          case 'sellftotalprice':
            this.chargesSellFTotalPrice = Number(value || 0).toFixed(2);
            this.chargesSellFUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate foreign unit price based on total
            this.chargesSellUPrice = this.cRateUpdateUnitPrice(
              this.chargesSellFUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'FToLocal',
            ); // calculate local unit price based on foreign unit price (currency conversion)
            this.chargesSellTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesSellUPrice); // calculate local total price based on local unit price

            this.cUpdateMarkupFields();
            break;

          case 'selltotalprice':
            this.chargesSellTotalPrice = Number(value || 0).toFixed(2);
            this.chargesSellUPrice = this.cTotalUpdateUnit(value, this.chargesQty); // calculate local unit price based on total
            this.chargesSellFUPrice = this.cRateUpdateUnitPrice(
              this.chargesSellUPrice,
              this.chargesExchangeRate,
              this.chargesFactor,
              'LocalToF',
            ); // calculate foreign unit price based on local unit price (currency conversion)
            this.chargesSellFTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesSellFUPrice); // calculate foreign total price based on foreign unit price

            this.cUpdateMarkupFields();
            break;

          case 'exchgRate':
            this.chargesExchangeRate = Number(value || this.selectedChargesCurrency['Currency Rate'] || 1).toFixed(4);
            this.chargesBuyUPrice = this.cRateUpdateUnitPrice(
              this.chargesBuyFUPrice,
              value,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesBuyTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesBuyUPrice);
            this.chargesTaxAmt = this.cUpdateTaxAmt(this.chargesBuyTotalPrice, this.chargesTaxRate);
            this.chargesUPriceInclTax = this.cUpdateUPriceInclTax(this.chargesBuyUPrice, this.chargesTaxRate);
            this.chargesTotalPriceInclTax = this.cUpdateNetInclTax(this.chargesBuyTotalPrice, this.chargesTaxAmt);

            this.chargesSellUPrice = this.cRateUpdateUnitPrice(
              this.chargesSellFUPrice,
              value,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesSellTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesSellUPrice);

            this.chargesMUpUPrice = this.cRateUpdateUnitPrice(
              this.chargesMUpFUPrice,
              value,
              this.chargesFactor,
              'FToLocal',
            );
            this.chargesMUpTotalPrice = this.cUnitQtyUpdateTotal(this.chargesQty, this.chargesMUpUPrice);
            break;

          case 'taxrate':
            this.chargesTaxRate = Number(value || 0).toString();
            this.cUpdateTaxFields(
              this.chargesBuyFUPrice,
              this.chargesBuyFTotalPrice,
              this.chargesBuyUPrice,
              this.chargesBuyTotalPrice,
              value,
            );
            break;

          case 'rebate':
            this.chargesRebate = Number(value || 0).toFixed(2);
            break;
        }
      }
    },

    cUnitQtyUpdateTotal: function (qty = 1, uPrice = 0) {
      let total = 0;
      total = Number(qty || 0) * Number(uPrice || 0);
      return total.toFixed(2) || '0.00';
    },

    cTotalUpdateUnit: function (total = 0, qty = 1) {
      let unitPrice = 0;
      unitPrice = Number(total || 0) / Number(qty || 1);
      return unitPrice.toFixed(4) || '0.0000';
    },

    cUpdateTaxAmt: function (total = 0, taxRate = 0) {
      let tax = 0;
      tax = Number(total || 0) * (Number(taxRate || 0) / 100);
      return tax.toFixed(2) || '0.00';
    },

    cUpdateNetInclTax: function (total = 0, taxAmt = 0) {
      let incl = 0;
      incl = Number(total || 0) + Number(taxAmt || 0);
      return incl.toFixed(2) || '0.00';
    },

    cUpdateUPriceInclTax: function (price = 0, taxRate = 0) {
      let unitTax = 0;
      unitTax = Number(price || 0) * (Number(taxRate || 0) / 100 + 1);
      return unitTax.toFixed(4) || '0.0000';
    },

    cUpdateBuyMUSellTotal: function (buy = 0, markup = 0, sell = 0, type) {
      let total = 0;
      switch (type) {
        case 'markup':
          total = Number(sell || 0) - Number(buy || 0);
          break;

        case 'sell':
          total = Number(buy || 0) + Number(markup || 0);
          break;

        default:
          total = Number(sell || 0) - Number(markup || 0) || buy;
          break;
      }
      return total.toFixed(2) || '0.00';
    },

    cRateUpdateUnitPrice: function (price = 0, rate = 1, factor = 1, exch) {
      // exch: (FToLocal), LocalToF
      let unitPrice = 0;
      switch (exch) {
        case 'LocalToF':
          unitPrice = (Number(price || 0) / Number(rate || 1)) * Number(factor || 1); // price = local unit price
          break;

        default:
          unitPrice = (Number(price || 0) * Number(rate || 1)) / Number(factor || 1); // price = foreign unit price
          break;
      }
      return unitPrice.toFixed(4) || '0.0000';
    },

    cUpdateTaxFields: function (funit, ftotal, unit, total, taxrate) {
      this.chargesFTaxAmt = this.cUpdateTaxAmt(Number(ftotal || 0), Number(taxrate || 0));
      this.chargesFUPriceInclTax = this.cUpdateUPriceInclTax(Number(funit || 0), Number(taxrate || 0));
      this.chargesFTotalPriceInclTax = this.cUpdateNetInclTax(Number(ftotal || 0), this.chargesFTaxAmt);
      this.chargesTaxAmt = this.cUpdateTaxAmt(Number(total || 0), Number(taxrate || 0));
      this.chargesUPriceInclTax = this.cUpdateUPriceInclTax(Number(unit || 0), Number(taxrate || 0));
      this.chargesTotalPriceInclTax = this.cUpdateNetInclTax(Number(total || 0), this.chargesTaxAmt);
    },

    cUpdateSellFields: function () {
      this.chargesSellFTotalPrice = this.cUpdateBuyMUSellTotal(
        this.chargesBuyFTotalPrice,
        this.chargesMUpFTotalPrice,
        this.chargesSellFTotalPrice,
        'sell',
      );
      this.chargesSellFUPrice = this.cTotalUpdateUnit(this.chargesSellFTotalPrice, this.chargesQty);
      this.chargesSellTotalPrice = this.cUpdateBuyMUSellTotal(
        this.chargesBuyTotalPrice,
        this.chargesMUpTotalPrice,
        this.chargesSellTotalPrice,
        'sell',
      );
      this.chargesSellUPrice = this.cTotalUpdateUnit(this.chargesSellTotalPrice, this.chargesQty);
    },

    cUpdateMarkupFields: function () {
      this.chargesMUpFTotalPrice = this.cUpdateBuyMUSellTotal(
        this.chargesBuyFTotalPrice,
        this.chargesMUpFTotalPrice,
        this.chargesSellFTotalPrice,
        'markup',
      );
      this.chargesMUpFUPrice = this.cTotalUpdateUnit(this.chargesMUpFTotalPrice, this.chargesQty);
      this.chargesMUpTotalPrice = this.cUpdateBuyMUSellTotal(
        this.chargesBuyTotalPrice,
        this.chargesMUpTotalPrice,
        this.chargesSellTotalPrice,
        'markup',
      );
      this.chargesMUpUPrice = this.cTotalUpdateUnit(this.chargesMUpTotalPrice, this.chargesQty);
    },
    //#endregion

    //#region Direct Posting Price Loss Focus and calculations
    dpPriceLossFocus: function (state, field, value) {
      if (!state && !this.viewMode && this.showInputDirectPosting) {
        switch (field.toLowerCase()) {
          case 'exchgrate':
            this.dpExchangeRate =
              value ? Number(value).toFixed(4) : Number(this.selectedDPCurrency['Currency Rate']).toFixed(4);
            this.dpTotalPrice = this.dUpdateTotal(this.dpFTotalPrice, value, this.dpExchFactor, 'FToLocal');
            this.dpTaxAmt = this.dUpdateTaxAmt(this.dpTotalPrice, this.dpTaxRate);
            this.dpTotalPriceInclTax = this.dUpdateInclTax(this.dpTotalPrice, this.dpTaxAmt);
            break;

          case 'ftotalprice':
            this.dpFTotalPrice = Number(value || 0).toFixed(2);
            this.dpTotalPrice = this.dUpdateTotal(value, this.dpExchangeRate, this.dpExchFactor, 'FToLocal');
            this.dUpdateTaxFields(this.dpTotalPrice, value, this.dpTaxRate);
            break;

          case 'totalprice':
            this.dpTotalPrice = Number(value || 0).toFixed(2);
            this.dpFTotalPrice = this.dUpdateTotal(value, this.dpExchangeRate, this.dpExchFactor, 'LocalToF');
            this.dUpdateTaxFields(value, this.dpFTotalPrice, this.dpTaxRate);
            break;

          case 'taxrate':
            this.dpTaxRate = Number(value || 0).toString();
            this.dUpdateTaxFields(this.dpTotalPrice, this.dpFTotalPrice, value);
            break;
        }
      }
    },

    dUpdateTotal: function (amt = 0, rate = 1, factor = 1, exch) {
      let total = 0;
      switch (exch) {
        case 'LocalToF':
          total = (Number(amt || 0) / Number(rate || 1)) * Number(factor || 1); // amt = local amount
          break;

        default:
          total = (Number(amt || 0) * Number(rate || 1)) / Number(factor || 1); // amt = foreign amount
          break;
      }
      return total.toFixed(2) || '0.00';
    },

    dUpdateTaxAmt: function (amt = 0, taxrate = 0) {
      let tax = 0;
      tax = Number(amt || 0) * (Number(taxrate || 0) / 100);
      return tax.toFixed(2) || '0.00';
    },

    dUpdateInclTax: function (amt = 0, taxAmt = 0) {
      let incl = 0;
      incl = Number(amt || 0) + Number(taxAmt || 0);
      return incl.toFixed(2) || '0.00';
    },

    dUpdateTaxFields: function (total, ftotal, taxrate) {
      this.dpTaxAmt = this.dUpdateTaxAmt(total, taxrate);
      this.dpFTaxAmt = this.dUpdateTaxAmt(ftotal, taxrate);
      this.dpTotalPriceInclTax = this.dUpdateInclTax(total, this.dpTaxAmt);
      this.dpFTotalPriceInclTax = this.dUpdateInclTax(ftotal, this.dpFTaxAmt);
    },
    //#endregion

    //#endregion

    //#region Selected
    apTypeSelected: async function (type) {
      this.$fullscreenLoading('Getting Job No');
      this.jobNoList = await this.$apiCallV2(`/APInvoice/GetJobListing`, {
        jobtypeid: type['JobTypeID'],
        top: this.jobnoRowLimit,
      });
      this.$fullscreenLoading();

      this.viewMode = this.action == '';
    },

    payeeTypeSelected: async function (newType, _oldType) {
      // let discard = 'Discard';
      // if (this.showJobCharges || this.showExpenses) {
      //   discard = await this.$showAlert(
      //     'warning',
      //     'Discard Changes',
      //     "There's already data added in the tabs. Do you wish to discard the changes and change payee type?",
      //     [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      //   );
      // }
      // if (discard == 'Cancel') {
      //   this.selectedPayeeType = oldType;
      //   return;
      // }

      // this.showJobCharges = false;
      // this.showExpenses = false;

      this.$fullscreenLoading('Getting Payee Codes');
      await this.getPayeeCodeList(newType);
      this.$fullscreenLoading();
      this.selectedPayeeCode = null;
    },

    payeeCodeSelected: async function (newPayeeCode) {
      const payeeTerm = newPayeeCode && newPayeeCode['Term'] ? newPayeeCode['Term'].trim() : '';
      if (payeeTerm !== '') {
        this.selectedTerm = this.termList.find((t) => t['term'] == payeeTerm);
        if (!this.selectedTerm) {
          this.termInput = payeeTerm;
        }
        this.dueDate = this.calculateDueDate(payeeTerm, this.invoiceDate);
      }

      await this.refreshClicked('Job');
      if (this.txnType != '0') {
        await this.getAPINForAPCN(this.selectedPayeeType['Party Code'], newPayeeCode[this.payeeCodeKey]);
      }

      this.$nextTick(() => {
        this.jobChargesListing = this.updateJobChargesListing();
      });
    },

    headerCurrencySelected: async function (newCurr) {
      this.headerExchRate = newCurr ? Number(newCurr['Currency Rate']).toFixed(4) : '1.0000';
      if (newCurr && Number(newCurr['Currency Rate']) == 0) {
        await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
        this.headerExchRate = '1.0000';
      }
      this.headerExchFactor = newCurr ? newCurr['Factor'].toString() : '';
      this.oldRate = newCurr ? newCurr['Currency Rate'] : '';
      this.selectedChargesCurrency = this.currencyList.find(
        (c) => c['Currency Code'] == (newCurr ? newCurr['Currency Code'] : 'MYR'),
      );
      this.chargeExchgRateDisabled = this.selectedChargesCurrency['Currency Code'] === 'MYR';
      this.selectedDPCurrency = this.currencyList.find(
        (c) => c['Currency Code'] == (newCurr ? newCurr['Currency Code'] : 'MYR'),
      );
      this.dpExchgRateDisabled = this.selectedDPCurrency['Currency Code'] === 'MYR';
    },

    termSelected: function (newTerm) {
      if (!newTerm) return;
      this.dueDate = this.calculateDueDate(newTerm['term'], this.invoiceDate);
    },

    contNoSelected: async function (newCont, oldCont) {
      if (!newCont && (!this.selectedJobNoFreeText || this.selectedJobNoFreeText.trim() == '') && (!this.selectedConsignmentFreeText || this.selectedConsignmentFreeText.trim() == '')) {
        this.containerList = [...this.oriContainerList];
        this.consignmentList = [...this.oriConsignmentList];
        return;
      } else if (!newCont) {
        return;
      }
      
      if (this.selectedJobNoFreeText && this.selectedJobNoFreeText.trim() != '') {
        if (newCont['Job No'].trim() != this.selectedJobNoFreeText.trim()) {
          await this.$showAlert(
            'warning',
            `Invalid Container No for Job ${this.selectedJobNoFreeText.trim()}`,
            `${newCont['Cont No']} does not belong to ${this.selectedJobNoFreeText.trim()}`,
          );
          this.selectedContainer = oldCont;
          this.selectedContainerFreeText = oldCont['Cont No'];
        } else {
          this.selectedConsignment =
            newCont['Consignment No'] && newCont['Consignment No'].trim() != '' ?
              this.consignmentList.find((c) => c['Consignment No'] == newCont['CN No'])
            : null;
          if (this.selectedConsignment) {
            this.selectedConsignmentFreeText = this.selectedConsignment['Consignment No'];
            this.consignmentErr = '';
          }
        }
      } else {
        this.selectedJobNo =
          newCont['Job No'] && newCont['Job No'].trim() !== '' ?
            this.jobNoList.find((job) => job['Job No'] == newCont['Job No'])
          : null;
        if (this.selectedJobNo) {
          this.selectedJobNoFreeText = this.selectedJobNo['Job No'];
        }
        this.selectedConsignment =
          newCont['CN No'] && newCont['CN No'].trim() != '' ?
            this.consignmentList.find((c) => c['Consignment No'] == newCont['CN No'])
          : null;
        if (this.selectedConsignment) {
          this.selectedConsignmentFreeText = this.selectedConsignment['Consignment No'];
          this.consignmentErr = '';
        }
      }
    },

    cnnoSelected: async function (newCN, oldCN) {
      if (!newCN && (!this.selectedJobNoFreeText || this.selectedJobNoFreeText.trim() == '') && (!this.selectedContainerFreeText || this.selectedContainerFreeText.trim() == '')) {
        this.containerList = [...this.oriContainerList];
        this.consignmentList = [...this.oriConsignmentList];
        return;
      } else if (!newCN) {
        return;
      }

      if (this.selectedJobNoFreeText && this.selectedJobNoFreeText.trim() != '') {
        if (newCN['Job No'].trim() != this.selectedJobNoFreeText.trim()) {
          await this.$showAlert(
            'warning',
            `Invalid Consignment No for Job ${this.selectedJobNoFreeText.trim()}`,
            `Job No: ${newCN['Job No']}`,
          );
          this.selectedConsignment = oldCN;
          this.selectedConsignmentFreeText = oldCN['Consignment No'];
        } else {
          this.selectedContainer =
            newCN['Cont No'] && newCN['Cont No'].trim() != '' ?
              this.containerList.find((c) => c['Cont No'] == newCN['Cont No'])
            : null;
          if (this.selectedContainer) {
            this.selectedContainerFreeText = this.selectedContainer['Cont No'];
            this.containerErr = '';
          }
        }
      } else {
        this.selectedJobNo =
          newCN['Job No'] && newCN['Job No'].trim() !== '' ?
            this.jobNoList.find((job) => job['Job No'] == newCN['Job No'])
          : null;
        if (this.selectedJobNo) {
          this.selectedJobNoFreeText = this.selectedJobNo['Job No'];
        }
        this.selectedContainer =
          newCN['Cont No'] && newCN['Cont No'].trim() != '' ?
            this.containerList.find((c) => c['Cont No'] == newCN['Cont No'])
          : null;
        if (this.selectedContainer) {
          this.selectedContainerFreeText = this.selectedContainer['Cont No'];
          this.containerErr = '';
        }
      }
    },

    chargesCurrSelected: function (newCurr) {
      const rate =
        newCurr['Currency Code'] == this.selectedHeaderCurr['Currency Code'] ? this.headerExchRate
        : newCurr ? Number(newCurr['Currency Rate']).toFixed(4)
        : '1.0000';

      this.chargesExchangeRate = rate;
      this.chargeExchgRateDisabled = newCurr && newCurr['Currency Code'] === 'MYR';
      this.chargesFactor = newCurr ? newCurr['Factor'] : '1';

      this.chargesPriceLossFocus(false, 'exchgRate', rate);
    },

    taxCodeChrgSelected: function (newCode) {
      this.chargesTaxRate = newCode ? newCode[this.txnType == '3' ? 'RATE' : 'Tax Rate'].toString() : '0';
      this.chargesPriceLossFocus(false, 'taxrate', this.chargesTaxRate);
    },

    dpCurrSelected: function (newCurr) {
      const rate =
        newCurr['Currency Code'] == this.selectedHeaderCurr['Currency Code'] ? this.headerExchRate
        : newCurr ? Number(newCurr['Currency Rate']).toFixed(4)
        : '1.0000';
      this.dpExchangeRate = rate;
      this.dpExchgRateDisabled = newCurr && newCurr['Currency Code'] === 'MYR';
      this.dpFactor = newCurr ? newCurr['Factor'] : '1';

      this.dpPriceLossFocus(false, 'exchgrate', rate);
    },

    taxCodeDPSelected: function (newCode) {
      this.dpTaxRate = newCode ? newCode[this.txnType == '3' ? 'RATE' : 'Tax Rate'].toString() : '0';
      this.dpPriceLossFocus(false, 'taxrate', this.dpTaxRate);
    },
    //#endregion

    // Since this code is shared by APIN, APCN, APDN,
    // this method ensures that all UI states, filters, and menu labels are updated according to the current route/page type.
    // Called in:
    //    1. By `$bus.$on('routeChanged', this.routeChanged)` when user switches pages.
    //    2. After mountedPromises to initialize data on first load. (first load won't trigger $bus.$on)
    routeChanged: async function (route) {
      this.showPopup = false;
      this.clearAllData();

      //Clear all filter values incase user switch page while keeping main filter open
      this.mainFilterIsOpen = false;
      this.filterAPListingByDate = true;
      this.apListingFromDate = new Date();
      this.apListingToDate = new Date();
      this.filterAPListingByInvDate = false;
      this.apListingFromInvDate = new Date();
      this.apListingToInvDate = new Date();
      this.filterAPListingBySearch = false;

      let pageRoute = this.$route.path.split('/');
      if (route) {
        pageRoute = route.path.split('/');
        this.menu.forEach((m) => {
          m['visible'] = true;
          m['enabled'] = m['key'] == 'mnuImportData';
          m['title'] =
            ['mnAuthorized', 'mnuImportData'].includes(m['key']) ? m['title'] : `${m['title'].split(' ')[0]} `;
        });
      }
      const pageType = pageRoute[pageRoute.length - 1].split(' ')[1];
      this.pageTitle = pageRoute[pageRoute.length - 1];
      this.txnType =
        pageType == 'Credit' ? '1'
        : pageType == 'Debit' ? '3'
        : '0';

      this.menu.forEach((m) => {
        if (
          m.title.startsWith('New') ||
          m.title.startsWith('Edit') ||
          m.title.startsWith('Delete') ||
          m.title.startsWith('View')
        ) {
          m.title = `${m.title.split(' ')[0]} ${this.pageTitle}`;
        }
      });

      this.$nextTick(() => {
        this.searchFilterItems.forEach((i) => {
          const searchBy = i['Search By'];

          if (this.txnType == '1') {
            // Replace 'Invoice' or 'Debit Note' → 'Credit Note'
            i['Search By'] = searchBy.replace(/Invoice|Debit Note/g, 'Credit Note');
          } else if (this.txnType == '3') {
            // Replace 'Invoice' or 'Credit Note' → 'Debit Note'
            i['Search By'] = searchBy.replace(/Invoice|Credit Note/g, 'Debit Note');
          } else {
            // Replace 'Debit Note' or 'Credit Note' → 'Invoice'
            i['Search By'] = searchBy.replace(/Debit Note|Credit Note/g, 'Invoice');
          }
          // i['Search By'] = i['Search By'].replace(
          // this.pageType == 'Credit' ? 'Invoice' : 'Credit Note',
          // this.pageType == 'Credit' ? 'Credit Note' : 'Invoice',
          // );
        });
      });

      if (this.txnType != '0') {
        this.menu.find((m) => m['key'] == 'mnuImportData')['visible'] = false;
      }

      if (route) {
        this.menu.forEach((m) => (m['enabled'] = true));

        await this.refreshClicked('APListing');
      }

      this.$fullscreenLoading('Getting Job Type Config');
      this.apTypeList = await this.$apiCallV2('/Payment/GetJobTypeConfig', {
        // doctype: this.pageType == 'Credit' ? 'J' : 'V',
        doctype: this.txnType == '0' ? 'V' : 'J',
      });

      await this.refreshClicked('APListing');

      this.$fullscreenLoading('Checking Security Access');
      let sasControl = [];
      sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=FINPayment&page=${this.pageTitle}`);

      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));

      sasControl.forEach((s) => {
        const menu = this.menu.find((m) => m['key'] == s['Control']);
        if (menu) {
          menu[s['Properties'].toLowerCase()] = s['PropertiesValue'] !== 'False';
          menu['message'] = 'Access Denied';
        }
      });
      this.$fullscreenLoading();

      if (this.$route.query['apcn']) {
        this.filterAPListingByDate = false;
        this.filterAPListingBySearch = true;
        this.selectedSearchBy = this.searchFilterItems.getSelected('APINV', 'key');
        this.selectedSearchCriteria = this.searchCriteriaItems.getSelected('EQUALS', 'name');
        this.searchValue = this.$route.query['apcn'];
        await this.refreshClicked('APListing');
        this.selectedAPListing = this.apListing.filter((i) => i[`${this.pageTitle} No`] === this.$route.query['apcn']);
        // SAS Control
        const menuTitle = this.$route.query['action'] == 'E' ? `Edit ${this.pageTitle}` : `View ${this.pageTitle}`;
        const selectedMenu = this.menu.find((m) => m['title'] == menuTitle);
        if (selectedMenu['visible'] === false) {
          this.$showAlert('warning', 'Access Denied', `You do not have permission to ${menuTitle}`);
          return;
        }
        this.$nextTick(() => {
          this.menuClicked({
            title: menuTitle,
          });
        });
      }
    },
    selectedJobNoChange: async function(newVal, oldVal) {
      const tempJobNo = newVal ? newVal['Job No'] : this.selectedJobNoFreeText ?? '';
      
      if (tempJobNo.trim() == '' && (!this.selectedContainerFreeText || this.selectedContainerFreeText.trim() == '') && (!this.selectedConsignmentFreeText || this.selectedConsignmentFreeText.trim() == '')) {
        this.containerList = [...this.oriContainerList];
        this.consignmentList = [...this.oriConsignmentList];
        return;
      } else if (tempJobNo.trim() == '') {
        return;
      }

      const tempContNo = this.selectedContainer ? this.selectedContainer['Cont No'] : this.selectedContainerFreeText ?? '';
      const tempCN = this.selectedConsignment ? this.selectedConsignment['Consignment No'] : this.selectedConsignmentFreeText ?? '';
      let filteredContList = [];
      let filteredCNList = [];

      if (tempJobNo.trim() != '' && this.selectedChargesDetails.length == 0) {
        const proceed = await this.checkJobStatus(tempJobNo);
        if (!proceed) {
          this.selectedJobNo = oldVal;
          this.selectedJobNoFreeText = oldVal['Job No'];
          return;
        }

        filteredContList = this.oriContainerList.filter((c) => c['Job No'] == tempJobNo);
        filteredCNList = this.oriConsignmentList.filter((c) => c['Job No'] == tempJobNo);
      } else {
        filteredContList = this.oriContainerList;
        filteredCNList = this.oriConsignmentList;
      }

      if (tempContNo && tempContNo.trim() != '') {
        const exist = filteredContList.some((cont) => cont['Cont No'] == tempContNo.trim());
        if (!exist) {
          this.$showAlert(
            'warning',
            `Invalid Job No for Container ${tempContNo.trim()}`,
            `${tempContNo.trim()} does not belong to ${tempJobNo ?? ''}`,
          );
          this.selectedJobNo = oldVal;
          this.selectedJobNoFreeText = oldVal['Job No'];
          return;
        }
      }

      this.containerList = this.oriContainerList.filter((c) => c['Job No'] == tempJobNo);
      if (tempContNo && this.tempContNo.trim() != '') {
        this.selectedContainer = this.containerList.find((c) => c['Cont No'] == tempContNo);
        this.selectedContainerFreeText = this.selectedContainer['Cont No'];
      }

      if (tempCN && tempCN.trim() != '') {
        const exist = filteredCNList.some((cn) => cn['Consignment No'] == tempCN.trim());
        if (!exist) {
          this.$showAlert(
            'warning',
            `Invalid Job No for Consignment ${tempCN.trim()}`,
            `${tempCN.trim()} does not belong to ${tempJobNo ?? ''}`,
          );
          this.selectedJobNo = oldVal;
          this.selectedJobNoFreeText = oldVal['Job No'];
          return;
        }
      }

      this.consignmentList = this.oriConsignmentList.filter((c) => c['Job No'] == tempJobNo);
      if (tempCN && tempCN.trim() != '') {
        this.selectedConsignment = this.consignmentList.find((c) => c['Consignment No'] == tempCN);
        this.selectedConsignmentFreeText = this.selectedConsignment['Consignment No'];
        this.consignmentErr = '';
      }
    }
  },
  async mounted() {
    // const pageRoute = this.$route.path.split('/');

    // let userDetails = [];
    const mountedPromises = [];
    let mountedPromisesFulfilledCount = 0;

    // Move to inside route change function, so the same API won't be called 2 times
    // mountedPromises.push(
    //   this.$apiCallV2('/Payment/GetJobTypeConfig', { doctype: this.pageType == 'Credit' ? 'J' : 'V' }).then(
    //     (result) => {
    //       this.apTypeList = result;
    //       this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
    //     },
    //   ),
    // );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTY', module: 'APINV' }).then((result) => {
        this.payeeTypeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/APInvoice/GetQueryByJobConfig', {
        table: 'Container',
        top: this.contnoRowLimit,
      }).then((result) => {
        this.oriContainerList = result;
        this.containerList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/APInvoice/GetQueryByJobConfig', {
        table: 'Consignment',
        top: this.consignmentRowLimit,
      }).then((result) => {
        this.oriConsignmentList = result;
        this.consignmentList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/APInvoice/GetChargesExpensesListing').then((result) => {
        this.expenseCodeList = result.map((code) => {
          return {
            ...code,
            'Code - Description': `${code['Expenses Code']} - ${code['Description']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COSTCODE' }).then((result) => {
        this.costCodeList = result.map((code) => {
          return {
            ...code,
            'Code - Description': `${code['Cost Code']} - ${code['Cost Desc']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LORRYNO' }).then((result) => {
        this.vehicleNoList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'APTAXCODE' }).then((result) => {
        this.taxCodeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ARTAXCODE' }).then((result) => {
        this.ARTaxCodeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'UOM' }).then((result) => {
        this.uomList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GLACCOUNT',
      }).then((result) => {
        this.glAccList = result.map((acc) => {
          return {
            ...acc,
            'Code - Description': `${acc['Acc No']} - ${acc['Description']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EnableVendorRebate').then((result) => {
        this.enableVendorRebate = result[0].SettingsValue == 'Y';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/APInvoice/GetExpensesDocRefNoListing').then((result) => {
        this.expensesDocRefNoList = result.filter((doc) => doc['Expenses  Ref #'].trim() != '');
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=JobClosedPassword').then((result) => {
        this.settingsJobClosePswd = result[0].SettingsValue;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GAPJobNoRequired').then((result) => {
        this.jobNoRequired = result[0].SettingsValue == '1';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(async (_promiseOutcomes) => {
      if (this.$route.query['apinvno']) {
        // only for AP Invoice
        this.pageTitle = 'AP Invoice';
        this.filterAPListingByDate = false;
        this.filterAPListingBySearch = true;
        this.selectedSearchBy = this.searchFilterItems.find((s) => s['key'] == 'APINV');
        this.selectedSearchCriteria = this.searchCriteriaItems.find((s) => s['name'] == 'EQUALS');
        this.searchValue = this.$route.query['apinvno'];
        await this.refreshClicked('APListing');
        this.selectedAPListing = this.apListing.filter((i) => i['AP Invoice No'] === this.$route.query['apinvno']);
        // SAS control
        const action = this.$route.query['action'] == 'E' ? 'Edit' : 'View';
        const selectedMenu = this.menu.getSelected(`${action} `, 'title');
        if (selectedMenu['visible'] === false) {
          this.$showAlert('warning', 'Access Denied', `You do not have permission to ${action} AP Invoice`);
          return;
        }
        this.$nextTick(() => {
          this.menuClicked({
            title: this.$route.query['action'] == 'E' ? `Edit ${this.pageTitle}` : `View ${this.pageTitle}`,
          });
        });
      } else {
        await this.refreshClicked('APListing');
      }
    });
    await this.routeChanged(); //move to here after all API calls
  },
  watch: {
    async $route() {
      await this.routeChanged();
    },
    selectedAPListing: function (val) {
      const authoBtn = this.menu.find((m) => m['key'] === 'mnAuthorized');
      if (val.length > 0) {
        authoBtn['title'] = val[0]['Authorized'] === 'A' ? 'Unauthorize' : 'Authorize';
      } else {
        authoBtn['title'] = 'Authorize';
      }
    },

    chargesQty: function (val) {
      this.chargesAmtInputDisable = !(val.toString().trim().length > 0);
    },

    selectedChargesDetails: function (val) {
      if (!this.viewMode || val.length == 0) return;
      this.subtitleJC = 'Job Charges';
      this.showInputCharges = true;
      this.populateChargesDetails(val[0]);
    },

    selectedDPEntry: function (val) {
      if (!this.viewMode || val.length == 0) return;
      this.subtitleDP = 'Direct Posting';
      this.showInputDirectPosting = true;
      this.populateDirectPostingDetails(val[0]);
    },

    showJobCharges: function (val) {
      if (!val) {
        this.selectedJobCharges = [];
        this.jobChargesFilterOpen = false;
      }
    },

  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
@import '@/css/FIN.scss';

.ap-main {
  display: flex;
  font-size: 12px;
  padding: 10px 45px;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-desc {
        padding-right: 8px;
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px);
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.ap-entries {
  height: calc(100% - 27px);
  width: 100%;
  overflow: scroll;

  .ap-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .header-containers {
      width: 100%;
      margin-right: 5px;
    }
  }
}

.tab-slot {
  display: flex;
  height: 100%;

  > .tab-details-styling {
    margin-right: 3px;
    height: 100%;
    overflow: scroll;

    .tab-button-total {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    .tab-buttons {
      display: flex;
      flex-direction: row;
      margin: 5px 5px;
      align-items: center;

      > button {
        margin-right: 5px;
        height: fit-content;
      }
    }
  }

  > .side-entry-open {
    height: 100%;
    border-left: 1px solid black;
    overflow: scroll;
    margin-left: 5px;
    padding-left: 10px;

    .title-button {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      height: 50px;
      margin: 5px 0px;
      padding: 5px 0px;
      position: sticky;
      top: 0;
      z-index: 5;
      background-color: white;

      .buttons-arr {
        display: flex;

        button {
          margin-right: 5px;
          height: fit-content;
        }
      }
    }
  }
}

.header-total-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}

.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  padding: 5px 10px;
  align-content: space-evenly;

  .expand {
    min-width: 210px;
    padding-right: 5px;
    display: inline;
    text-align: right;
  }
}

.foreign-currency-background {
  > .background-left {
    width: calc((100% / 3) + 4px);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    width: calc((100% / 3) + 4px);
    border-left: 3px solid white;
  }
}

.payee-info {
  display: flex;

  > .info-container {
    margin-top: 5px;
    padding-top: 1px;
    padding-bottom: 1px;
    width: 70%;
  }
}
</style>
